import {
  fetchDispatchReservations,
  fetchDispatchVehicles,
  fetchDispatchVehicleTypes,
  fetchStatuses,
  saveDispatchReservation,
} from '../api/dispatchService';
import { fetchBookingCalc, toCalcDateTime, type BookingCalcVehicle, type BookingCalcResponse } from '../api/bookingCalcService';
import { formatHotelDeliverySummary, parseHotelDelivery } from '../lib/hotelDelivery';
import { hasStatusCache, setStatusCache } from '../lib/statusMapper';
import { postAgentAction } from './api';

function getCurrentStaffName(): string {
  try {
    const raw = localStorage.getItem('bo_staff');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.name === 'string' ? parsed.name.trim() : '';
  } catch {
    return '';
  }
}

/**
 * 予約保存前に status キャッシュ（statusMapper）が populate されていることを保証する。
 *
 * 通常は App.tsx 起動時に useReservationStatuses → setStatusCache が走るが、
 * モバイルで起動直後にエージェントから予約作成するとキャッシュが空のまま走り、
 * resolveStatusId('booked') が null を返して "status field is required" になる事故が
 * 発生していた。保険として保存直前に cache を見てなければ自前で fetch する。
 */
async function ensureStatusCachePopulated(): Promise<void> {
  if (hasStatusCache()) return;
  // 2026-07-09 修正: 失敗を握りつぶすと status=null のまま保存へ進み
  // "status field is required" 500 や「キャンセルが黙って効かない」事故になるため、
  // 2回試行してもキャッシュが埋まらなければ明確なエラーで中断する
  let lastError: any = null;
  for (let attempt = 0; attempt < 2 && !hasStatusCache(); attempt++) {
    try {
      const data = await fetchStatuses();
      if (data?.data) {
        setStatusCache({
          reservation: data.data.reservation || {},
          vehicle: data.data.vehicle || {},
          vehicle_location: data.data.vehicle_location || {},
          vehicle_type: data.data.vehicle_type || {},
        });
      }
    } catch (e) {
      lastError = e;
    }
  }
  if (!hasStatusCache()) {
    throw new Error(
      `ステータス情報の取得に失敗したため保存を中断しました。通信環境を確認してもう一度お試しください${lastError ? `（${(lastError as any)?.message || lastError}）` : ''}`,
    );
  }
}

/**
 * 予約データの memo に追記するタイムラインエントリを構築する。
 * 既存の `useAutoChangeMemo.formatAutoMemoEntry` と同じフォーマット
 * （`---アクション スタッフ名 YYYY-MM-DD HH:MM---` ヘッダー + 内容 + `---` フッター）に揃える。
 * スタッフ名に `(エージェント)` を付けて手動操作と区別できるようにする。
 */
type AgentMemoAction = 'reservation' | 'cancel' | 'invoice';
const AGENT_MEMO_ACTION_LABEL: Record<AgentMemoAction, string> = {
  reservation: '予約処理',
  cancel: 'キャンセル処理',
  invoice: '請求書処理',
};

function formatTimelineTimestamp(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mi}`;
}

function buildAgentMemoEntry(action: AgentMemoAction, contentLines: string[]): string {
  const ts = formatTimelineTimestamp();
  const staffName = getCurrentStaffName();
  const label = staffName ? `${staffName}(エージェント)` : 'エージェント';
  const header = `---${AGENT_MEMO_ACTION_LABEL[action]} ${label} ${ts}---`;
  const body = contentLines.filter(Boolean).join('\n');
  return body ? `${header}\n${body}\n---` : `${header}\n---`;
}

function appendAgentMemoEntry(existingMemo: unknown, entry: string): string {
  const cur = String(existingMemo || '').trimEnd();
  return cur ? `${cur}\n${entry}` : entry;
}
import type { PendingConfirm, WizardField } from './types';
import { RESERVATION_WIZARD_FIELDS, INVOICE_GROUPING_FIELD } from './wizard';
import { SYS_INVOICE_BASE } from '../constants/calendar';
import { getSquareIndex, isSquareEnabled } from '../config/backend';
import { LINE_SYNC_KEY } from '../config/instances';
import { classifyMail } from './mailWorkflows';
import { sendEscalation } from './contradiction';
import { findOtherStoreTokens } from '../config/storeBrand';
import { getStorePolicy, isSelfPlan, fleetDiscountFor } from '../config/storePolicies';
import { isRepresentativePickupPlan, isWalkInPlan } from '../utils/planPrefix';
import { currentParts, proposeLp, proposeOta, diffParts, type PriceParts, type Proposal, type CalcSnapshot } from './priceChange';
import { hasBundledPricing, isCdwBundled, isNocBundled, isLocationBundled, isCruisePriced } from '../lib/cdwBundle';
import { isHolidayPackReservation } from '../lib/holidayPack';
import { getOperatorName } from '../pages/reconcile/systemApi';
import { toggleReservationLicense } from '../api/dispatchService';
import { getPageContext } from './pageContext';
import { getSessionMeta } from './sessionContext';
import { toEditorReservation } from '../lib/editorDataTransform';
import { buildSquareInvoicePayload } from '../lib/squareInvoicePayload';
import { canCreateOver, diffLineItems, fetchSquareStatus, readableSquareError, referenceInvoice, squareDashboardUrl } from './squareStatus';
import { operatorInvoiceInstruction } from './taskPlan';
import { priceBreakdownFields } from './priceLines';
import { applyGroupToBody, fetchReservationGroup } from './reservationGroup';
export { applyGroupToBody, fetchReservationGroup } from './reservationGroup';
import { draftTotalProblem } from './draftAmounts';
import { logEvent } from '../lib/eventLog';

/**
 * 定型メールテンプレは **動的 import** で読む。
 * ⚠ 静的 import にすると tools → usePaymentEmail → aiEmail → tools の循環になり、
 *   モジュール初期化時に AGENT_TOOL_DECLARATIONS が undefined になって落ちる（2026-08-24）。
 */
async function loadBuiltinMail() {
  return import('../components/editor2/hooks/usePaymentEmail');
}
import { getStoreKey } from '../config/backend';

export const AGENT_TOOL_DECLARATIONS = [
  {
    name: 'search_reservations',
    description: [
      '予約を検索する。**予約番号 (contact_code)**・貸出日範囲・**予約日(申込日)範囲**・顧客名・電話番号で絞り込める。少なくとも1つの引数を指定すること。',
      'ユーザーが「00001NYYHSの予約」「H26-0413-55」のような英数字コードを言及した場合は、',
      'それを contact_code として検索すること。contact_code 指定時は日付範囲を渡さない（全期間から1件特定する）。',
      '**「今日入った予約」「本日の申込」「いつ予約された」は予約日(booking_date)のこと** → booked_start_date / booked_end_date を使う。',
      '**「本日出発」「明日の貸出」は pickup_date、「本日返却」は return_date** を使う（その日ちょうどの予約だけが返る）。',
      '**start_date / end_date は「その期間に利用が重なる予約」**を返す（貸出日が範囲内とは限らない。滞在中の予約も含む）。期間全体の状況を見たいときだけ使う。',
      '日付を省略すると「今日-30日〜今日+90日に利用が重なる予約」になる。この既定値で得た件数を「今日の件数」と言い換えてはいけない。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: {
          type: 'string',
          description: '予約番号（例: "00001NYYHS", "H26-0413-55", "TR-ABC123"）。完全一致でバックエンド検索する。',
        },
        start_date: {
          type: 'string',
          description: '**貸出日**範囲の開始日 (YYYY-MM-DD)。省略すると今日から過去30日。contact_code 指定時は不要。',
        },
        end_date: {
          type: 'string',
          description: '**貸出日**範囲の終了日 (YYYY-MM-DD)。省略すると今日から未来90日。contact_code 指定時は不要。',
        },
        booked_start_date: {
          type: 'string',
          description: '**予約日(申込を受けた日)**範囲の開始日 (YYYY-MM-DD)。「今日入った予約」はこれを使う。',
        },
        booked_end_date: {
          type: 'string',
          description: '**予約日(申込を受けた日)**範囲の終了日 (YYYY-MM-DD)。',
        },
        pickup_date: {
          type: 'string',
          description: '**その日に貸出（出発）する**予約だけに絞る (YYYY-MM-DD)。「本日出発」「明日の貸出」はこれ。',
        },
        return_date: {
          type: 'string',
          description: '**その日に返却する**予約だけに絞る (YYYY-MM-DD)。「本日返却」はこれ。',
        },
        customer_name: {
          type: 'string',
          description: '予約者名（部分一致）',
        },
        phone: {
          type: 'string',
          description: '電話番号（部分一致、ハイフン無視）',
        },
        email: {
          type: 'string',
          description: 'メールアドレス（部分一致）。ユーザーがメールアドレスだけを提示したらこれで検索する。',
        },
      },
    },
  },
  {
    name: 'search_vehicle_types',
    description: '車両タイプを検索する。車両タイプ名/コード/カテゴリに加えて、具体的な車両コード・車両番号（例: M-D5-H）でも検索でき、該当する車両が属する車両タイプを返す。pickup_date/return_date を指定すると、該当期間の在庫（空き車両数）も含めて返す。見つからない場合は全車両タイプの一覧を alternatives として返す。',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: '車両タイプ名/コード、または具体的な車両コード・車両番号の一部（例: "アルファード", "M-D5-H", "デリカ"）',
        },
        pickup_date: {
          type: 'string',
          description: '在庫・料金を確認したい貸出日時 (YYYY-MM-DD HH:mm)。ユーザーが時刻を伝えていれば必ず時刻ごと渡す（料金の課金日数が時刻で変わるため）。省略時は在庫情報を含めない。',
        },
        return_date: {
          type: 'string',
          description: '在庫・料金を確認したい返却日時 (YYYY-MM-DD HH:mm)。pickup_date とセットで指定する。',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'list_all_vehicle_types',
    description: [
      '全車両タイプの料金一覧を期間付きで取得する。',
      '「6/3 - 6/5の予約」のように**日付のみで車両タイプが未指定**の曖昧な問い合わせを受けた際、',
      '全候補車両タイプと各々の合計料金、在庫を一括で返す。',
      '【重要】ユーザーが時刻を伝えている場合は必ず「YYYY-MM-DD HH:mm」形式で時刻ごと渡すこと。',
      '課金日数は時刻で変わる（例: 14:00貸出→16:00返却は返却超過で+1日課金）ため、',
      '時刻を捨てて日付だけ渡すと料金が実際と食い違う。',
      '時刻が本当に不明な場合のみ日付だけ渡してよい（その場合 12:00 を仮定して計算する）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        pickup_date: { type: 'string', description: '貸出日時 (YYYY-MM-DD HH:mm)。ユーザーが時刻を伝えていれば必ず含める。不明時のみ YYYY-MM-DD（12:00 仮定）' },
        return_date: { type: 'string', description: '返却日時 (YYYY-MM-DD HH:mm)。同上' },
      },
      required: ['pickup_date', 'return_date'],
    },
  },
  {
    name: 'calculate_price',
    description: [
      '⚠ vehicle_type_id は search_vehicle_types が返す ID。search_reservations の vehicle_id（車両個体のID）とは**別物**なので流用しない。予約から料金を計算するときは、まず search_vehicle_types で car_type から ID を解決する。',
      '指定車両タイプ・期間の料金を計算する。**お客様の見積もり問い合わせにも必ずこのツールを使う**。',
      'NOC免責 / CDW / チャイルドシート / ブースターシート / 複数台数まで一括で計算可能。',
      'pickup_datetime / return_datetime（YYYY-MM-DD HH:mm）を渡すと正確な時刻で計算する。',
      'pickup_date / return_date（YYYY-MM-DD）でも動くがその場合は 12:00 を仮定する。',
      '時刻が「14時頃」「13時半」のような曖昧表現でも **ユーザーに確認せず確定値**（14:00, 13:30）として渡してよい。',
      'レスポンスは per_vehicle（1 台分の内訳）と grand_total（quantity 台分の合計）を含む。',
      '「15%割引だといくら」のような **仮定計算** を求められた場合は per_vehicle.base_subtotal に (1 - X%) を掛けて算出すること（割引後の値に重ねがけしない）。',
      'クルーズ船の予約（宮古=平良港・那覇=那覇港）のときは location="cruise" を渡す。クルーズ船料金表の料金（入っている日だけ）で計算し、送迎料（クルーズ料金を使った予約は 0 円・使わなければ 宮古 ¥4,000／那覇 ¥5,000）を location_surcharge と合計に含める。クルーズ船の予約は NOC・CDW 必須で基本料金に込み（calc が加算）・事前決済必須（prepay_required）・キャンペーン割引なし（calc の discount=0）。宮古は貸出・返却の 8:00〜8:59 が早朝対応料 ¥2,200（それぞれ。fees に含む）で即時確定、那覇は通常の深夜早朝料金。両店舗とも返却が 19:00 を過ぎると「時」ごとに +¥1,100（fees に含み cruise_late_fee にも。那覇は表の分も足す）、21:00〜翌 5:59 の貸出・返却はリクエスト予約。返却が翌日 12:00 までなら1泊プラン（単日の 1.5 倍。calc の cruise_one_night。補償は返却の時刻に関係なく2日分）、12:00 を過ぎる返却はスタッフが個別に対応（リクエスト予約）。宮古は港の近くの貸出場所でお渡し（港では渡せない）、那覇は港の近くで場所は当日ご案内、と案内する。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        vehicle_type_id: { type: 'integer', description: '車両タイプ ID' },
        pickup_datetime: { type: 'string', description: '貸出日時 (YYYY-MM-DD HH:mm) — 推奨' },
        return_datetime: { type: 'string', description: '返却日時 (YYYY-MM-DD HH:mm) — 推奨' },
        pickup_date: { type: 'string', description: '貸出日 (YYYY-MM-DD)。datetime 未指定時に 12:00 を仮定' },
        return_date: { type: 'string', description: '返却日 (YYYY-MM-DD)' },
        noc_waiver: { type: 'boolean', description: 'NOC 免責の有無（料金に加算）' },
        cdw: { type: 'boolean', description: 'CDW の有無（料金に加算）' },
        child_seat: { type: 'integer', description: 'チャイルドシート台数' },
        booster_seat: { type: 'integer', description: 'ブースターシート台数' },
        quantity: { type: 'integer', description: '車両台数（既定 1）。複数台数の合計を計算する' },
        location: { type: 'string', enum: ['cruise'], description: 'クルーズ船の予約（宮古=平良港・那覇=那覇港）のとき "cruise"。それ以外は渡さない' },
      },
      required: ['vehicle_type_id'],
    },
  },
  {
    name: 'search_customer',
    description: '過去の予約から顧客を検索する。氏名・電話・メールアドレスで引ける。',
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: '顧客名（部分一致）' },
        phone: { type: 'string', description: '電話番号（部分一致）' },
        email: { type: 'string', description: 'メールアドレス（部分一致）' },
      },
    },
  },
  {
    name: 'propose_plan',
    description: [
      '**書き込みが2つ以上ある依頼**（例: CDW を追加 → 請求書を直す → メールで案内）で、道具を使う前に完了までの全手順を番号付きで示し、',
      'スタッフの承認を受ける（チャットに手順カードが出て、［この手順で進める］か［手順を直す］が押されるまで待つ）。',
      '承認されたら手順どおり1つずつ進め、1つ終わるたびに advance_plan を呼ぶ。途中で手順が変わったら、残りの手順でもう一度呼ぶ。',
      '書き込みが1つで終わる依頼・見積もり・検索では使わない。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: '何の作業か（例: 「TR-XXXX の CDW 追加」）' },
        steps: {
          type: 'array',
          description: '完了までの手順（順番どおり・2〜12個）',
          items: {
            type: 'object',
            properties: {
              label: { type: 'string', description: '何をするか（使うツールも書く。例: 「CDW を付けて料金を再計算して反映する（apply_price_change）」）' },
              actor: { type: 'string', enum: ['ai', 'staff'], description: 'ai=AI が実行 / staff=スタッフが操作（Square の請求書など AI ができないもの）' },
              check: { type: 'string', enum: ['card', 'draft', 'staff', 'none'], description: 'どこで確認を求めるか: card=確認カード / draft=下書きカードで送信 / staff=スタッフが操作 / none=確認なし（読み取り・アーカイブなど）' },
            },
            required: ['label', 'actor', 'check'],
          },
        },
      },
      required: ['title', 'steps'],
    },
  },
  {
    name: 'advance_plan',
    description: [
      '承認済みの手順（propose_plan）の1ステップが終わったら呼び、手順カードに印を付ける。',
      'done=終わった / skipped=要らなくなった（note に理由）/ waiting_staff=スタッフの操作待ち（note に何をしてほしいか）。',
      'waiting_staff のときは次へ進まず、スタッフが「完了」と言ってから done にする。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        step: { type: 'integer', description: '手順の番号（1 始まり）' },
        status: { type: 'string', enum: ['done', 'skipped', 'waiting_staff'], description: '手順の状態' },
        note: { type: 'string', description: '結果のひとこと（例: 「合計 ¥24,000 → ¥30,600」）。skipped と waiting_staff では必須' },
      },
      required: ['step', 'status'],
    },
  },
  {
    name: 'archive_mail',
    description: [
      '受信メールを**アーカイブ**する（Gmail の受信トレイから外し、mail-queue でも処理済み扱いになる）。',
      '対応が完了したメールを「このメールをアーカイブして」と言われたら使う。',
      'セッションが受信メールから開かれている場合、そのメールの ID はシステム文脈に示されている。',
      'アーカイブは可逆（Gmail 側で受信トレイに戻せる）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        mail_id: { type: 'string', description: 'アーカイブするメールの ID（セッション文脈の mail_id）' },
      },
      required: ['mail_id'],
    },
  },
  {
    name: 'list_reservation_mails',
    description: [
      '予約番号に紐づく**受信メールの一覧**を取得する（mail_id・差出人・件名・受信日時・受信トレイ有無・AI判断）。',
      '「この予約のメールはある？」の確認や、archive_mail / reply_to_mail_id に渡す mail_id を得るために使う。',
      'セッション文脈に mail_id が無くても、このツールで予約のメールを特定できる。読み取り専用。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号（TR-XXX 等）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'get_mail_content',
    description: [
      '受信メール1通の**本文**を読む（差出人・件名・受信日時・本文）。',
      '「メールの内容を確認して」に使う。mail_id は list_reservation_mails かセッション文脈から得る。読み取り専用。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        mail_id: { type: 'string', description: 'メールの ID（list_reservation_mails の mail_id）' },
      },
      required: ['mail_id'],
    },
  },
  {
    name: 'check_mail_attachments',
    description: [
      '受信メールの**添付ファイル（免許証・欠航証明などの画像/PDF）を AI が読み取り判定**する。',
      '「免許証を確認して」「添付を見て」に使う。書類の種類・要約・予約データ（氏名等）との一致・懸念点が返る。',
      '⚠ 判定は補助。免許確認チェックをオンにする前に、判定結果（氏名一致・有効期限）をユーザーに提示して合意を得ること。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        mail_id: { type: 'string', description: 'メールの ID（list_reservation_mails の mail_id）' },
        instruction: { type: 'string', description: '確認目的（例: 運転免許証として有効か・予約者名と一致するか）。省略時は書類の種類と有効性の確認' },
      },
      required: ['mail_id'],
    },
  },
  {
    name: 'check_schedule_conflict',
    description: [
      '予約の**日時変更が可能かチェック**する（記録#25 ルール1）。変更後の期間で、アサイン済み車両に他の予約が被っていないかを確認し、',
      '被っている場合は同タイプの他車両に空きがあるか（振替可能性）も返す。',
      'お客様から時間・日付の変更希望が来たら、**update_reservation の前に必ずこれを呼ぶ**。読み取り専用。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        new_pickup_datetime: { type: 'string', description: '変更後の貸出日時 (YYYY-MM-DD HH:mm)。変更しない場合は省略' },
        new_return_datetime: { type: 'string', description: '変更後の返却日時 (YYYY-MM-DD HH:mm)。変更しない場合は省略' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'preview_price_change',
    description: [
      '日時変更や NOC/CDW の追加・削除に伴う**料金の再計算プレビュー**。読み取り専用（書き込みは apply_price_change）。',
      '自社LP = 料金計算 API で再計算（補償だけなら基本料金は据置・日程変更は予約時の割引率を維持）/ OTA = 日割り。',
      '補償の日額は**店舗と車種の価格帯**で決まる（料金計算 API が返す。那覇 例 エクリプス 550/1,650・デリカ 770/2,200 / 宮古 NOC 550・CDW 1,650 または 1,100）。',
      'お客様に金額を案内する前に必ずこれで確認する。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        new_pickup_datetime: { type: 'string', description: '変更後の貸出日時 (YYYY-MM-DD HH:mm)。変更しない場合は省略' },
        new_return_datetime: { type: 'string', description: '変更後の返却日時 (YYYY-MM-DD HH:mm)。変更しない場合は省略' },
        noc: { type: 'boolean', description: 'NOC 免責を付ける/外す（省略時は現状のまま）' },
        cdw: { type: 'boolean', description: 'CDW を付ける/外す（省略時は現状のまま）' },
        noc_per_day: { type: 'integer', description: 'NOC の日額を明示するとき（needs_decision が出たときだけ）' },
        cdw_per_day: { type: 'integer', description: 'CDW の日額を明示するとき（needs_decision が出たときだけ）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'apply_price_change',
    description: [
      '**料金を再計算して予約に反映する**（2026-10-03〜）。補償(NOC/CDW)の追加・削除、日程変更に伴う金額の更新に使う。',
      'preview_price_change と同じ計算をし、日時・補償フラグ・基本料金・割引・オプション・NOC料金・CDW料金・合計を',
      '**確認カード（各項目の変更前→変更後と計算根拠）でユーザーが承認してから**保存する。保存後に実反映を検証する。',
      '日程変更を伴うときは update_reservation で日時だけ変えずに、これで日時と金額を一緒に変える。',
      'needs_decision が返ったら書き込まれていない。日額をユーザーに確認して noc_per_day / cdw_per_day を付けて再実行。',
      '⚠ 請求書（Square）は自動では直らない。発行済みなら追加ルール#9（未払い=取消→再作成 / 支払い済み=差額のみ請求）。未払いの作り直しは void_square_invoice → create_square_invoice（手順カードで承認後）でできる。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        new_pickup_datetime: { type: 'string', description: '変更後の貸出日時 (YYYY-MM-DD HH:mm)。変更しない場合は省略' },
        new_return_datetime: { type: 'string', description: '変更後の返却日時 (YYYY-MM-DD HH:mm)。変更しない場合は省略' },
        noc: { type: 'boolean', description: 'NOC 免責を付ける/外す（省略時は現状のまま）' },
        cdw: { type: 'boolean', description: 'CDW を付ける/外す（省略時は現状のまま）' },
        noc_per_day: { type: 'integer', description: 'NOC の日額を明示するとき（needs_decision が出たときだけ）' },
        cdw_per_day: { type: 'integer', description: 'CDW の日額を明示するとき（needs_decision が出たときだけ）' },
        memo: { type: 'string', description: '社内メモに残す補足（任意。計算根拠は自動で残る）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'report_contradiction',
    description: [
      '**会話内の矛盾をスタッフ全員へ即時メールで知らせる**（2026-10-03〜）。',
      '次のような食い違いに気づいたら、作業を進める前に必ず呼ぶ: 予約データとメール本文・メモ・お客様の要望が違う /',
      '追加ルールとスタッフの指示が違う / ツールの結果どうしが合わない / 自分の前の発言が事実と違ったと分かった。',
      '黙ってどちらかに寄せない。呼んだ後、チャットでもスタッフに矛盾の内容を伝えて確認する。',
      '同じ内容は30分は再送されない。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: '何と何が食い違っているか（1文）' },
        evidence: { type: 'array', items: { type: 'string' }, description: '根拠（それぞれの出どころと値。例「予約データ: 返却 10/12 10:30」「お客様のメール: 返却 10/12 15:00」）' },
        contact_code: { type: 'string', description: '関係する予約番号（あれば）' },
      },
      required: ['title', 'evidence'],
    },
  },
  {
    name: 'list_available_vehicles',
    description: [
      '予約の期間に**空いている号車**を一覧する。「空いている車両は？」「振り替えられる車両を教えて」で使う。',
      '通常はその予約の車種タイプの中から、**ガチャ枠（Gatcha / GatchaH）は複数タイプにまたがるプールから**探す。',
      '使用不可・非稼働の車両は除く。結果の車両コードは assign_vehicle にそのまま渡せる。読み取り専用。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        pickup_datetime: { type: 'string', description: '判定に使う貸出日時（省略時は予約の現在値）' },
        return_datetime: { type: 'string', description: '判定に使う返却日時（省略時は予約の現在値）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'assign_vehicle',
    description: [
      '予約の**車両（号車）を変更・アサイン**する。「車両を T-AL-B-2857 に変えて」「空いている車両に振り替えて」の後半で使う。',
      '同じ車両タイプ内の変更のみ（タイプが違うと車種・料金が変わるため AI からは行わない）。',
      '実行前に変更先車両の予約重なりを自動チェックし、重なりがあれば実行しない。',
      '呼び出すとフロントが確認カードを出し、承認後に保存する（監査メモ付き）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        vehicle_code: { type: 'string', description: '変更先の車両コード（例: T-AL-B-2857）' },
      },
      required: ['contact_code', 'vehicle_code'],
    },
  },
  {
    name: 'set_mail_followup',
    description: [
      'メールに**フォローアップ期限**を設定する（Gmail のスヌーズ相当）。',
      '期限が来たとき、お客様からの返信が無ければ mail-queue に「要対応」として再浮上する（返信が来ていれば何もしない）。',
      'リクエスト予約の承諾メールを送って返信待ち（オファー中）にするとき等に使う。',
      'followup_at を省略すると**翌日12:00**になる。followup_at を空文字にすると解除。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        mail_id: { type: 'string', description: 'メールの ID（list_reservation_mails の mail_id）' },
        followup_at: { type: 'string', description: '期限（YYYY-MM-DD HH:MM）。省略=翌日12:00 / 空文字=解除' },
        note: { type: 'string', description: '再浮上時に表示するメモ（例: 返信なければ「予約に至らなかった」へ変更）' },
      },
      required: ['mail_id'],
    },
  },
  {
    name: 'set_reservation_checks',
    description: [
      '予約の**確認・同意チェック**（サイドパネルの「確認・同意状況」と同じもの）を変更する。',
      '対象: license(免許確認) / payment(支払い確認) / terms_and_conditions(利用規約) / disclaimer(免責事項) / agreement(契約同意)。',
      '「免許証の確認をOKに」「支払い確認と利用規約をオンに」等で使う。複数まとめて変更できる。',
      '⚠ メモへの記載ではなく**専用のチェック**を更新する。変更内容は監査としてメモにも自動で残る。',
      '呼び出すとフロントが確認カードを出し、承認後に保存する。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        license: { type: 'boolean', description: '免許確認（true=確認済み / false=未確認に戻す）' },
        payment: { type: 'boolean', description: '支払い確認' },
        terms_and_conditions: { type: 'boolean', description: '利用規約' },
        disclaimer: { type: 'boolean', description: '免責事項' },
        agreement: { type: 'boolean', description: '契約同意' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'open_reservation_session',
    description: [
      '**予約セッション**（その予約のあらゆる作業が蓄積される場）へ切り替える。',
      '「この予約の作業をする」「TR-XXX のセッションを開いて」等で使う。',
      '開くと過去の作業履歴（メール・操作・メモ）が文脈として読み込まれる。1予約=1セッションで、誰が開いても同じ場所に戻る。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'get_reservation_timeline',
    description: [
      '予約の**作業履歴タイムライン**を取得する（メール受信・AI判断・エージェント操作・予約メモの監査行を時系列マージ）。',
      '「この予約でこれまで何をした？」「経緯を教えて」に使う。読み取り専用。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'remember_rule',
    description: [
      'ユーザーから教わった業務ルール・判断基準を**次回以降も使えるように保存する**。',
      '「覚えて」「今後はこうして」「次からはこうする」「これがルール」等と言われたら必ず呼ぶ。',
      'また、**業務上の決まりごとを教わった直後**は、言われなくても「これを覚えておきますか？」と確認して呼ぶ。',
      '保存すると以後すべての会話に自動で反映される。確認カードが出るのでユーザーが最終確認する。',
      'ルール本文は**そのまま指示として読める形**で書く（例: 「台風で旅程が短縮された場合の返金額は〜」）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'ルールの見出し（例: 悪天候による旅程短縮時の返金計算）' },
        rule_text: { type: 'string', description: 'ルール本文。条件と手順が分かるように具体的に書く' },
      },
      required: ['title', 'rule_text'],
    },
  },
  {
    name: 'record_conversation',
    description: [
      'ここまでのやりとりを記録として保存する。**うまくいかなかった指示を後で見返して改善するため**の機能。',
      'ユーザーが「この件は記録して」「記録しておいて」「今のやりとりを残して」等と言ったら必ず呼ぶ。',
      '保存されるのは会話の全文なので、何を要約するか自分で考える必要はない。',
      'note には「何がうまくいかなかったか」を一文で入れる（ユーザーが理由を言っていればその要約、無ければ空でよい）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        note: { type: 'string', description: '記録の理由・何がうまくいかなかったか（一文。分からなければ省略可）' },
      },
    },
  },
  {
    name: 'list_mail_templates',
    description: [
      '**お客様宛メールの定型テンプレート一覧**を返す。予約詳細サイドパネルで使われているものと同じ。',
      'ユーザーが「テンプレートを使いたい」「テンプレートから作って」と言ったら、まずこれを呼んで一覧を提示する。',
      '**Notion を検索しない**（テンプレートは Notion には無い）。',
    ].join(' '),
    // ⚠ properties を空にしない。空だと PHP プロキシの json_decode/encode で {} が [] に化け、
    //    Gemini が「Cannot bind a list to map」で **全リクエストを 400 にする**
    //    （2026-08-25: これでエージェントが丸一日停止した）。サーバ側でも直したが、
    //    宣言側でも空にしないでおく（二重の守り）。
    parameters: {
      type: 'object',
      properties: {
        kind: {
          type: 'string',
          description: "省略可。'builtin'=定型のみ / 'saved'=保存分のみ。既定は全部",
        },
      },
    },
  },
  {
    name: 'draft_reservation_email',
    description: [
      '予約のお客様宛メールを**起案する（送信はしない）**。指示と予約の実データからサーバ側で件名・本文を作る。',
      '「◯◯の件でメールを作って」「返金の確認メールを送りたい」等はまずこれを呼ぶ。',
      '**宛先はサーバ側が予約データから決める**ので、宛先を引数で渡すことはできない（誤送信防止）。',
      '結果の draft_id / subject / body をユーザーに提示し、送ってよいか確認する。送信は send_reservation_email。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号（例: "R07ZMWLB"）' },
        instruction: {
          type: 'string',
          description: 'メールの目的・伝えたいこと（例: 支払い済みの予約がキャンセルになったため、支払い時のクレジットカードへ返金してよいか確認したい）',
        },
        template: {
          type: 'string',
          description: 'テンプレート名（list_mail_templates の key または name）。指定すると **AI で作文せず定型文をそのまま使う**。'
            + 'オファー・満車・キャンセル等の定型連絡は必ずテンプレートを使うこと。',
        },
        language: {
          type: 'string',
          description: "本文の言語。'en' で英語。ユーザーが「英語で」と言ったら必ず指定する（既定は日本語）。",
        },
        reply_to_mail_id: {
          type: 'string',
          description: '受信メールへの**返信（スレッド）**として送る場合、そのメールのID。'
            + 'セッションが受信メールから開かれた場合はシステム文脈に reply_to_mail_id が示されるので、'
            + '**お客様への返信は必ずこれを渡す**（新規メールにしない）。',
        },
        body: {
          type: 'string',
          description: '**一部修正のとき**: 修正後の全文。instruction 無しで body だけ渡すと'
            + 'AI が作文し直さず**その全文のまま**起案し直す（指示した箇所以外が変わらない）。',
        },
        subject: { type: 'string', description: 'body 指定時の件名（省略時は前回の件名を言い直すか既定）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'send_reservation_email',
    description: [
      'draft_reservation_email で作った下書きを**実際にお客様へ送信する**。',
      '送信は取り消せないため、必ず本文をユーザーに提示して「送ってよいか」の合意を得てから呼ぶこと。',
      '呼び出すとフロントが確認カードを出し、承認後に送信する。送信後は予約メモに送信履歴が自動で残る。',
      '**この会話で作った下書きしか送れない**（他画面の下書きは送れない）。本文を直したい場合は body に修正後の全文を渡す。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        draft_id: { type: 'integer', description: 'draft_reservation_email が返した draft_id' },
        body: { type: 'string', description: '送信する本文。省略すると下書きのまま送る。修正したい場合のみ全文を渡す。' },
      },
      required: ['draft_id'],
    },
  },
  {
    name: 'cancel_reservation_full',
    description: [
      '予約のキャンセル一式を実行する。予約ステータスをキャンセルに変更し、発行済みの Square 請求書があれば取り消し、監査メモを追記する — すべてサーバ側の1回の処理で行われる。',
      '「この予約をキャンセルして」「TR-XXX をキャンセル」のようなキャンセル依頼はこのツールを使う（update_reservation で status だけ変えると請求書が残るので使わない）。',
      '呼び出すとフロントが (1) 予約を取得 → (2) 確認カード提示 → (3) 承認後にサーバへ依頼、を自動で行う。「確認しますか？」と自分で聞かない。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号（例: "TR-XXXXXX-XXXXXX" / "R071B8EB"）' },
        reason: { type: 'string', description: 'キャンセルの理由・経緯（監査メモに残る。例: お客様から電話でキャンセル依頼）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'get_square_payment_status',
    description: [
      '予約番号から Square の決済状況を読む（2026-10-31・読み取りのみ）。サイドパネルの Square 欄と同じく Square に直接問い合わせる。',
      '返すもの: 予約に結ばれた請求書の状態（UNPAID / PAID / FAILED / CANCELED / DRAFT など）と金額・支払い済み額・明細、',
      '決済の試みの回数と結果（完了・失敗）、返金、補償（NOC/CDW）の支払い判定、同じ予約番号の結ばれていない古い請求書、',
      '再発行してよいか（reissue.allowed）と古い請求書の片付け方（reissue.void_action）。',
      '**「決済の状況を確認して」「決済状況は？」「支払いは済んでいる？」「Square を見て」「払えないそうです」と言われたら、必ずこれを呼ぶ**（メールを読んで推測しない。「道具がない」と答えない）。',
      '答えるときは status_label と金額・決済の試みをそのまま使い、推測で状態を言わない。読むだけのツールなので、確認の依頼でも呼んでよい（メールの起案・アーカイブはしない）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号（例: "TR-XXXXXX-XXXXXX"）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'void_square_invoice',
    description: [
      '失敗した・古い Square 請求書を片付ける（請求書の再発行の1つ目の書き込み）。呼ぶ直前に Square の最新の状態を読み直して決める:',
      'UNPAID / SCHEDULED / FAILED なら Square で取り消す（FAILED は Square が受け付けないことがある）。CANCELED（取り消し済み）なら予約から外すだけ。',
      '**Square が取り消しを受け付けなかったら（stop_plan）手順を止め**、square_error と guidance（Square の管理画面で取り消してから「再度作成します」と指示）をそのままチャットに書く。create_square_invoice に進まない。',
      'どちらも予約メモに古い請求書の記録と誰の指示かを残す。PAID・一部支払い・下書き・処理中は止まる（二重請求を防ぐ）。',
      '**オペレータがチャットで「請求書を取り消し」「再発行」とはっきり指示したときだけ呼ぶ**（それが承認になり、確認カードは出ない）。指示が無ければ自分の判断で呼ばない（呼ぶと確認カードでスタッフに聞く）。',
      '必ず先に手順カード（propose_plan）で再発行の手順を出す。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        reason: { type: 'string', description: '片付ける理由（予約メモに残る。例: お客様から「支払いができない」と連絡・請求書が FAILED）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'create_square_invoice',
    description: [
      '予約から新しい Square 請求書を作り、予約に結ぶ（サイドパネルの［請求書を発行］と同じ中身・同じ経路）。',
      'まだ払える請求書が結ばれていると止まる（先に void_square_invoice）。取り消し済み（CANCELED）・終わった状態で未払い（FAILED）の請求書が結ばれているときは、取り消しは済んだものとして差し替え、メモに残す',
      '（オペレータが Square の管理画面で取り消してから「再度作成します」と指示したとき。このとき void_square_invoice は呼び直さなくてよい）。',
      'send=true なら Square から請求書のメールがお客様に届く（宮古は常に下書き）。send=false（既定）は下書きで、お客様には届かない。',
      '金額は取り消した（前の）請求書と同じでなければ作らない（needs_decision と差が返る → オペレータに聞く）。同じ予約番号に払える・払われた請求書があっても作らない。',
      '**オペレータがチャットで「再度作成」「再発行」とはっきり指示したときだけ呼ぶ**（それが承認になり、確認カードは出ない。send を省くと前の請求書と同じ送り方）。指示が無ければ確認カードでスタッフに聞く（send を省くと下書き）。',
      '作ったら new_invoice の番号・状態・送り方をチャットにそのまま書く。お客様への返信は draft_reservation_email で案を出すだけで、送信はオペレータに任せる。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        contact_code: { type: 'string', description: '予約番号' },
        send: { type: 'boolean', description: 'true = Square からお客様へ請求書のメールを送る（公開）。false / 省略 = 下書き（お客様には届かない）' },
        reason: { type: 'string', description: '作る理由（予約メモに残る。例: 前の請求書が支払い失敗のため再発行）' },
      },
      required: ['contact_code'],
    },
  },
  {
    name: 'update_reservation',
    description: [
      '既存の予約を更新する。ステータス変更（キャンセルなど）・貸出日時/返却日時の変更・到着便/出発便の変更・乗車人数・コメントなどに対応。',
      '予約は **reservation_id（整数）または contact_code（予約番号）のどちらか1つ** で特定する。',
      '渡された項目だけが上書きされ、それ以外の既存値は維持される。',
      '呼び出すとフロントが (1) 既存予約を取得 → (2) 差分確認カードをユーザーに提示 → (3) 承認後にバックエンドへ保存、を自動で行う。',
      'ユーザーに「確認しますか？」と聞かずにすぐ呼び出してよい（確認カードはフロントが出す）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        reservation_id: { type: 'integer', description: '予約 ID（search_reservations の results[i].id）' },
        contact_code: { type: 'string', description: '予約番号（例: "00001NYYHS"）。reservation_id が無いときに使う。' },
        status: {
          type: 'string',
          description: 'ステータスコード。利用可能: booked(予約確定) / cancelled(キャンセル) / done(貸出済み) / in_use(貸出中) / pending(確認中) / received(受信済み) / mail_ready(要連絡) / offer(オファー中) / nonreserved(予約に至らなかった)',
        },
        pickup_datetime: { type: 'string', description: '新しい貸出日時 (YYYY-MM-DD HH:mm)' },
        return_datetime: { type: 'string', description: '新しい返却日時 (YYYY-MM-DD HH:mm)' },
        arrival_flight: { type: 'string', description: '到着便名（例: NH123）。空文字でクリア。' },
        departure_flight: { type: 'string', description: '出発便名（例: JL987）。空文字でクリア。' },
        customer_name: { type: 'string', description: '予約者名' },
        phone: { type: 'string', description: '電話番号' },
        email: { type: 'string', description: 'メールアドレス' },
        pax: { type: 'integer', description: '乗車人数の**合計**（大人+子供。大人2名+子供2名なら 4。2026-08-31 記録#24）' },
        pax_children: { type: 'integer', description: '子供の人数' },
        child_seat: { type: 'integer', description: 'チャイルドシート数' },
        booster_seat: { type: 'integer', description: 'ブースターシート数' },
        noc_waiver: { type: 'boolean', description: 'NOC 免責の有無' },
        cdw: { type: 'boolean', description: 'CDW の有無' },
        comment: { type: 'string', description: 'お客様の要望に**追記**する内容（既存の要望は維持される。全文を渡さず、追加分だけを渡す）' },
        short_memo: { type: 'string', description: '**ショートメモ**（予約一覧に表示される短い社内ラベル。例: 臭いに注意）。上書き更新。「ショートメモに〜」と言われたら memo ではなく必ずこちらを使う（2026-09-01 記録#25）' },
        memo: { type: 'string', description: '社内メモに追記する内容（既存メモは維持され、末尾に追記される。全文を渡す必要はなく、今回の変更理由・経緯だけを簡潔に渡す。例: 台風のため返金なし）。⚠「ショートメモ」は別フィールド（short_memo）' },
      },
    },
  },
  {
    name: 'create_reservation',
    description: [
      '予約を新規作成する。',
      '【重要】必須4項目（customer_name, pickup_datetime, return_datetime, vehicle_type_id）が揃い、',
      'calculate_price で料金が確定したら、**ユーザーに「確認しますか？」と聞かずに直ちにこのツールを呼ぶこと**。',
      '呼び出すとフロントが自動的に:',
      '(1) 不足している重要質問項目（NOC/CDW/電話/メール/シート/コメント）をウィザードで一問一答で収集し、',
      '(2) 確認カードをユーザーに提示し、',
      '(3) 承認後にバックエンドに予約を作成する。',
      'よって重要質問項目はエージェントが自分で聞かないこと（ユーザーが自発的に伝えた値のみ引数に含める）。',
      '**複数台**: 同じ車種なら quantity に台数、**別車種の組み合わせ**なら vehicles に [{vehicle_type_id, quantity}] を渡す',
      '（例: デリカ2台＋アルファード30系1台）。台数分の予約を枝番（-1〜-N）付きで1グループとして作る。',
      '請求書をまとめるか（1枚にまとめる／台ごとに分ける）はフォームでスタッフに聞く。AI が自分で決めない。予約経路もフォームで聞く。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        pickup_datetime: { type: 'string', description: '貸出日時 (YYYY-MM-DD HH:mm)（必須）' },
        return_datetime: { type: 'string', description: '返却日時 (YYYY-MM-DD HH:mm)（必須）' },
        vehicle_type_id: { type: 'integer', description: '車両タイプ ID（必須、search_vehicle_types で解決）' },
        customer_name: { type: 'string', description: '予約者名。Gemini は通常は渡さなくてよい（フロントウィザードが収集する）。ユーザーが既に名乗っていればここで渡してもよい。' },
        base_price: { type: 'integer', description: '基本料金（円）。省略時は calculate_price の結果を使用。' },
        noc_waiver: { type: 'boolean', description: 'NOC 免責の有無' },
        cdw: { type: 'boolean', description: 'CDW の有無' },
        phone: { type: 'string', description: '電話番号' },
        email: { type: 'string', description: 'メールアドレス' },
        pax: { type: 'integer', description: '乗車人数の**合計**（大人+子供。大人2名+子供2名なら 4。2026-08-31 記録#24）' },
        pax_children: { type: 'integer', description: '子供の人数' },
        child_seat: { type: 'integer', description: 'チャイルドシート数' },
        booster_seat: { type: 'integer', description: 'ブースターシート数' },
        arrival_flight: { type: 'string', description: '到着便名（例: NH123）' },
        departure_flight: { type: 'string', description: '出発便名（例: JL987）' },
        comment: { type: 'string', description: 'お客様の要望・コメント' },
        quantity: { type: 'integer', description: '台数（既定 1）。同じ車種の複数台' },
        vehicles: {
          type: 'array',
          description: '別車種を組み合わせる複数台予約のとき（vehicle_type_id の代わり）。例 [{vehicle_type_id:3,quantity:2},{vehicle_type_id:1,quantity:1}]',
          items: {
            type: 'object',
            properties: {
              vehicle_type_id: { type: 'integer', description: '車両タイプ ID' },
              quantity: { type: 'integer', description: 'その車種の台数' },
            },
            required: ['vehicle_type_id'],
          },
        },
        contact_media: { type: 'string', description: '予約経路（TEL・直来・LINE 等。スタッフが言ったときだけ）' },
      },
      required: ['pickup_datetime', 'return_datetime'],   // vehicle_type_id か vehicles のどちらか（2026-10-19）
    },
  },
  {
    name: 'get_vehicle_notion',
    description: [
      'Notion の車両マスタから車両の詳細情報（整備履歴・車検・保険・備品・契約等）を取得する。',
      'backend の車両検索（search_vehicle_types）がリアルタイムの空き・稼働状況を返すのに対し、',
      'こちらは車両にまつわる社内ナレッジを返す。車両の状態・履歴・注意点を聞かれたら両方を参照し、',
      '回答では情報の出典（予約システム / Notion）を区別して示すこと。',
      'システムの車両コード（例: M-D5-1234）は Notion 側では末尾番号（1234）で登録されているため、',
      'フルコードで0件でも自動で末尾番号による再検索が行われる。結果の note の指示には必ず従うこと',
      '（特に候補が複数のときは1つに決めつけず、候補一覧をユーザーに提示してどれか確認する）。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: '車両コード（例: M-D5-1234）・末尾番号（例: 1234）・車両名等の検索語' },
      },
      required: ['query'],
    },
  },
  {
    name: 'get_accident_notion',
    description: [
      'Notion の事故DBから事故情報を検索する。車両番号（M-D5-1234 なら末尾の 1234 でも自動対応）・',
      '顧客名等で検索できる。候補が複数返った場合（note に記載）は1つに決めつけず、',
      '候補一覧をユーザーに提示してどれか確認すること。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: '車両コード・番号・顧客名等の検索語' },
      },
      required: ['query'],
    },
  },
  {
    name: 'create_accident_notion',
    description: [
      'Notion の事故連絡簿に新しい事故記録を追記する（**新規行の作成のみ**。既存行の変更・削除は不可）。',
      '手順: ①まず get_accident_notion(query="*") で既存データのプロパティ名と登録形式を確認する',
      '②その名前に合わせて fields を組み立てる（例: 車両番号=沖縄303わ8510, 発生日時=2026-07-10 14:00, 状況=単独事故 国道）',
      '③実行するとユーザーに確認カードが表示され、承認後に書き込まれる。',
      '不明な項目は無理に埋めず、ユーザーに聞くか省略する。日時は YYYY-MM-DD HH:mm 形式。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        fields: {
          type: 'array',
          description: '書き込むプロパティの配列。name は Notion のプロパティ名（get_accident_notion の結果に出る名前）',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'プロパティ名（例: 車両番号 / 発生日時 / 状況 / 怪我有無）' },
              value: { type: 'string', description: '値' },
            },
            required: ['name', 'value'],
          },
        },
      },
      required: ['fields'],
    },
  },
  {
    name: 'search_notion',
    description: [
      'Notion の社内ドキュメント（運用ルール・契約・調整メモ等）を検索する。',
      '車両以外も含む社内ナレッジ全般の質問に使う。結果のページはタイトルと page_id が返るので、',
      '中身が必要なら続けて get_notion_page を呼ぶ。',
    ].join(' '),
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: '検索キーワード' },
      },
      required: ['query'],
    },
  },
  {
    name: 'get_notion_page',
    description: 'search_notion で見つけた Notion ページの本文とプロパティを取得する。',
    parameters: {
      type: 'object',
      properties: {
        page_id: { type: 'string', description: 'search_notion の結果に含まれる page_id' },
      },
      required: ['page_id'],
    },
  },
  {
    name: 'send_operator_request',
    description:
      '車両の出発（貸出開始）・返却のステータス変更を、オペレータのブラウザに実行リクエスト（リクエストウィンドウ）として送る。' +
      '「1234を出発にして」「1234の返却処理」等で使う。エージェントは直接ステータス変更せず、オペレータが候補予約を確認して実行する。' +
      '送信後は message をそのまま伝えること。',
    parameters: {
      type: 'object',
      properties: {
        vehicle: { type: 'string', description: '車両番号・コード（例: 1234）' },
        type: { type: 'string', description: 'departure=出発（貸出） / return=返却' },
      },
      required: ['vehicle', 'type'],
    },
  },
];

const normalizePhone = (s: string): string => s.replace(/[-\s]/g, '').toLowerCase();

// メールとして有効な文字列のみ受け入れ、そうでなければ null（空欄）にする
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const sanitizeEmail = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return EMAIL_REGEX.test(trimmed) ? trimmed : null;
};

// 電話番号: 数字とハイフン以外を含むもの（「不明」などの自由記述）は null にする
const PHONE_REGEX = /^[0-9+\-()\s]+$/;
const sanitizePhone = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return PHONE_REGEX.test(trimmed) ? trimmed : null;
};

const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (d: Date, days: number): Date => {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
};

const ensureDateTimeIso = (input: string): string => {
  // 受け入れ（2026-07-09 に寛容化）:
  //   "2026-07-10 10:00" / "2026-07-10T10:00" / "2026-07-10T10:00:00+09:00" に加え、
  //   "2026/7/10 9:30"（スラッシュ・1桁の月/日/時）も正規化する。
  //   Gemini が「明日の9時半」を 0 埋めせず変換して、確認カード承認後に
  //   throw していた事故への対策。日付のみ（時刻なし）は引き続きエラー。
  const trimmed = String(input).trim();
  if (trimmed.includes('+09:00')) return trimmed;
  const m = trimmed
    .replace('T', ' ')
    .match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m) {
    const pad = (v: string) => v.padStart(2, '0');
    const iso = `${m[1]}-${pad(m[2])}-${pad(m[3])}T${pad(m[4])}:${m[5]}:${m[6] || '00'}+09:00`;
    if (!Number.isNaN(new Date(iso).getTime())) return iso;
  }
  // 日付のみ → 時刻を仮置きしない。エラーにする
  throw new Error(`日時の形式が不正です: ${input}（YYYY-MM-DD HH:mm 形式で、時刻も含めて指定してください）`);
};

// -------- 個別 tool 実装 --------

/**
 * vehicle_id → vehicle_code の対応表（5分キャッシュ）。
 * 予約データは vehicle_id しか持たないため、車両コードを答えるにはここで解決する。
 */
let vehicleCodeCache: { at: number; map: Record<string, string> } | null = null;
async function getVehicleCodeMap(): Promise<Record<string, string>> {
  if (vehicleCodeCache && Date.now() - vehicleCodeCache.at < 5 * 60 * 1000) return vehicleCodeCache.map;
  try {
    const vehicles: any[] = await fetchDispatchVehicles();
    const map: Record<string, string> = {};
    (vehicles || []).forEach((v: any) => {
      if (v?.id != null && v?.vehicle_code) map[String(v.id)] = String(v.vehicle_code);
    });
    vehicleCodeCache = { at: Date.now(), map };
    return map;
  } catch {
    // 解決できなくても検索自体は返す（vehicle_code が null になるだけ）
    return vehicleCodeCache?.map ?? {};
  }
}

/**
 * 予約チャネルの判定。返金・延長の計算方式（LP/OTA）を AI が自動で選べるようにする。
 * contact_media（jalan / rennavi / lp[en] 等）を最優先、無ければ予約コードの形式で補う。
 * どちらでも判定できないときは「不明」を返す（推測して計算方式を選ばせない）。
 */
function classifyChannel(r: any): string {
  const media = String(r.contact_media ?? r.detail?.contact_media ?? '').toLowerCase();
  if (media !== '') {
    return media.startsWith('lp') ? '自社LP' : 'OTA';
  }
  const code = String(r.contact_code ?? '');
  if (/^(TR|PI|PT)-/.test(code)) return '自社LP';   // 那覇 TR- / 宮古 PI-（2026-10-06 実データで確認）
  return '不明';
}

function numOrNull(v: any): number | null {
  const n = Number(v);
  return Number.isFinite(n) && v !== null && v !== '' ? n : null;
}

export async function runSearchReservations(args: Record<string, any>): Promise<any> {
  const contactCode = typeof args.contact_code === 'string' ? args.contact_code.trim() : '';

  // contact_code 指定時は日付範囲なしで全期間検索（一意キーなので 1 件返るのが普通）
  const bookedFromArg = args.booked_start_date ? String(args.booked_start_date).slice(0, 10) : null;
  const bookedToArg = args.booked_end_date ? String(args.booked_end_date).slice(0, 10) : null;

  const fetchParams: Record<string, any> = {};
  let usedStartDate: string | null = null;
  let usedEndDate: string | null = null;
  if (contactCode) {
    fetchParams.contactCode = contactCode;
  } else {
    const today = new Date();
    if (args.email && !args.start_date && !args.end_date && !bookedFromArg && !bookedToArg) {
      // メールアドレス検索も貸出日の既定窓（-30〜+90日）で切ると先の予約を落とす。
      // 予約日検索と同じく窓を広げる（2026-08-16 の取りこぼし事象と同型）
      const t = new Date();
      usedStartDate = toYmd(addDays(t, -365));
      usedEndDate = toYmd(addDays(t, 365 * 2));
    } else if ((bookedFromArg || bookedToArg) && !args.start_date && !args.end_date) {
      // ⚠ 予約日で検索するときに貸出日の既定窓（-30〜+90日）で取ると、
      //    先の日付の貸出予約が**取得前に落ちる**。
      //    2026-08-16 の実測: 予約日8/16の6件のうち3件（貸出日 11/18・12/18・12/18）が
      //    窓の外で消え、AI が「3件です」と誤答した。予約日検索では窓を広く取る。
      usedStartDate = toYmd(addDays(new Date((bookedFromArg || bookedToArg) as string), -365));
      usedEndDate = toYmd(addDays(new Date((bookedToArg || bookedFromArg) as string), 365 * 2));
    } else {
      usedStartDate = args.start_date || toYmd(addDays(today, -30));
      usedEndDate = args.end_date || toYmd(addDays(today, 90));
    }
    fetchParams.startDate = usedStartDate;
    fetchParams.endDate = usedEndDate;
  }

  const reservations = await fetchDispatchReservations(fetchParams);
  const name = args.customer_name ? String(args.customer_name).toLowerCase() : null;
  const phone = args.phone ? normalizePhone(String(args.phone)) : null;
  // メールアドレス検索（2026-08-24）。実運用でメールアドレスだけ渡されて検索できず、
  // オペレータが自分で調べて予約コードを渡す羽目になった事象への対応。
  const email = args.email ? String(args.email).trim().toLowerCase() : null;
  // 予約日(申込日)での絞り込み。backend にこの検索条件は無いのでクライアント側で行う。
  const bookedFrom = bookedFromArg;
  const bookedTo = bookedToArg;
  // ⚠ backend の start_date/end_date は「その期間に**利用が重なる**予約」を返す。
  //    貸出日そのものではない（2026-08-16 実測: 8/16 指定で192件返るが貸出日が8/16のものは0件）。
  //    「本日出発」「本日返却」に答えるにはこのクライアント側フィルタが要る。
  const pickupOn = args.pickup_date ? String(args.pickup_date).slice(0, 10) : null;
  const returnOn = args.return_date ? String(args.return_date).slice(0, 10) : null;

  const filtered = reservations.filter((r: any) => {
    if (bookedFrom || bookedTo) {
      const booked = String(r.booking_date ?? '').slice(0, 10);
      if (!booked) return false;
      if (bookedFrom && booked < bookedFrom) return false;
      if (bookedTo && booked > bookedTo) return false;
    }
    if (pickupOn && String(r.start_date ?? '').slice(0, 10) !== pickupOn) return false;
    if (returnOn && String(r.end_date ?? '').slice(0, 10) !== returnOn) return false;
    if (name) {
      const cand = [r.customer_name, r.customerName, r.cname, r.contact_name]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(' | ');
      if (!cand.includes(name)) return false;
    }
    if (phone) {
      const cand = [r.customer_tel, r.tel, r.customerTel, r.phone]
        .filter(Boolean)
        .map((v: any) => normalizePhone(String(v)))
        .join(' | ');
      if (!cand.includes(phone)) return false;
    }
    if (email) {
      const cand = [r.customer_email, r.mail, r.detail?.mail]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(' | ');
      if (!cand.includes(email)) return false;
    }
    return true;
  });

  // vehicle_id だけ返すと「車両コードは？」に答えられず AI が Notion 検索に逃げるため、
  // ここで車両コード（M-D5-B-8897 等）まで解決して返す（2026-08-16 の誤答対応）。
  const codeMap = await getVehicleCodeMap();
  const simplified = filtered.slice(0, 30).map((r: any) => ({
    id: r.id ?? r.reservation_id,
    contact_code: r.contact_code ?? null,
    booking_date: r.booking_date ?? null,
    start_date: r.start_date,
    end_date: r.end_date,
    customer_name: r.customer_name ?? r.cname,
    customer_tel: r.customer_tel ?? r.tel,
    car_type: r.car_type,
    vehicle_id: r.vehicle_id ?? r.resource_id,
    vehicle_code: codeMap[String(r.vehicle_id ?? r.resource_id ?? '')] ?? null,
    status: r.status_code ?? r.status,
    // チャネルと実際の請求額（2026-08-26 会話記録#7 の改善）:
    // 返金・延長の計算はチャネルで方式が異なり（LP=差額と泊数割の少ない方 / OTA=日割り）、
    // かつ**実際の請求額**で計算する必要がある。これらが無いため AI が人に聞き返し、
    // 定価で計算しかけた。ここで返して構造的に解消する。
    channel: classifyChannel(r),
    channel_raw: r.contact_media ?? r.detail?.contact_media ?? null,
    // プラン名（OTA のプラン表記）。「セルフ」を含むかで送迎の案内が変わる（2026-09-08 記録#29）
    plan: r.contact_plan ?? r.detail?.contact_plan ?? null,
    // ホテル配車（json_data の配車情報。サイドパネルの「ホテル送迎」と同じ）。2026-11-01 セッション3205:
    // 既にあるのに読まず、出発・返却の便名欄に「ホテル配車（有）」を書こうとして4回取り消された
    hotel_delivery: (() => { const h = parseHotelDelivery(r.json_data ?? r.detail?.json_data); return h ? formatHotelDeliverySummary(h) : null; })(),
    prices: {
      base_price: numOrNull(r.base_price ?? r.detail?.base_price),
      option_price: numOrNull(r.option_price ?? r.detail?.option_price),
      noc_price: numOrNull(r.noc_price ?? r.detail?.noc_price),
      cdw_price: numOrNull(r.cdw_price ?? r.detail?.cdw_price),
      sum_price: numOrNull(r.sum_price ?? r.detail?.sum_price),
    },
  }));

  return {
    count: filtered.length,
    truncated: filtered.length > 30,
    query: {
      contact_code: contactCode || null,
      start_date: usedStartDate,
      end_date: usedEndDate,
      customer_name: args.customer_name || null,
      phone: args.phone || null,
      booked_start_date: bookedFrom,
      booked_end_date: bookedTo,
      pickup_date: pickupOn,
      return_date: returnOn,
      date_field: (bookedFrom || bookedTo) ? '予約日(申込日)' : '貸出日',
      // start_date/end_date は「その期間に利用が重なる予約」を返す（貸出日ではない）
      range_meaning: '指定期間に利用が重なる予約',
      used_default_range: !contactCode && !args.start_date && !args.end_date && !bookedFrom && !bookedTo,
    },
    results: simplified,
  };
}

/**
 * 車両タイプ id ⇔ code を相互変換するためのマップを取得。
 * bookingCalc API は code (例: T-AV-H) しか返さないが、保存 API・create_reservation の
 * vehicle_id 選定では vehicle_type_id (整数) が必要なため。
 */
async function getVehicleTypeCodeMaps(): Promise<{
  codeToId: Map<string, number>;
  idToCode: Map<number, string>;
  byId: Map<number, any>;
}> {
  const types = await fetchDispatchVehicleTypes();
  const codeToId = new Map<string, number>();
  const idToCode = new Map<number, string>();
  const byId = new Map<number, any>();
  (types as any[]).forEach((t) => {
    const id = Number(t?.id);
    const code = String(
      t?.code ?? t?.vehicle_type_code ?? '',
    ).trim();
    if (Number.isFinite(id) && id > 0) {
      byId.set(id, t);
      if (code) {
        codeToId.set(code, id);
        idToCode.set(id, code);
      }
    }
  });
  return { codeToId, idToCode, byId };
}

/**
 * 料金計算 API（n-car.jp）呼び出しのラッパー（2026-07-09）。
 * 外部サイト依存のため一時的な不調が「価格確認でたまにエラー」に直結していた。
 * 1回リトライし、それでも失敗したらユーザーに伝わるメッセージに整形する。
 */
async function fetchBookingCalcSafe(
  params: Parameters<typeof fetchBookingCalc>[0],
): Promise<BookingCalcResponse> {
  try {
    return await fetchBookingCalc(params);
  } catch (e1: any) {
    // ⚠ 4xx は「入力が不正」であって接続失敗ではない。再試行しても必ず同じ結果になり、
    //    「時間をおいて再試行」は誤った案内になる（2026-08 実ログ: 貸出と返却が同時刻で
    //    end must be after start が2件。AI が接続障害として扱っていた）。
    const input = classifyCalcInputError(e1);
    if (input) throw new Error(input);
    try {
      return await fetchBookingCalc(params);
    } catch (e2: any) {
      const input2 = classifyCalcInputError(e2);
      if (input2) throw new Error(input2);
      throw new Error(
        `料金計算サービス（n-car.jp）への接続に失敗しました。少し時間をおいて再試行してください（${e2?.message || e2}）`,
      );
    }
  }
}

/**
 * 料金APIの 4xx（入力エラー）を、AI とオペレータが対処できる日本語にする。
 * 入力エラーでなければ null（＝接続障害として扱う）。
 */
function classifyCalcInputError(e: any): string | null {
  const msg = String(e?.message || e || '');
  const m = msg.match(/API error:\s*(4\d\d)\s*(\{.*?\})?/);
  if (!m) return null;
  let apiError = '';
  try { apiError = String(JSON.parse(m[2] || '{}').error || ''); } catch { /* JSON でなければ無視 */ }
  const known: Record<string, string> = {
    'end must be after start': '返却日時が貸出日時より後になっていません。返却日時を確認してください',
  };
  const reason = known[apiError] || (apiError !== '' ? apiError : '指定内容が正しくありません');
  // 「再試行してください」と言わない。同じ入力では必ず同じ結果になるため。
  return `料金を計算できませんでした: ${reason}`;
}

const DEFAULT_TIME = '12:00';

/** YYYY-MM-DD → YYYY-MM-DDT12:00。HH:mm を含んでいればそのまま toCalcDateTime で正規化 */
function toCalcDateTimeFromAny(input: string, defaultTime = DEFAULT_TIME): string {
  const trimmed = String(input || '').trim();
  if (!trimmed) throw new Error('日時が空です');
  // HH:mm を含むかどうかをラフ判定
  const hasTime = /T\d{2}:\d{2}|\s\d{2}:\d{2}/.test(trimmed);
  if (hasTime) return toCalcDateTime(trimmed);
  return `${trimmed.slice(0, 10)}T${defaultTime}`;
}

/**
 * 指定車両タイプのうち、指定期間に予約が重ならない車両を 1 台選んで返す。
 * 空きがなければ null。
 *
 * 保存 API には `vehicle_type_id` フィールドが無く、`vehicle_id` を指定しないと
 * バックエンドが任意の車両を自動割当してしまうため、**フロント側で具体車両を 1 台選ぶ**必要がある。
 */
/**
 * 予約に付けてよい車両か: 有効 OFF（is_active=0）と、有効期間（active_from〜active_to）が予約の期間にかからない車両を除く。
 * バックエンドの active（'ON'/'OFF'）は「今日」基準なので、is_active か有効期間があればそちらで判断する（来月から使う車両まで OFF になる）
 */
export function isVehicleUsableFor(v: any, pickupYmd: string, returnYmd: string): boolean {
  const ia = v?.is_active;
  if (ia === false || ia === 0 || ia === '0') return false;
  // is_active も有効期間も無く active だけ 'OFF' のとき（理由が分からない）は、これまでどおり除く
  if (ia == null && !v?.active_from && !v?.active_to && String(v?.active ?? '').toUpperCase() === 'OFF') return false;
  const from = String(v?.active_from ?? '').slice(0, 10);
  const to = String(v?.active_to ?? '').slice(0, 10);
  if (from && returnYmd && from > returnYmd) return false;
  if (to && pickupYmd && to < pickupYmd) return false;
  return true;
}

async function pickAvailableVehicleForType(
  vehicleTypeId: number,
  pickupDate: string,
  returnDate: string,
  exclude: Set<number> = new Set(),
): Promise<{ id: number; vehicle_code: string; vehicle_number: string } | null> {
  const [vehicles, reservations] = await Promise.all([
    fetchDispatchVehicles({}),
    fetchDispatchReservations({ startDate: pickupDate, endDate: returnDate }),
  ]);

  const periodStart = new Date(`${pickupDate}T00:00:00`).getTime();
  const periodEnd = new Date(`${returnDate}T23:59:59`).getTime();

  const reservedVehicleIds = new Set<number | string>();
  reservations.forEach((r: any) => {
    const vehicleId = r.vehicle_id ?? r.resource_id;
    if (vehicleId == null) return;
    const status = String(r.status_code ?? r.status ?? '').toLowerCase();
    if (status === 'cancelled' || status === 'nonreserved') return;
    const rStart = r.start_date ? new Date(String(r.start_date).replace(' ', 'T')).getTime() : null;
    const rEnd = r.end_date ? new Date(String(r.end_date).replace(' ', 'T')).getTime() : null;
    if (rStart == null || rEnd == null) return;
    if (rStart <= periodEnd && rEnd >= periodStart) reservedVehicleIds.add(vehicleId);
  });

  const typeVehicles = vehicles.filter((v: any) => {
    if (v.out_of_use === true) return false;
    // 使っていない車両（有効 OFF・有効期間の外）は選ばない（2026-11-03 A2: 有効 OFF の CP-VN-RENT-1111 を新しい予約に付けた）
    if (!isVehicleUsableFor(v, pickupDate, returnDate)) return false;
    return Number(v.vehicle_type_id) === Number(vehicleTypeId);
  });

  const available = typeVehicles.find(
    (v: any) => !reservedVehicleIds.has(v.id ?? v.vehicle_id) && !exclude.has(Number(v.id ?? v.vehicle_id)),
  );

  if (!available) return null;
  return {
    id: Number((available as any).id ?? (available as any).vehicle_id),
    vehicle_code: String((available as any).vehicle_code ?? ''),
    vehicle_number: String((available as any).vehicle_number ?? ''),
  };
}

export async function runSearchVehicleTypes(args: Record<string, any>): Promise<any> {
  const query = String(args.query || '').trim();
  if (!query) throw new Error('query is required');

  // 時刻を捨てない（課金日数が時刻で変わるため。list_all_vehicle_types と同じ理由）
  const pickupDate = args.pickup_date ? String(args.pickup_date) : null;
  const returnDate = args.return_date ? String(args.return_date) : null;
  const withCalc = !!(pickupDate && returnDate);

  // 車両タイプマスタ + bookingCalc（期間指定時のみ）を並列取得
  const [{ codeToId, idToCode, byId }, calcResult] = await Promise.all([
    getVehicleTypeCodeMaps(),
    withCalc
      ? fetchBookingCalcSafe({
          start: toCalcDateTimeFromAny(pickupDate!),
          end: toCalcDateTimeFromAny(returnDate!),
        }).catch(() => null)
      : Promise.resolve<BookingCalcResponse | null>(null),
  ]);

  // bookingCalc の結果を code でインデックス
  const calcByCode = new Map<string, BookingCalcVehicle>();
  if (calcResult) {
    calcResult.vehicles.forEach((v) => calcByCode.set(v.code, v));
  }

  const q = query.toLowerCase();

  // タイプ名 / コード / カテゴリで部分一致
  const matchedIds = new Set<number>();
  const matchReasons = new Map<number, string>();
  byId.forEach((t, id) => {
    const name = String((t as any)?.name ?? (t as any)?.vehicle_type_name ?? '').toLowerCase();
    const code = String((t as any)?.code ?? (t as any)?.vehicle_type_code ?? '').toLowerCase();
    const category = String((t as any)?.category ?? '').toLowerCase();
    if (name.includes(q) || code.includes(q) || category.includes(q)) {
      matchedIds.add(id);
      if (name.includes(q)) matchReasons.set(id, 'type_name');
      else if (code.includes(q)) matchReasons.set(id, 'type_code');
      else matchReasons.set(id, 'type_category');
    }
  });

  // 0 件のときだけ vehicle_code / vehicle_number で具体車両も検索
  if (matchedIds.size === 0) {
    try {
      const vehicleLookup = await fetchDispatchVehicles({});
      vehicleLookup.forEach((v: any) => {
        const hay = [v.vehicle_code, v.vehicle_number, v.name]
          .filter(Boolean)
          .map((s: any) => String(s).toLowerCase())
          .join(' ');
        if (hay.includes(q)) {
          const tid = Number(v.vehicle_type_id);
          if (Number.isFinite(tid) && tid > 0 && !matchedIds.has(tid)) {
            matchedIds.add(tid);
            const reason = String(v.vehicle_code || '').toLowerCase().includes(q)
              ? 'vehicle_code'
              : String(v.vehicle_number || '').toLowerCase().includes(q)
                ? 'vehicle_number'
                : 'vehicle_name';
            matchReasons.set(tid, reason);
          }
        }
      });
    } catch { /* ignore */ }
  }

  const formatType = (id: number) => {
    const t = byId.get(id);
    const code = idToCode.get(id) || String((t as any)?.code ?? '');
    const calcVehicle = calcByCode.get(code);
    const info: any = {
      vehicle_type_id: id,
      code,
      name: (t as any)?.name ?? calcVehicle?.name_ja,
      category: (t as any)?.category,
      capacity: (t as any)?.capacity,
      // 1日目の定価（期間の基本料金ではない。2026-11-03 A1: base_price の名前で期間の基本料金と取り違えた）
      daily_list_price: calcVehicle?.base_price ?? (t as any)?.base_price ?? null,
    };
    const reason = matchReasons.get(id);
    if (reason) info.match_reason = reason;
    if (calcVehicle) {
      info.total_price = calcVehicle.breakdown.grand_total;
      Object.assign(info, priceBreakdownFields(calcVehicle.breakdown));
      if (calcVehicle.holiday_pack) info.holiday_pack = calcVehicle.holiday_pack;
      if (calcVehicle.pack_request_reason) info.pack_request_reason = calcVehicle.pack_request_reason;
      info.availability = {
        period: { pickup_date: pickupDate, return_date: returnDate },
        total_vehicles: calcVehicle.total_stock,
        available_vehicles: calcVehicle.available_stock,
        reserved_vehicles: calcVehicle.total_stock - calcVehicle.available_stock,
        has_stock: calcVehicle.available_stock > 0,
      };
    }
    return info;
  };

  if (matchedIds.size === 0) {
    const allIds = Array.from(byId.keys()).slice(0, 20);
    return {
      count: 0,
      results: [],
      alternatives: allIds.map(formatType),
      note: 'クエリに合致する車両タイプ・車両コードが見つかりませんでした。alternatives から候補を選んでください。',
      query: { query, pickup_date: pickupDate, return_date: returnDate },
    };
  }

  return {
    count: matchedIds.size,
    results: Array.from(matchedIds).slice(0, 10).map(formatType),
    query: { query, pickup_date: pickupDate, return_date: returnDate },
  };
}

export async function runListAllVehicleTypes(args0: Record<string, any>): Promise<any> {
  // 引数名の揺れを吸収（2026-09-29 セッション1501: return_datetime を渡して「必須です」で失敗）
  const args = {
    ...args0,
    pickup_date: args0.pickup_date ?? args0.pickup_datetime,
    return_date: args0.return_date ?? args0.return_datetime,
  };
  if (!args.pickup_date || !args.return_date) {
    throw new Error('pickup_date と return_date は必須です');
  }
  // 時刻を捨てない。課金日数は時刻で変わる（例: 14:00→16:00 は返却超過で +1 日課金）ため、
  // YYYY-MM-DD に丸めると LP・確認カードと料金が食い違う（過去に実事例あり）。
  // 時刻なし入力のときだけ toCalcDateTimeFromAny が 12:00 を仮定する。
  const start = toCalcDateTimeFromAny(String(args.pickup_date));
  const end = toCalcDateTimeFromAny(String(args.return_date));

  const [calcResult, { codeToId }] = await Promise.all([
    fetchBookingCalcSafe({ start, end }),
    getVehicleTypeCodeMaps(),
  ]);

  const results = calcResult.vehicles.map((v) => ({
    vehicle_type_id: codeToId.get(v.code) ?? null,
    code: v.code,
    name: v.name_ja || v.name,
    daily_list_price: v.base_price,   // 1日目の定価（期間の基本料金ではない。2026-11-03 A1）
    first_night_price: v.first_night_price,
    total_price: v.breakdown.grand_total,
    ...priceBreakdownFields(v.breakdown),
    has_stock: v.available_stock > 0,
    available_vehicles: v.available_stock,
    total_vehicles: v.total_stock,
    is_request_reservation: v.is_request_reservation,
    campaign_eligible: v.campaign_eligible,
    etc_card_free: v.etc_card_free,
    // 連休パック: 適用中ならパック内容、期間に関わるリクエスト理由（'pickup'/'return'/'span'/'no_pack'）
    holiday_pack: v.holiday_pack ?? null,
    pack_request_reason: v.pack_request_reason ?? null,
    breakdown: v.breakdown,
  }));

  // 料金の安いものから並べる（ユーザーが選びやすい）
  results.sort((a, b) => a.total_price - b.total_price);

  // 実際に計算へ使った日時（12:00 仮定が入ったかどうかも Gemini に伝わる）
  return {
    period: {
      pickup_datetime: start,
      return_datetime: end,
      note: '料金はこの日時で計算。時刻未指定分は 12:00 を仮定。',
    },
    nights: calcResult.duration.nights,
    days: calcResult.duration.option_days,
    short: calcResult.duration.short,
    campaign: calcResult.campaign,
    count: results.length,
    vehicle_types: results,
  };
}

export async function runCalculatePrice(args: Record<string, any>): Promise<any> {
  const vehicleTypeId = Number(args.vehicle_type_id);
  if (!Number.isFinite(vehicleTypeId) || vehicleTypeId <= 0) {
    throw new Error('vehicle_type_id は正の整数で指定してください');
  }

  // pickup/return: datetime を優先、無ければ date を 12:00 仮定で使う
  const pickupSrc = args.pickup_datetime || args.pickup_date;
  const returnSrc = args.return_datetime || args.return_date;
  if (!pickupSrc || !returnSrc) {
    throw new Error('pickup_datetime/return_datetime もしくは pickup_date/return_date を指定してください');
  }
  const start = toCalcDateTimeFromAny(String(pickupSrc));
  const end = toCalcDateTimeFromAny(String(returnSrc));

  const wantsNoc = args.noc_waiver === true;
  const wantsCdw = args.cdw === true;
  const childSeat = Math.max(0, Number(args.child_seat) || 0);
  const boosterSeat = Math.max(0, Number(args.booster_seat) || 0);
  const quantity = Math.max(1, Math.floor(Number(args.quantity) || 1));
  // 宮古の平良港（クルーズ船）。クルーズ船料金表と送迎料を calc 側で計算する（那覇では buildUrl が送らない）
  const location = args.location === 'cruise' ? 'cruise' as const : undefined;

  // 1 回目: 要求された組合せで計算
  const [{ idToCode, byId }, calcMain] = await Promise.all([
    getVehicleTypeCodeMaps(),
    fetchBookingCalcSafe({
      start, end,
      noc: wantsNoc, cdw: wantsCdw,
      child_seat: childSeat, booster_seat: boosterSeat,
      location,
    }),
  ]);

  const code = idToCode.get(vehicleTypeId);
  if (!code) {
    throw new Error(`vehicle_type_id=${vehicleTypeId} に対応する車両タイプコードが見つかりません`);
  }
  const target = calcMain.vehicles.find((v) => v.code === code);
  if (!target) {
    throw new Error(`vehicle_type_id=${vehicleTypeId} (code=${code}) は料金計算結果に含まれていません`);
  }

  const breakdown = target.breakdown;
  const totalInsurance = breakdown.insurance;

  // NOC + CDW 両方選択時は NOC 単独でもう 1 回計算して、insurance の差分から内訳を取得
  // （Calc API が内訳 noc/cdw を返すならそれを使う。連休パックは補償が外せないため差分方式が使えない）
  let nocPrice = 0;
  let cdwPrice = 0;
  if (typeof breakdown.noc === 'number' && typeof breakdown.cdw === 'number') {
    nocPrice = breakdown.noc;
    cdwPrice = breakdown.cdw;
  } else if (wantsNoc && wantsCdw) {
    try {
      const calcNocOnly = await fetchBookingCalcSafe({
        start, end,
        noc: true, cdw: false,
        child_seat: childSeat, booster_seat: boosterSeat,
        location,
      });
      const nocVehicle = calcNocOnly.vehicles.find((v) => v.code === code);
      nocPrice = Number(nocVehicle?.breakdown.insurance) || 0;
      cdwPrice = Math.max(0, totalInsurance - nocPrice);
    } catch {
      // フォールバック: 取得失敗時は noc 側にまとめる
      nocPrice = totalInsurance;
      cdwPrice = 0;
    }
  } else if (wantsNoc) {
    nocPrice = totalInsurance;
  } else if (wantsCdw) {
    cdwPrice = totalInsurance;
  }

  const typeMeta = byId.get(vehicleTypeId);
  const perVehicleTotal = breakdown.grand_total;
  // 平良港の送迎料は予約単位で1回（calc は1台分の grand_total に含めて返す）
  const locationSurcharge = Number(breakdown.location_surcharge) || 0;

  return {
    vehicle_type_id: vehicleTypeId,
    code,
    vehicle_type_name: target.name_ja || (typeMeta as any)?.name || code,
    period: {
      pickup_datetime: start,
      return_datetime: end,
      nights: calcMain.duration.nights,
      days: calcMain.duration.option_days,
      short: calcMain.duration.short,
    },
    options_requested: {
      noc_waiver: wantsNoc,
      cdw: wantsCdw,
      child_seat: childSeat,
      booster_seat: boosterSeat,
    },
    per_vehicle: {
      base_subtotal: breakdown.subtotal_base,        // キャンペーン適用前の基本料金
      campaign_discount: breakdown.discount,         // キャンペーン割引額
      after_campaign: breakdown.subtotal_base - breakdown.discount, // ＝割引後（オプション抜き）
      noc_price: nocPrice,
      cdw_price: cdwPrice,
      options_price: breakdown.options,              // チャイルド/ブースター等
      fees: breakdown.fees,
      location_surcharge: locationSurcharge,         // 平良港の送迎料（宮古・location=cruise のときだけ）
      grand_total: perVehicleTotal,                  // すべての加算後（1 台分）
    },
    // 名前付きの内訳（1台分）。この名前で書かせる（2026-11-03 A1・N2）
    ...priceBreakdownFields({ ...breakdown, grand_total: perVehicleTotal }, { noc: nocPrice, cdw: cdwPrice }),
    quantity,
    grand_total: (perVehicleTotal - locationSurcharge) * quantity + locationSurcharge, // quantity 台分の合計（送迎料は1回）
    // クルーズ船料金表を使ったとき（未使用・location 未指定は null）
    cruise_pricing: target.cruise_pricing ?? null,
    campaign: calcMain.campaign,
    // 連休パック（期間中・パック価格のある車種）。base_subtotal はパック価格＋超過分。クーポン割引は付かない
    holiday_pack: target.holiday_pack ?? null,
    pack_request_reason: target.pack_request_reason ?? null,
    is_request_reservation: target.is_request_reservation,
    has_stock: target.available_stock >= quantity,
    available_vehicles: target.available_stock,
    total_vehicles: target.total_stock,
    // 後方互換: 旧 calculate_price 利用者のために残す（同じ値）
    base_price: breakdown.subtotal_base,
    discount: breakdown.discount,
    total_price: perVehicleTotal,
    nights: calcMain.duration.nights,
    days: calcMain.duration.option_days,
    short: calcMain.duration.short,
    breakdown,
  };
}

export async function runSearchCustomer(args: Record<string, any>): Promise<any> {
  if (!args.name && !args.phone && !args.email) {
    throw new Error('name / phone / email のいずれかを指定してください');
  }
  const today = new Date();
  const startDate = toYmd(addDays(today, -365));
  const endDate = toYmd(addDays(today, 30));

  const reservations = await fetchDispatchReservations({ startDate, endDate });
  const name = args.name ? String(args.name).toLowerCase() : null;
  const phone = args.phone ? normalizePhone(String(args.phone)) : null;
  const email = args.email ? String(args.email).trim().toLowerCase() : null;

  const byKey = new Map<string, { customer_name: string; customer_tel: string | null; customer_email: string | null; reservation_count: number; latest: string | null }>();

  reservations.forEach((r: any) => {
    const cname = r.customer_name ?? r.cname ?? '';
    const ctel = r.customer_tel ?? r.tel ?? null;
    const cmail = r.customer_email ?? r.mail ?? null;

    if (name && !String(cname).toLowerCase().includes(name)) return;
    if (phone && (!ctel || !normalizePhone(String(ctel)).includes(phone))) return;
    if (email && (!cmail || !String(cmail).toLowerCase().includes(email))) return;

    const key = `${cname}|${ctel ?? ''}`;
    const prev = byKey.get(key);
    const startIso = r.start_date || '';
    if (prev) {
      prev.reservation_count += 1;
      if (startIso > (prev.latest || '')) prev.latest = startIso;
    } else {
      byKey.set(key, {
        customer_name: cname,
        customer_tel: ctel,
        customer_email: cmail,
        reservation_count: 1,
        latest: startIso,
      });
    }
  });

  const list = Array.from(byKey.values()).sort((a, b) => (b.latest || '').localeCompare(a.latest || ''));
  return {
    count: list.length,
    results: list.slice(0, 20),
  };
}

// --- 重複作成ガード（Track B3, 2026-07-04） ---
// Gemini がエラーリトライ等で同じ create_reservation を再度呼んだ場合の
// 重複予約をフロントで遮断する（バックエンド冪等性キーは backlog）。
const DUPLICATE_GUARD_WINDOW_MS = 5 * 60 * 1000;
const recentCreations = new Map<string, { at: number; reservationId: any }>();
/**
 * オペレータが確認カードで**取り消した**提案。
 * ⚠ 従来は成功だけを覚えていたため、取り消しても AI が同じ内容を再提案していた
 *   （2026-08-17 実ログ: 西田様の同一提案が 16:23 と 16:27 に連続で取消）。
 *   取消は「その内容ではダメ」という意思表示なので、繰り返さない。
 */
const rejectedCreations = new Map<string, number>();
/**
 * 確認カードで**取り消された更新提案**（2026-08-29 TR-CMGWL3 の拒否ループ対応）。
 * 拒否は「その内容ではダメ」の意思表示。Gemini は失敗応答を見ると同じ呼び出しを
 * 繰り返すことがあり、オペレータが何度も同じカードを拒否させられた。
 * 同一予約×同一変更内容は5分間、確認カードを出さずに止める。
 */
const rejectedUpdates = new Map<string, number>();

/** テスト用: 拒否記憶のリセット */
export const __test_clearRejectedProposals = () => { rejectedUpdates.clear(); rejectedCreations.clear(); };

function updateDedupKey(contactCode: string, changes: Array<{ key: string; after: string }>): string {
  // 2026-09-19 記録#30: status→booked を拒否されると、AI がメモ文言だけ変えて3回再提案した。
  // memo / comment は注釈であって「変更の実体」ではないので、実体（status・日時・フラグ等）
  // だけでキーを作る。実体が無い（メモだけの提案）ときだけ全項目で判定する。
  const substantive = changes.filter((c) => c.key !== 'memo' && c.key !== 'comment');
  const basis = substantive.length > 0 ? substantive : changes;
  return contactCode + '|' + basis.map((c) => `${c.key}=${c.after}`).sort().join('&');
}

function creationDedupKey(args: Record<string, any>): string {
  return [
    String(args.pickup_datetime || ''),
    String(args.return_datetime || ''),
    Array.isArray(args.vehicles) && args.vehicles.length
      ? args.vehicles.map((v: any) => `${v.vehicle_type_id}x${v.quantity ?? 1}`).join('+')
      : String(args.vehicle_type_id || ''),
    String(args.customer_name || '').trim(),
  ].join('|');
}

/** 作る車両の並び: vehicles（別車種の組み合わせ）か vehicle_type_id × quantity（同一車種） */
export function normalizeVehicleLines(args: Record<string, any>): Array<{ vehicle_type_id: number; quantity: number }> {
  if (Array.isArray(args.vehicles) && args.vehicles.length > 0) {
    return args.vehicles
      .map((v: any) => ({ vehicle_type_id: Number(v.vehicle_type_id), quantity: Math.max(1, Math.floor(Number(v.quantity) || 1)) }))
      .filter((v: any) => Number.isFinite(v.vehicle_type_id) && v.vehicle_type_id > 0);
  }
  return [{ vehicle_type_id: Number(args.vehicle_type_id), quantity: Math.max(1, Math.floor(Number(args.quantity) || 1)) }];
}

interface CarUnit {
  typeId: number; code: string; typeName: string; breakdown: any;
  nocPrice: number; cdwPrice: number;
  picked?: { id: number; vehicle_code: string; vehicle_number: string };
}

export async function runCreateReservation(
  args: Record<string, any>,
  context: {
    requestConfirm: (args: Record<string, any>) => Promise<boolean>;
    requestWizard?: (fields: WizardField[]) => Promise<Record<string, any>>;
  },
): Promise<any> {
  // 超必須項目チェック（customer_name はフロントウィザードで収集するためここでは要求しない）
  const hasVehicles = Array.isArray(args.vehicles) && args.vehicles.length > 0;
  const required = hasVehicles ? ['pickup_datetime', 'return_datetime'] : ['pickup_datetime', 'return_datetime', 'vehicle_type_id'];
  const missing = required.filter((k) => args[k] === undefined || args[k] === null || args[k] === '');
  if (missing.length > 0) {
    throw new Error(`必須項目が不足しています: ${missing.join(', ')}`);
  }
  const lines = normalizeVehicleLines(args);
  if (lines.length === 0) return { success: false, error: '車両タイプを指定してください（vehicle_type_id または vehicles）' };
  const qty = lines.reduce((x, l) => x + l.quantity, 0);
  if (qty > 10) return { success: false, error: '一度に作れるのは10台までです' };

  // 必須・準必須含む全項目のうち未指定のものはフロントのウィザードで収集
  // （Gemini は重要質問を自分で聞かない方針。systemPrompt で制御）
  if (context.requestWizard) {
    const pending = RESERVATION_WIZARD_FIELDS.filter(
      (f) => args[f.key] === undefined || args[f.key] === null || args[f.key] === '',
    );
    // 複数台のときだけ、請求書をまとめるかをスタッフに聞く（2026-10-19 前田さん）
    if (qty > 1 && !args.invoice_grouping) pending.push(INVOICE_GROUPING_FIELD);
    if (pending.length > 0) {
      const collected = await context.requestWizard(pending);
      Object.keys(collected).forEach((k) => {
        if (collected[k] !== undefined && collected[k] !== null && collected[k] !== '') {
          args[k] = collected[k];
        }
      });
    }
  }

  // 複数台（2026-10-19〜）: 台数分の予約を枝番付きで作る（憲法 booking-platform/md/multi-vehicle.md
  // 「1予約=1台・枝番でグループ・請求書はグループで1枚」）。別車種の組み合わせも1グループにできる
  const groupInvoice = qty > 1 && /まとめ/.test(String(args.invoice_grouping ?? ''));
  if (qty > 1 && !args.invoice_grouping) {
    return { success: false, error: '複数台の予約は、請求書をまとめるか（1枚にまとめる／台ごとに分ける）をスタッフに確認してから作成します' };
  }

  // 必須項目（予約者名）がウィザード後にも欠けている場合は中断
  if (!args.customer_name || String(args.customer_name).trim() === '') {
    return { success: false, error: '予約者名が指定されなかったため予約を作成できませんでした' };
  }

  // 重複作成ガード（B3）: 直近5分以内に同一内容（日時・車両タイプ・予約者名）で
  // 作成済みなら、確認カードを出す前に遮断する
  const dedupKey = creationDedupKey(args);
  const rejectedAt = rejectedCreations.get(dedupKey);
  if (rejectedAt && Date.now() - rejectedAt < DUPLICATE_GUARD_WINDOW_MS) {
    return {
      success: false,
      rejected_guard: true,
      error: '同じ内容の予約作成を直前にユーザーが取り消しています。'
        + '同じ内容で作り直さず、どこを変えればよいか（日時・車両タイプ・予約者名など）を'
        + 'ユーザーに確認してください。',
    };
  }
  const recent = recentCreations.get(dedupKey);
  if (recent && Date.now() - recent.at < DUPLICATE_GUARD_WINDOW_MS) {
    return {
      success: false,
      duplicate_guard: true,
      existing_reservation_id: recent.reservationId,
      error: `同一内容の予約（同じ日時・車両タイプ・予約者名）を直前に作成済みです（予約ID: ${recent.reservationId}）。重複防止のため作成を中断しました。本当に2件目が必要な場合はその旨をユーザーに確認してください。`,
    };
  }

  // 日時は確認カードを出す前に検証・正規化する（承認後に throw して「承認したのにエラー」にしない）
  let startIso: string;
  let endIso: string;
  try {
    startIso = ensureDateTimeIso(String(args.pickup_datetime));
    endIso = ensureDateTimeIso(String(args.return_datetime));
  } catch (e: any) {
    return { success: false, error: e?.message || String(e) };
  }
  const startCal = toCalcDateTimeFromAny(String(args.pickup_datetime));
  const endCal = toCalcDateTimeFromAny(String(args.return_datetime));
  const childSeat = Number(args.child_seat) || 0;
  const boosterSeat = Number(args.booster_seat) || 0;
  const wantsNoc = args.noc_waiver === true;
  const wantsCdw = args.cdw === true;

  // 自社予約サイト calc API で料金計算（NOC/CDW/オプション/キャンペーン込み）。全車種が1回で返る
  const { idToCode, byId } = await getVehicleTypeCodeMaps();
  const calcMain = await fetchBookingCalcSafe({
    start: startCal, end: endCal, noc: wantsNoc, cdw: wantsCdw, child_seat: childSeat, booster_seat: boosterSeat,
  });
  // NOC / CDW を別々に保存するため、両方選択時は NOC 単体で再計算して内訳を取る
  let calcNocOnly: any = null;
  if (wantsNoc && wantsCdw) {
    try {
      calcNocOnly = await fetchBookingCalcSafe({
        start: startCal, end: endCal, noc: true, cdw: false, child_seat: childSeat, booster_seat: boosterSeat,
      });
    } catch { calcNocOnly = null; }
  }
  const optionDays = calcMain.duration.option_days;
  const units: CarUnit[] = [];
  for (const line of lines) {
    const code = idToCode.get(line.vehicle_type_id);
    if (!code) return { success: false, error: `vehicle_type_id=${line.vehicle_type_id} に対応する車両タイプコードが見つかりません` };
    const typeName = (byId.get(line.vehicle_type_id) as any)?.name || code;
    const tv = calcMain.vehicles.find((v) => v.code === code);
    if (!tv) return { success: false, error: `${typeName} (${code}) は料金計算結果に含まれていません` };
    const ins = Number(tv.breakdown.insurance) || 0;
    let nocPrice = 0; let cdwPrice = 0;
    if (wantsNoc && wantsCdw) {
      const nv = calcNocOnly?.vehicles?.find((v: any) => v.code === code);
      if (nv) { nocPrice = Number(nv.breakdown.insurance) || 0; cdwPrice = Math.max(0, ins - nocPrice); } else { nocPrice = ins; }
    } else if (wantsNoc) nocPrice = ins;
    else if (wantsCdw) cdwPrice = ins;
    for (let k = 0; k < line.quantity; k++) units.push({ typeId: line.vehicle_type_id, code, typeName, breakdown: tv.breakdown, nocPrice, cdwPrice });
  }
  const first = units[0];

  // 具体車両を台数分選ぶ（vehicle_id を送らないとバックエンドが任意に自動割当してしまうため）。
  // 確認カードの前に選ぶ: 台数分の空きが無いのに承認させない
  const pickupYmd = String(args.pickup_datetime).slice(0, 10);
  const returnYmd = String(args.return_datetime).slice(0, 10);
  const used = new Set<number>();
  for (const u of units) {
    const v = await pickAvailableVehicleForType(u.typeId, pickupYmd, returnYmd, used);
    if (!v) {
      const need = units.filter((x) => x.typeId === u.typeId).length;
      const got = units.filter((x) => x.typeId === u.typeId && x.picked).length;
      return {
        success: false,
        error: `指定期間で ${u.typeName} の空き車両が` + (need > 1 ? `${need}台分ありません（空き ${got}台）` : '見つかりませんでした'),
      };
    }
    u.picked = v;
    used.add(v.id);
  }

  // 1台ごとの金額: シート類は1台目だけに付ける（2026-10-19 前田さん了承。シート数は全台の合計として聞いている）
  const perCarSum = (k: number) => (k === 0 ? units[k].breakdown.grand_total : units[k].breakdown.grand_total - units[k].breakdown.options);
  const groupTotal = units.reduce((x, _u, k) => x + perCarSum(k), 0);
  const groupBase = qty > 1 ? generateGroupBaseCode() : '';
  const groupCodes = qty > 1 ? units.map((_u, k) => `${groupBase}-${k + 1}`) : [];
  // フリート割: 別車種も合算。コンパクトカー・アルファード20系は対象外（台数にも数えない）。請求書にだけ反映
  const fleet = qty > 1 ? fleetDiscountFor(units.map((u) => ({ code: u.code, base: Number(u.breakdown.subtotal_base) || 0 })))
    : { eligibleCount: 0, rate: 0, amount: 0, excludedCount: 0 };
  const mix = Array.from(new Map(units.map((u) => [u.code, u])).values())
    .map((u) => `${u.typeName}×${units.filter((x) => x.code === u.code).length}`).join('・');

  // ユーザー確認（手数料内訳を渡して確認カードに表示）。単数の表示項目は1台目の値
  const confirmed = await context.requestConfirm({
    ...args,
    vehicle_type_id: first.typeId,
    ...(qty > 1 ? {
      quantity: qty,
      vehicle_mix: mix,
      group_codes: groupCodes,
      group_total: groupTotal,
      invoice_grouping: groupInvoice ? '1枚にまとめる（下書きで作成・全台に同じ請求書を結ぶ）' : '台ごとに分ける（各予約のサイドパネルから発行）',
      fleet_note: fleet.amount > 0
        ? `フリート割 ${Math.round(fleet.rate * 100)}%（対象 ${fleet.eligibleCount}台・-¥${fleet.amount.toLocaleString()}）は請求書にだけ反映`
          + (fleet.excludedCount ? `。コンパクト・20系 ${fleet.excludedCount}台は対象外` : '')
        : (fleet.excludedCount ? `フリート割なし（コンパクト・20系 ${fleet.excludedCount}台は対象外で、対象が3台未満）` : ''),
    } : {}),
    base_price: first.breakdown.subtotal_base,
    discount: first.breakdown.discount,
    coupon_applied: first.breakdown.coupon_applied,
    options_price: first.breakdown.options,
    fees_price: first.breakdown.fees,
    noc_price: first.nocPrice,
    cdw_price: first.cdwPrice,
    insurance: first.breakdown.insurance,
    sum_price: first.breakdown.grand_total,
    days: optionDays,
    nights: calcMain.duration.nights,
    short: calcMain.duration.short,
    vehicle_type_name: qty > 1 ? mix : first.typeName,
    vehicle_type_code: first.code,
    campaign: calcMain.campaign,
    breakdown: first.breakdown,
  });
  if (!confirmed) {
    // 取消を覚えておき、同じ内容の再提案を止める（2026-08-17 の再提案事象）
    rejectedCreations.set(dedupKey, Date.now());
    return { success: false, cancelled: true, message: 'ユーザーが予約作成をキャンセルしました' };
  }

  const comment = String(args.comment || '').trim();
  const sanitizedEmail = sanitizeEmail(args.email);
  const sanitizedPhone = sanitizePhone(args.phone);
  const contactMedia = String(args.contact_media ?? '').trim();

  // status: 'booked' の文字列→ID変換が statusMapper キャッシュに依存するため、保存直前に populate を保証
  await ensureStatusCachePopulated();

  const savedList: any[] = [];
  for (let k = 0; k < units.length; k++) {
    const u = units[k];
    const isFirst = k === 0;
    // detail.coupon: 割引額（正の整数）。キャンペーン名は json_data.campaign に別途保存される
    const discountAmount = Number(u.breakdown.discount || 0);
    const groupLine = qty > 1
      ? `【複数台予約・全${qty}台(この予約は${k + 1}台目)】${mix}／グループの予約コードは ${groupCodes[0]} 〜 ${groupCodes[qty - 1]}`
        + (isFirst ? '' : '（チャイルド・ブースターシートは1台目に計上）')
      : '';
    // 予約データの memo（タイムライン形式）にエージェント実行ログを記録
    const memoEntry = buildAgentMemoEntry('reservation', [
      qty > 1 ? `エージェント経由で複数台予約を作成（${k + 1}/${qty}台目）` : 'エージェント経由で新規予約を作成',
      `車両: ${u.typeName} (${u.code})`,
      `期間: ${formatDateTime(startIso)} 〜 ${formatDateTime(endIso)}`,
      `合計: ¥${Number(perCarSum(k)).toLocaleString()}` + (qty > 1 ? `（グループ合計 ¥${groupTotal.toLocaleString()}）` : ''),
      ...(contactMedia ? [`予約経路: ${contactMedia}`] : []),
      ...(qty > 1 ? [`請求書: ${groupInvoice ? 'グループで1枚' : '台ごと'}`] : []),
    ]);
    const detail: Record<string, any> = {
      base_price: u.breakdown.subtotal_base,
      noc: wantsNoc,
      noc_price: u.nocPrice,
      cdw: wantsCdw,
      cdw_price: u.cdwPrice,
      pax: isFirst ? (Number(args.pax) || 0) : 0,
      pax_children: isFirst ? (Number(args.pax_children) || 0) : 0,
      child_seat: isFirst ? childSeat : 0,
      booster_seat: isFirst ? boosterSeat : 0,
      arrival_flight: String(args.arrival_flight || '').trim(),
      departure_flight: String(args.departure_flight || '').trim(),
      option_price: isFirst ? u.breakdown.options : 0,
      sum_price: perCarSum(k),
      days: optionDays,
      nights: calcMain.duration.nights,
      coupon: discountAmount > 0 ? discountAmount : '',
      comment: [groupLine, comment].filter(Boolean).join('\n'),
      // 予約経路（2026-10-19 前田さん: エージェントから作った予約にも残す）
      ...(contactMedia ? { contact_media: contactMedia } : {}),
    };
    const reservationInput: any = {
      status: 'booked',
      memo: memoEntry, // root に置けば buildDetailPayload が detail.memo に格納する
      start_date: startIso,
      end_date: endIso,
      vehicle_id: u.picked!.id,
      customer_name: String(args.customer_name),
      customer_tel: sanitizedPhone,
      customer_email: sanitizedEmail,
      car_type: u.typeName,
      ...(qty > 1 ? { contact_code: groupCodes[k] } : {}),
      detail,
      json_data: {
        agent_created: true,
        breakdown: u.breakdown,
        campaign: calcMain.campaign,
        duration: calcMain.duration,
        ...(qty > 1 ? { group: { base: groupBase, index: k + 1, count: qty, total: groupTotal, mix, invoice: groupInvoice ? 'grouped' : 'separate' } } : {}),
      },
    };
    const saved = await saveDispatchReservation(reservationInput);
    if (!saved) {
      return {
        success: false,
        error: `${k + 1}台目の保存に失敗しました。`
          + (savedList.length ? `作成済み: ${savedList.map((x) => x.contact_code).join(', ')}（残りはサイドパネルで作成するか、作成済みを確認してください）` : '予約は作成されていません'),
      };
    }
    savedList.push({ ...saved, unit: u });
  }
  const saved = savedList[0];
  // 重複作成ガードに記録（B3）
  recentCreations.set(dedupKey, { at: Date.now(), reservationId: saved?.id ?? null });

  // 請求書をまとめる: グループで1枚の Square 請求書を**下書き**で作り、全台の予約に同じ invoice_link を結ぶ
  let invoice: any = null;
  if (qty > 1 && groupInvoice) {
    invoice = await createGroupInvoiceDraft({
      codes: savedList.map((x) => String(x.contact_code ?? '')),
      reservationIds: savedList.map((x) => Number(x.id)),
      customer: { name: String(args.customer_name), email: sanitizedEmail, tel: sanitizedPhone },
      startIso, endIso, typeName: mix, qty, contactMedia, comment,
      lines: buildGroupInvoiceLines({ units, optionDays, childSeat, boosterSeat, wantsNoc, wantsCdw }),
      discounts: [
        ...units.map((u, k) => ({ name: `キャンペーン割引（${u.typeName} ${k + 1}台目）`, amount: Number(u.breakdown.discount || 0) }))
          .filter((d) => d.amount > 0),
        ...(fleet.amount > 0 ? [{ name: `フリート割引 ${Math.round(fleet.rate * 100)}%（対象${fleet.eligibleCount}台・基本料金）`, amount: fleet.amount }] : []),
      ],
      days: optionDays, nights: calcMain.duration.nights,
    });
  }

  return {
    success: true,
    reservation_id: saved?.id ?? null,
    // ⚠ contact_code を返さないと、AI が後続ターンで ID を予約番号として使い迷走する（2026-08-17）
    contact_code: saved?.contact_code ?? null,
    ...(qty > 1 ? {
      quantity: qty,
      vehicle_mix: mix,
      group_codes: savedList.map((x) => x.contact_code),
      group_total: groupTotal,
      fleet: { rate: fleet.rate, amount: fleet.amount, eligible: fleet.eligibleCount, excluded: fleet.excludedCount },
      invoice: groupInvoice
        ? (invoice?.success
          ? { mode: 'grouped', status: '下書き', invoice_link: invoice.invoice_link, linked: invoice.linked,
              ...(invoice.error ? { warning: invoice.error } : {}),
              note: 'Square で内容を確認してから公開（送信）してください。お客様にはまだ届いていません' }
          : { mode: 'grouped', error: invoice?.error ?? '請求書を作れませんでした（予約は作成済み）。サイドパネルから発行してください' })
        : { mode: 'separate', note: '請求書は各予約のサイドパネルから1台ずつ発行してください'
            + (fleet.amount > 0 ? `（フリート割 ${Math.round(fleet.rate * 100)}% -¥${fleet.amount.toLocaleString()} は請求書で手動で引く）` : '') },
    } : {}),
    base_price: first.breakdown.subtotal_base,
    noc_price: first.nocPrice,
    cdw_price: first.cdwPrice,
    discount: first.breakdown.discount,
    sum_price: perCarSum(0),
    days: optionDays,
    nights: calcMain.duration.nights,
    vehicle_type_name: first.typeName,
    ...(contactMedia ? { contact_media: contactMedia } : {}),
    campaign: calcMain.campaign,
    assigned_vehicle: { id: first.picked!.id, vehicle_code: first.picked!.vehicle_code, vehicle_number: first.picked!.vehicle_number },
    ...(qty > 1 ? { assigned_vehicles: units.map((u) => `${u.typeName}: ${u.picked!.vehicle_code}`) } : {}),
  };
}

/** 複数台グループの予約コードの親（那覇 TR- / 宮古 PI-。英大文字＋数字 6+6） */
export function generateGroupBaseCode(): string {
  const A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const r = (n: number) => Array.from({ length: n }, () => A[Math.floor(Math.random() * A.length)]).join('');
  return `${getStoreKey() === 'pit' ? 'PI' : 'TR'}-${r(6)}-${r(6)}`;
}

/** グループ請求書の明細（LP の ReservationForm と同じ形: 車両ごとに レンタカー/深夜早朝/NOC/CDW、シートは1台目） */
export function buildGroupInvoiceLines(p: {
  units: Array<{ typeName: string; breakdown: any; nocPrice: number; cdwPrice: number }>;
  optionDays: number; childSeat: number; boosterSeat: number; wantsNoc: boolean; wantsCdw: boolean;
}): Array<{ name: string; quantity: number; amount: number }> {
  const lines: Array<{ name: string; quantity: number; amount: number }> = [];
  const d = Math.max(1, p.optionDays);
  p.units.forEach((u, k) => {
    lines.push({ name: `レンタカー ${u.typeName}（${k + 1}台目）`, quantity: 1, amount: Number(u.breakdown.subtotal_base) || 0 });
    if (Number(u.breakdown.fees) > 0) lines.push({ name: '深夜・早朝手数料', quantity: 1, amount: Number(u.breakdown.fees) });
    if (p.wantsNoc && u.nocPrice > 0) lines.push({ name: `NOC（免責補償）${u.typeName}`, quantity: d, amount: Math.round(u.nocPrice / d) });
    if (p.wantsCdw && u.cdwPrice > 0) lines.push({ name: `CDW（車両損害補償）${u.typeName}`, quantity: d, amount: Math.round(u.cdwPrice / d) });
  });
  const opt = Number(p.units[0]?.breakdown.options) || 0;
  if (opt > 0) {
    const label = [p.childSeat ? `チャイルドシート ×${p.childSeat}` : '', p.boosterSeat ? `ブースターシート ×${p.boosterSeat}` : ''].filter(Boolean).join('・') || 'オプション';
    lines.push({ name: label, quantity: 1, amount: opt });
  }
  return lines;
}

/** グループで1枚の Square 請求書を下書きで作り、全台の予約に同じ invoice_link を書き込む */
async function createGroupInvoiceDraft(p: {
  codes: string[]; reservationIds: number[]; customer: { name: string; email: string; tel: string };
  startIso: string; endIso: string; typeName: string; qty: number; contactMedia: string; comment: string;
  lines: Array<{ name: string; quantity: number; amount: number }>; discounts: Array<{ name: string; amount: number }>;
  days: number; nights: number;
}): Promise<{ success: boolean; invoice_link?: string; linked?: string[]; error?: string }> {
  if (!isSquareEnabled()) return { success: false, error: 'この店舗では Square 請求書が使えません' };
  if (!p.customer.email) return { success: false, error: 'お客様のメールアドレスが無いため請求書を作れません（予約は作成済み）' };
  const label = `複数台${p.qty}台（${p.typeName}×${p.qty}）`;
  const linesTotal = p.lines.reduce((x, l) => x + l.quantity * l.amount, 0);
  let data: any;
  try {
    // publish を付けない = 下書き。お客様には送られない（スタッフが Square で確認して公開する）
    const res = await fetch(`${SYS_INVOICE_BASE}/square_create_invoice.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sqNum: getSquareIndex(),
        contact_code: p.codes[0],
        customer_name: p.customer.name, customer_email: p.customer.email, customer_tel: p.customer.tel,
        start_date: p.startIso, end_date: p.endIso,
        car_type: label, days: p.days, nights: p.nights,
        detail: {
          contact_id: p.codes[0], base_price: linesTotal, contact_plan: label, contact_media: p.contactMedia,
          child_seat: 0, booster_seat: 0, noc: false, cdw: false, coupon: 0, comment: p.comment,
          group_line_items: p.lines, group_discounts: p.discounts,
        },
        json_data: { data: { lang: 'ja' } },
      }),
    });
    data = await res.json().catch(() => null);
  } catch (e: any) {
    return { success: false, error: `請求書 API に接続できませんでした（${e?.message || e}）。予約は作成済みです` };
  }
  if (!data || data.status !== 'success' || !data.invoice_id) {
    return { success: false, error: `請求書の作成に失敗しました（${data?.message ?? data?.error ?? '不明'}）。予約は作成済みです` };
  }
  const link = String(data.invoice_id);
  // 全台に同じ請求書を結ぶ（どのレコードを開いても同じ請求書に飛ぶ）
  const linked: string[] = [];
  for (let i = 0; i < p.reservationIds.length; i++) {
    try {
      const fresh = (await fetchDispatchReservations({ id: p.reservationIds[i] }) as any[])
        .find((r) => Number(r.id) === p.reservationIds[i]);
      if (!fresh) continue;
      const merged: any = { ...fresh, detail: { ...(fresh.detail || {}), invoice_link: link }, invoice_link: link, __completeDetail: true };
      if (await saveDispatchReservation(merged)) linked.push(p.codes[i]);
    } catch { /* 下で未結合として返す */ }
  }
  if (linked.length < p.codes.length) {
    return { success: true, invoice_link: link, linked,
      error: `請求書は作成しましたが、${p.codes.filter((c) => !linked.includes(c)).join(', ')} に結べませんでした。サイドパネルで請求書IDを入れてください` };
  }
  return { success: true, invoice_link: link, linked };
}

// -------- update_reservation --------

/** どのキーが「実際に上書き対象として渡された」かを判定するためのホワイトリスト */
const UPDATE_FIELD_KEYS = [
  'status',
  'pickup_datetime',
  'return_datetime',
  'arrival_flight',
  'departure_flight',
  'customer_name',
  'phone',
  'email',
  'pax',
  'pax_children',
  'child_seat',
  'booster_seat',
  'noc_waiver',
  'cdw',
  'comment',
  'short_memo',
  'memo',
] as const;

/** 表示用ラベル */
const UPDATE_FIELD_LABELS: Record<string, string> = {
  status: 'ステータス',
  pickup_datetime: '貸出日時',
  return_datetime: '返却日時',
  arrival_flight: '到着便',
  departure_flight: '出発便',
  customer_name: '予約者名',
  phone: '電話番号',
  email: 'メール',
  pax: '乗車人数（大人+子供の合計）',
  pax_children: '子供の人数',
  child_seat: 'チャイルドシート',
  booster_seat: 'ブースターシート',
  noc_waiver: 'NOC免責',
  cdw: 'CDW',
  comment: 'お客様の要望',
  short_memo: 'ショートメモ',
  memo: '社内メモ',
};

const STATUS_LABEL_MAP: Record<string, string> = {
  booked: '予約確定',
  done: '貸出済み',
  in_use: '貸出中',
  pending: '確認中',
  received: '受信済み',
  mail_ready: '要連絡',
  offer: 'オファー中',
  request: 'リクエスト',
  cancelled: 'キャンセル',
  nonreserved: '予約に至らなかった',
  full: '予約がいっぱい',
};

const formatStatus = (code: any): string => {
  if (!code) return '';
  const key = String(code).toLowerCase();
  return STATUS_LABEL_MAP[key] ? `${STATUS_LABEL_MAP[key]} (${key})` : String(code);
};

const formatDateTime = (v: any): string => {
  if (!v) return '';
  const s = String(v).replace('T', ' ').slice(0, 16);
  return s;
};

const formatBool = (v: any): string => {
  if (v === true || v === 1 || v === '1' || v === 'true') return 'あり';
  if (v === false || v === 0 || v === '0' || v === 'false') return 'なし';
  return '';
};

const formatFieldValue = (key: string, value: any): string => {
  if (value === null || value === undefined || value === '') return '（未設定）';
  switch (key) {
    case 'status':
      return formatStatus(value);
    case 'pickup_datetime':
    case 'return_datetime':
      return formatDateTime(value);
    case 'noc_waiver':
    case 'cdw':
      return formatBool(value);
    default:
      return String(value);
  }
};

export async function runUpdateReservation(
  args: Record<string, any>,
  context: {
    requestUpdateConfirm?: (
      before: Record<string, any>,
      changes: Array<{ key: string; label: string; before: string; after: string }>,
    ) => Promise<boolean>;
  },
): Promise<any> {
  const reservationId = parseInt(String(args.reservation_id || '').trim(), 10);
  const contactCode = typeof args.contact_code === 'string' ? args.contact_code.trim() : '';
  if (!Number.isFinite(reservationId) && !contactCode) {
    return { success: false, error: 'reservation_id または contact_code を指定してください' };
  }

  // AI が予約 ID（14693 等）を contact_code に入れてくる誤りが実機で起きた（2026-08-17）。
  // 数字だけで reservation_id が無い場合は ID として扱う（決定的フォールバック）。
  let effectiveReservationId = reservationId;
  let effectiveContactCode = contactCode;
  if (!Number.isFinite(reservationId) && /^\d+$/.test(contactCode)) {
    effectiveReservationId = Number(contactCode);
    effectiveContactCode = '';
  }

  // 既存予約を取得
  // 2026-07-09 修正: 従来は「±365日の日付窓で全件取得→クライアントで id フィルタ」で、
  // 貸出日が1年超先/前の予約が「見つかりません」になっていた。編集パネルの保存
  // (useReservationSave.handleSave) と同じ id 直接フィルタに切替（本番実績あり）。
  let candidates: any[];
  if (Number.isFinite(effectiveReservationId) && effectiveReservationId > 0) {
    candidates = await fetchDispatchReservations({ id: effectiveReservationId });
    if (!Array.isArray(candidates) || candidates.length === 0) {
      // 保険: id フィルタが効かない場合は従来の日付窓で検索
      const today = new Date();
      candidates = await fetchDispatchReservations({
        startDate: toYmd(addDays(today, -365)),
        endDate: toYmd(addDays(today, 365)),
      });
    }
  } else {
    candidates = await fetchDispatchReservations({ contactCode: effectiveContactCode });
  }
  const target = (candidates as any[]).find((r) => {
    if (Number.isFinite(effectiveReservationId) && effectiveReservationId > 0) {
      return Number(r.id ?? r.reservation_id) === effectiveReservationId;
    }
    return String(r.contact_code || '') === effectiveContactCode;
  });

  if (!target) {
    const idDesc = Number.isFinite(effectiveReservationId) ? `id=${effectiveReservationId}` : `contact_code=${effectiveContactCode}`;
    return { success: false, error: `対象の予約が見つかりませんでした (${idDesc})` };
  }

  const detail: any = (target as any).detail || {};

  // 既存値の正規化（差分比較用）
  const existingValues: Record<string, any> = {
    status: (target as any).status_code ?? (target as any).status,
    pickup_datetime: (target as any).start_date,
    return_datetime: (target as any).end_date,
    arrival_flight: detail.arrival_flight,
    departure_flight: detail.departure_flight,
    customer_name: (target as any).customer_name,
    phone: (target as any).customer_tel,
    email: (target as any).customer_email,
    pax: detail.pax,
    pax_children: detail.pax_children,
    child_seat: detail.child_seat,
    booster_seat: detail.booster_seat,
    noc_waiver: detail.noc,
    cdw: detail.cdw,
    comment: detail.comment,
    short_memo: (target as any).short_memo,
    memo: detail.memo,
  };

  // 渡された項目だけを「変更対象」として抽出。値が既存と同じものは除外
  const changes: Array<{ key: string; label: string; before: string; after: string; rawAfter: any; plainAfter?: string }> = [];
  for (const key of UPDATE_FIELD_KEYS) {
    if (!(key in args)) continue;
    const newValue = (args as any)[key];
    // memo は「置換」ではなく常に「追記」(2026-07-09 修正):
    //   従来は既存メモ全体を newValue で置き換えていたため、キャンセル依頼等で
    //   これまでの経緯（タイムライン）が全消しされていた。確認カードにも
    //   「既存の履歴は維持して追記」であることを明示する。
    if (key === 'memo') {
      const note = String(newValue ?? '').trim();
      if (!note) continue;
      changes.push({ key, label: '社内メモ（追記）', before: '（既存の履歴は維持）', after: note, rawAfter: note });
      continue;
    }
    // comment（お客様の要望）も**追記**。上書きするとお客様の元の要望が消える
    // （2026-08-29 TR-CMGWL3: AI が上書きを提案し、オペレータが承認できず拒否ループになった）
    if (key === 'comment') {
      const note = String(newValue ?? '').trim();
      if (!note) continue;
      const cur = String(existingValues.comment ?? '').trim();
      if (cur !== '' && cur === note) continue;   // 同一内容なら no-op
      changes.push({ key, label: 'お客様の要望（追記）', before: cur !== '' ? '（既存の要望は維持）' : '（未設定）', after: note, rawAfter: note });
      continue;
    }
    const oldValue = existingValues[key];
    const beforeStr = formatFieldValue(key, oldValue);
    const plainAfter = formatFieldValue(key, newValue);   // 保存後の照合はこの値で行う（注意書きを混ぜない）
    let afterStr = plainAfter;
    if (beforeStr === afterStr) continue; // no-op
    // 2026-09-19 記録#30: 「リクエストOK」を booked と解釈して3回拒否された → カードで知らせる。
    // ただし在庫があればオファーを経ずに即確定する運用もある（2026-10-13 記録#40）ので、警告ではなく確認の一言にする
    if (key === 'status' && /request/i.test(String(oldValue)) && /booked/i.test(String(newValue))) {
      afterStr += '（リクエストから直接確定。お客様の返信を待つ承諾なら「オファー中」）';
    }
    changes.push({ key, label: UPDATE_FIELD_LABELS[key] || key, before: beforeStr, after: afterStr, rawAfter: newValue, plainAfter });
  }

  if (changes.length === 0) {
    return { success: false, error: '変更項目が指定されていない、または既存値と同じです' };
  }

  // 日時変更は確認カードを出す前に形式検証する（2026-07-09:
  // 従来は承認後に throw して「承認したのにエラー」になっていた）
  for (const c of changes) {
    if (c.key === 'pickup_datetime' || c.key === 'return_datetime') {
      try {
        ensureDateTimeIso(String(c.rawAfter));
      } catch (e: any) {
        return { success: false, error: e?.message || String(e) };
      }
    }
  }

  // 確認カードを表示
  const beforeSnapshot = {
    reservation_id: (target as any).id,
    contact_code: (target as any).contact_code,
    customer_name: (target as any).customer_name,
    car_type: (target as any).car_type,
    pickup_datetime: formatDateTime((target as any).start_date),
    return_datetime: formatDateTime((target as any).end_date),
    status: formatStatus((target as any).status_code ?? (target as any).status),
  };

  const dedupKey = updateDedupKey(contactCode || String(effectiveReservationId), changes.map(({ key, after }) => ({ key, after })));
  const rejectedAt = rejectedUpdates.get(dedupKey);
  if (rejectedAt && Date.now() - rejectedAt < 5 * 60 * 1000) {
    // 同上: 1回止めて Gemini に会話へ戻らせる。明示的な再指示は次で通る
    rejectedUpdates.delete(dedupKey);
    return {
      success: false,
      rejected_guard: true,
      error: '同じ内容の変更を直前にユーザーが取り消しています。同じ提案を繰り返さず、'
        + '**どこが問題だったか・どう変えればよいか**をユーザーに確認してください。',
    };
  }

  const confirmed = context.requestUpdateConfirm
    ? await context.requestUpdateConfirm(beforeSnapshot, changes.map(({ key, label, before, after }) => ({ key, label, before, after })))
    : true;

  if (!confirmed) {
    rejectedUpdates.set(dedupKey, Date.now());
    return {
      success: false, cancelled: true,
      message: 'ユーザーが更新を取り消しました。同じ内容で再提案せず、どこを直すべきか確認してください。',
    };
  }

  // 既存予約をベースに、変更項目だけ上書き。
  //
  // ★重要★ mapReservation は detail フィールドを **ルートにも spread** するため、
  // 取得した予約は target.detail.child_seat と target.child_seat の両方に値を持つ。
  // buildDetailPayload の pick = input[key] ?? input.detail[key] は **ルート優先** なので、
  // detail 側だけ書き換えても root に古い値が残っていれば古い値が保存される。
  // → detail 系フィールドはルートと detail の両方に上書きする必要がある。
  const merged: any = { ...(target as any) };
  merged.detail = { ...detail };

  let memoNote: string | null = null; // Gemini/ユーザー由来のメモ内容（監査エントリの本文に含めて追記する。置換はしない）

  // detail 内・ルート両方に書く必要があるキー
  const setDetail = (k: string, v: any) => {
    merged.detail[k] = v;
    merged[k] = v;
  };

  for (const { key, rawAfter } of changes) {
    switch (key) {
      case 'status':
        merged.status = rawAfter; // 'cancelled' などの文字列。resolveStatusId が ID に変換
        break;
      case 'pickup_datetime':
        merged.start_date = ensureDateTimeIso(String(rawAfter));
        break;
      case 'return_datetime':
        merged.end_date = ensureDateTimeIso(String(rawAfter));
        break;
      case 'phone':
        merged.customer_tel = sanitizePhone(rawAfter);
        break;
      case 'email':
        merged.customer_email = sanitizeEmail(rawAfter);
        break;
      case 'customer_name':
        merged.customer_name = String(rawAfter);
        break;
      case 'noc_waiver':
        setDetail('noc', rawAfter === true);
        break;
      case 'cdw':
        setDetail('cdw', rawAfter === true);
        break;
      case 'short_memo':
        // ショートメモは root 直下の短いラベル（一覧に表示）。上書き更新（追記の memo とは別物）
        merged.short_memo = String(rawAfter ?? '').trim();
        break;
      case 'memo':
        memoNote = String(rawAfter ?? '').trim();
        break;
      case 'comment': {
        // 追記（上書き禁止）。お客様の元の要望を消さない
        const cur = String((target as any).detail?.comment ?? (target as any).comment ?? '').trim();
        const appended = cur !== '' ? `${cur}\n${String(rawAfter)}` : String(rawAfter);
        setDetail('comment', appended);
        break;
      }
      case 'arrival_flight':
      case 'departure_flight':
      case 'pax':
      case 'pax_children':
      case 'child_seat':
      case 'booster_seat':
        setDetail(key, rawAfter);
        break;
      default:
        break;
    }
  }

  // 監査ログを memo タイムラインに追記（2026-07-09 修正）:
  //   既存メモ（これまでの経緯）は必ず維持し、末尾に
  //   「---予約処理/キャンセル処理 {対応者名}(エージェント) 日時---」のエントリを追記する。
  //   Gemini が memo 引数で渡してきた内容（例: 台風のため返金なし）は
  //   このエントリの本文に含める。既存メモの置換は絶対にしない。
  const isCancel = changes.some(
    (c) => c.key === 'status' && /cancelled/i.test(String(c.rawAfter)),
  );
  const auditLines = [
    isCancel ? 'エージェント経由で予約をキャンセル' : 'エージェント経由で予約を更新',
    ...changes
      .filter((c) => c.key !== 'memo')
      .map((c) => `${c.label}: ${c.before || '（未設定）'} → ${c.after}`),
    memoNote ? `メモ: ${memoNote}` : '',
  ];
  const auditEntry = buildAgentMemoEntry(isCancel ? 'cancel' : 'reservation', auditLines);
  merged.memo = appendAgentMemoEntry((target as any).memo, auditEntry);

  // 完全な detail として送る (2026-07-09 修正 / ref-save-reservation.md):
  //   監査メモの追記により detail には必ず memo が入るが、detail 未ロードの予約
  //   (API が detail:null を返すケース) では sparse な detail になり backend が
  //   500 を返していた（「編集・キャンセルでたまにエラー」の本命）。
  //   target はこの関数内で fetch した最新値なので、__completeDetail で
  //   buildDetailPayload に既定値補完させる = 編集パネル保存(handleSave)と同じ実績あるパターン。
  merged.__completeDetail = true;

  // status 文字列→ID 変換のために statusMapper キャッシュが populate されていることを保証
  await ensureStatusCachePopulated();

  const saved = await saveDispatchReservation(merged);
  // 2026-08-30 記録#19: 保存 API の失敗でも success を返していた（無言の失敗）。
  if (!saved) {
    return { success: false, error: '保存 API が失敗しました。変更は反映されていません（再試行するか、サイドパネルで手動対応してください）' };
  }

  // 保存後の実反映を検証する（loop-engineering: 200 が返ることと副作用が起きたことは別。
  // 記録#19: NOC/CDW が success 表示のまま実際には反映されていなかった）。
  // memo/comment は追記なので対象外。検証できなくても保存自体は成立している（advisory）。
  let unverified: string[] = [];
  try {
    const refetched = await fetchDispatchReservations({ id: Number(saved?.id ?? (target as any).id) });
    const after: any = (refetched as any[])?.find(
      (r) => Number(r.id ?? r.reservation_id) === Number(saved?.id ?? (target as any).id),
    );
    if (after) {
      const ad: any = after.detail || {};
      const refreshed: Record<string, any> = {
        status: after.status_code ?? after.status,
        pickup_datetime: after.start_date,
        return_datetime: after.end_date,
        arrival_flight: ad.arrival_flight,
        departure_flight: ad.departure_flight,
        customer_name: after.customer_name,
        phone: after.customer_tel,
        email: after.customer_email,
        pax: ad.pax,
        pax_children: ad.pax_children,
        child_seat: ad.child_seat,
        booster_seat: ad.booster_seat,
        noc_waiver: ad.noc,
        cdw: ad.cdw,
        short_memo: after.short_memo,
      };
      unverified = changes
        .filter((c) => c.key !== 'memo' && c.key !== 'comment')
        // 2026-10-13: カードの注意書き入りの after と比べて「不一致」と誤判定し、誤ったエスカレーション(#2)が出た
        .filter((c) => formatFieldValue(c.key, refreshed[c.key]) !== (c.plainAfter ?? c.after))
        .map((c) => c.label);
    }
  } catch { /* 検証の失敗で結果は変えない（保存は成立している） */ }

  // NOC/CDW はフラグのみ。金額（noc_price/cdw_price/合計）は 2026-05 の580件事象以来
  // AI からは変更できない（tier C）。加算が必要なことを黙って落とさず先回りで伝える
  const insuranceOn = changes.some((c) => (c.key === 'noc_waiver' || c.key === 'cdw') && c.after === 'あり');
  const notes: string[] = [];
  if (insuranceOn) {
    notes.push('※ NOC/CDW は**フラグのみ**変更しました（金額は未反映）。金額も直すなら apply_price_change で再計算→確認カードで反映できます。');
  }
  if (unverified.length > 0) {
    notes.push(`⚠ 保存後の確認で次の項目の反映が確認できませんでした: ${unverified.join(' / ')}。サイドパネルで実際の値を確認してください。`);
  }

  return {
    success: true,
    reservation_id: saved?.id ?? (target as any).id,
    contact_code: (target as any).contact_code,
    changes: changes.map(({ key, label, before, after }) => ({ key, label, before, after })),
    ...(unverified.length > 0 ? { unverified } : {}),
    ...(notes.length > 0 ? { note: notes.join('\n') } : {}),
  };
}

// ============================================================
// 時間変更・補償変更の下ごしらえ（2026-09-01 記録#25 ルール1〜3）
// 「オペレータが変更前に必ずやる確認」をツール化。**どちらも読み取り専用** —
// 料金の書き込みは 580件事象以来 tier C のまま（計算と提示だけを AI が担う）。
// ============================================================

/** 実日数（日割り・実日数×単価の分母。24h 単位の切り上げ・最低1日） */
function calcChargeDays(startIso: string, endIso: string): number {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return Math.max(1, Math.ceil(ms / 86_400_000));
}

/** 予約の car_type（コード or 名称）から vehicle_type_id を解決する */
async function resolveVehicleTypeId(target: any): Promise<number | null> {
  const carType = String(target.car_type ?? '').trim();
  if (!carType) return null;
  const { codeToId, byId } = await getVehicleTypeCodeMaps();
  if (codeToId.has(carType)) return codeToId.get(carType)!;
  for (const [id, t] of Array.from(byId.entries())) {
    const name = String((t as any)?.name ?? (t as any)?.vehicle_type_name ?? '').trim();
    if (name !== '' && name === carType) return id;
  }
  return null;
}

/**
 * **ガチャ枠の車両プール**（2026-09-08 前田さんから共有・記録#28 の在庫判断ルール）。
 *
 * 通常の予約は「その予約の車種タイプに属する車両」から選ぶが、ガチャ枠は
 * 車種を確約しないプランなので**複数の車両タイプにまたがって**選ぶ。
 *   - Gatcha（ガチャ）    : アルファード20系 / アルファード30系 / デリカD:5
 *   - GatchaH（高級ガチャ）: アルファード30系 / デリカD:5
 *   - GatchaL（ガチャ低・じゃらん「ファミリータイプ」2026-10-24）: Gatcha と同じ
 * ※ 在庫判断のルールは今後追加されていく。ここが唯一の定義（散らさない）。
 */
// ガチャ枠・無料アップグレードの定義は店舗ごと（2026-10-06〜 src/config/storePolicies.ts が唯一の定義）
const gatchaPools = () => getStorePolicy().gatchaPools;
/**
 * **満車時の無料アップグレード先**（2026-09-19 前田さん・記録#30）。
 * 「20系なので30系は無断でアップグレードして問題ない。同じアルファードの上位版だから。
 *   デリカだと別の車種で同系統ではないため NG」
 * → 同系統の上位版だけ。別車種への振替はお客様の合意が要るのでここには入れない。
 * 予約の car_type は**変えない**（当日20系に戻すこともある）。実車だけ上位版をアサインする。
 */
const upgradePools = () => getStorePolicy().upgradePools;

/** 予約の car_type（コード or 表示名）からガチャ枠のプール定義を引く */
function gatchaPoolFor(carType: string): { key: string; codes: string[] } | null {
  const t = String(carType || '').trim();
  if (t === '') return null;
  const pools = gatchaPools();
  if (pools[t]) return { key: t, codes: pools[t] };
  if (/^高級ガチャ/.test(t) && pools.GatchaH) return { key: 'GatchaH', codes: pools.GatchaH };
  if (/^ガチャ低/.test(t) && pools.GatchaL) return { key: 'GatchaL', codes: pools.GatchaL };
  if (/^ガチャ/.test(t) && pools.Gatcha) return { key: 'Gatcha', codes: pools.Gatcha };
  return null;
}

/**
 * その予約にアサインしてよい車両タイプID の集合。
 *   通常     : その車種 + 無料アップグレード先（UPGRADE_POOLS）
 *   ガチャ枠 : プール全体（GATCHA_POOLS）
 * upgrades = 「アップグレード扱い」のタイプID（表示で区別する。car_type は変えない）
 */
async function allowedVehicleTypeIds(target: any): Promise<{
  ids: Set<number>; pool: { key: string; codes: string[] } | null; upgrades: Set<number>;
}> {
  const { codeToId, idToCode } = await getVehicleTypeCodeMaps();
  const pool = gatchaPoolFor(String(target.car_type ?? ''));
  if (pool) {
    const ids = new Set<number>();
    pool.codes.forEach((c) => { const id = codeToId.get(c); if (id) ids.add(id); });
    return { ids, pool, upgrades: new Set<number>() };
  }
  const own = await resolveVehicleTypeId(target);
  const ids = new Set<number>(own ? [own] : []);
  const upgrades = new Set<number>();
  const ownCode = own ? idToCode.get(own) : undefined;
  // 無料アップグレードも那覇の運用（宮古には30系がない）
  (ownCode ? upgradePools()[ownCode] ?? [] : []).forEach((c) => {
    const id = codeToId.get(c);
    if (id) { ids.add(id); upgrades.add(id); }
  });
  return { ids, pool: null, upgrades };
}

/** 有効な予約として車両を塞ぐステータス（cancelled / nonreserved は塞がない） */
const CONFLICT_EXCLUDED_STATUSES = new Set(['cancelled', 'nonreserved']);

/** 指定車両・指定期間に重なる有効予約を返す（check_schedule_conflict と assign_vehicle の共通部品） */
async function findVehicleConflicts(
  vehicleId: number, startIso: string, endIso: string, excludeReservationId: number,
): Promise<any[]> {
  const overlapping = await fetchDispatchReservations({
    startDate: startIso.slice(0, 10),
    endDate: endIso.slice(0, 10),
  });
  return (overlapping || [])
    .filter((r: any) => Number(r.id) !== Number(excludeReservationId))
    .filter((r: any) => Number(r.vehicle_id) === Number(vehicleId))
    .filter((r: any) => !CONFLICT_EXCLUDED_STATUSES.has(String(r.status_code ?? r.status ?? '')))
    // 日単位のAPI窓の中から、時刻まで見て実際に重なるものだけ
    .filter((r: any) => new Date(String(r.start_date)).getTime() < new Date(endIso).getTime()
      && new Date(String(r.end_date)).getTime() > new Date(startIso).getTime())
    .map((r: any) => ({
      contact_code: r.contact_code, customer_name: r.customer_name,
      start_date: r.start_date, end_date: r.end_date,
      status: formatStatus(r.status_code ?? r.status),
    }));
}

async function runCheckScheduleConflict(args: Record<string, any>): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };

  const list = await fetchDispatchReservations({ contactCode });
  const target: any = (list || []).find((r: any) => String(r.contact_code || '') === contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };

  const newStart = args.new_pickup_datetime
    ? ensureDateTimeIso(String(args.new_pickup_datetime)) : String(target.start_date);
  const newEnd = args.new_return_datetime
    ? ensureDateTimeIso(String(args.new_return_datetime)) : String(target.end_date);
  if (new Date(newStart).getTime() >= new Date(newEnd).getTime()) {
    return { success: false, error: '変更後の返却日時が貸出日時より前（または同時刻）になっています' };
  }

  // 変更後の期間に重なる予約を取得し、同じ車両のものだけを衝突として抽出
  const vid = Number(target.vehicle_id) || null;
  const conflicts: any[] = vid
    ? await findVehicleConflicts(vid, newStart, newEnd, Number(target.id))
    : [];

  // 同タイプの空き（振替の可能性）。calc API 依存なので取れなくても本体の判定は返す
  let typeAvailability: any = null;
  try {
    const typeId = await resolveVehicleTypeId(target);
    if (typeId) {
      const { idToCode } = await getVehicleTypeCodeMaps();
      const code = idToCode.get(typeId);
      const calc = await fetchBookingCalcSafe({
        start: toCalcDateTimeFromAny(newStart), end: toCalcDateTimeFromAny(newEnd),
      });
      const v = calc.vehicles.find((x) => x.code === code);
      if (v) typeAvailability = { vehicle_type: v.name_ja || code, ...(v as any).availability };
    }
  } catch { /* 空き情報が取れないだけ。衝突判定は上で確定済み */ }

  const note = !vid
    ? '⚠ この予約はまだ車両がアサインされていません（車両単位の衝突はありません）。同タイプの空きを確認してください。'
    : conflicts.length === 0
      ? '✅ 変更後の期間で、アサイン済み車両に他の予約の重なりはありません。'
      : '⚠ アサイン済み車両に予約の重なりがあります。同タイプに空きがあれば車両の振替で対応できますが、'
        + '**車両アサインの変更は AI からは行いません** — 予約一覧の車両コード列から実施してください。';

  // vehicle_code は生の予約データには無く、車両IDマップで解決する（search_reservations と同じ）。
  // 2026-09-03: これが無くて結果に vehicle_code:null が出ていた（記録#26）
  let vehicleCodeLabel: string | null = target.vehicle_code ?? null;
  if (!vehicleCodeLabel && vid) {
    try {
      const map = await getVehicleCodeMap();
      vehicleCodeLabel = map[String(vid)] ?? null;
    } catch { /* 表示ラベルだけなので取れなくてよい */ }
  }
  return {
    success: true,
    contact_code: contactCode,
    vehicle_code: vehicleCodeLabel,
    period: { new_pickup: newStart, new_return: newEnd },
    conflicts,
    conflict_count: conflicts.length,
    type_availability: typeAvailability,
    checked_statuses: 'cancelled / nonreserved（予約に至らなかった）は衝突として数えていません',
    note,
  };
}

interface PricePlan {
  target: any; channel: string; datesChanged: boolean;
  curStart: string; curEnd: string; newStart: string; newEnd: string;
  wantNoc: boolean; wantCdw: boolean; curNoc: boolean; curCdw: boolean;
  cur: PriceParts; curDays: number; newDays: number; proposal: Proposal;
}

/** 料金計算 API の結果を「その期間・その車種」のスナップショットにする（補償は両方付けて単価を取る） */
// 'holiday_pack' = 新しい期間に連休パックがかかる（補償の日額が insurance ÷ 日数で出せないので自動計算しない）
async function calcSnapshotFor(target: any, startIso: string, endIso: string): Promise<CalcSnapshot | null | 'holiday_pack'> {
  // 店舗の取り違え（宮古で那覇の料金）は bookingCalcService が応答の store を照合して止める（2026-10-03）。
  // 取れなければ null → 既存の日額か日額の指定で計算する（needs_decision で人に聞く）
  try {
    const typeId = await resolveVehicleTypeId(target);
    if (!typeId) return null;
    const d: any = target.detail || {};
    const c: any = await runCalculatePrice({
      vehicle_type_id: typeId, pickup_datetime: startIso, return_datetime: endIso,
      noc_waiver: true, cdw: true,
      child_seat: Number(d.child_seat) || 0, booster_seat: Number(d.booster_seat) || 0,
    });
    if (c?.holiday_pack) return 'holiday_pack';
    const pv = c?.per_vehicle || {};
    return {
      base_subtotal: Number(pv.base_subtotal) || 0,
      options: Number(pv.options_price) || 0,
      fees: Number(pv.fees) || 0,
      noc: Number(pv.noc_price) || 0,
      cdw: Number(pv.cdw_price) || 0,
      days: Number(c?.period?.days) || 0,
    };
  } catch { return null; }
}

async function buildPricePlan(args: Record<string, any>): Promise<PricePlan | { error: string }> {
  const contactCode = String(args.contact_code ?? '').trim();
  if (!contactCode) return { error: 'contact_code（予約番号）を指定してください' };
  const list = await fetchDispatchReservations({ contactCode });
  const target: any = (list || []).find((r: any) => String(r.contact_code || '') === contactCode);
  if (!target) return { error: `予約 ${contactCode} が見つかりませんでした` };
  const detail: any = target.detail || {};
  // 「基本料金に含める」プラン（CDW 込み / 宮古の NOC 込み / 下地島の送迎費込み）は base_price に
  // その額を含む。下の計算は別建てで足すため二重計上になる。自動再計算は未対応なので止めて人に回す
  // （CDW: 2026-10-05 / NOC・送迎費: 2026-10-21）
  if (hasBundledPricing(target.json_data)) {
    const parts = [
      isCdwBundled(target.json_data) ? 'CDW' : null,
      isNocBundled(target.json_data) ? 'NOC' : null,
      isLocationBundled(target.json_data) ? '下地島の送迎費' : null,
      isCruisePriced(target.json_data) ? 'クルーズ船料金（平良港の送迎料）' : null,
    ].filter(Boolean).join('・');
    return {
      error: `予約 ${contactCode} は ${parts} を基本料金に含むプランです。`
        + `料金の自動再計算にはまだ対応していません。予約編集パネルで基本料金（${parts} 込み）と合計を直してください。`,
    };
  }

  // 連休パックの予約も同様に、補償の一部がパック価格に入っているため自動再計算できない（2026-10-09）
  if (isHolidayPackReservation(target.json_data)) {
    return {
      error: `予約 ${contactCode} は連休パック（CDW/NOC込みの定額）の予約です。`
        + '料金の自動再計算には対応していません。予約編集パネルで基本料金（パック価格＋超過分）と合計を直してください。',
    };
  }

  const curStart = ensureDateTimeIso(String(target.start_date));
  const curEnd = ensureDateTimeIso(String(target.end_date));
  const newStart = args.new_pickup_datetime ? ensureDateTimeIso(String(args.new_pickup_datetime)) : curStart;
  const newEnd = args.new_return_datetime ? ensureDateTimeIso(String(args.new_return_datetime)) : curEnd;
  if (new Date(newStart).getTime() >= new Date(newEnd).getTime()) {
    return { error: '変更後の返却日時が貸出日時より前（または同時刻）になっています' };
  }
  const datesChanged = new Date(newStart).getTime() !== new Date(curStart).getTime()
    || new Date(newEnd).getTime() !== new Date(curEnd).getTime();

  const flag = (v: any) => Number(v ?? 0) === 1 || v === true;
  const curNoc = flag(detail.noc);
  const curCdw = flag(detail.cdw);
  const wantNoc = typeof args.noc === 'boolean' ? args.noc : curNoc;
  const wantCdw = typeof args.cdw === 'boolean' ? args.cdw : curCdw;

  const cur = currentParts(detail);
  const curDays = calcChargeDays(curStart, curEnd);
  const newDays = calcChargeDays(newStart, newEnd);
  const channel = classifyChannel(target);
  const snap = await calcSnapshotFor(target, newStart, newEnd);
  if (snap === 'holiday_pack') {
    return {
      error: `変更後の期間（${newStart} 〜 ${newEnd}）は連休パックの対象期間にかかります。`
        + 'パック料金への切り替えを含む料金変更は自動再計算に対応していません。スタッフが予約編集パネルで直してください。',
    };
  }
  const calcNew = snap;
  const override = (Number(args.noc_per_day) > 0 || Number(args.cdw_per_day) > 0)
    ? { noc: Number(args.noc_per_day) || 0, cdw: Number(args.cdw_per_day) || 0, source: 'ユーザー指定の日額' }
    : null;

  let proposal: Proposal;
  if (channel === '自社LP' && calcNew && !override) {
    proposal = proposeLp({ cur, calcNew, datesChanged, wantNoc, wantCdw });
  } else {
    const unit = override ?? (calcNew && calcNew.days > 0
      ? { noc: Math.round(calcNew.noc / calcNew.days), cdw: Math.round(calcNew.cdw / calcNew.days),
          source: '料金計算 API の車種単価（価格帯）' }
      : null);
    proposal = proposeOta({ cur, curDays, newDays, wantNoc, wantCdw, unit });
    if (channel !== 'OTA') {
      proposal.lines.unshift(channel === '自社LP'
        ? '⚠ 自社LP だが料金計算 API で車種を解決できなかったため日割りで計算'
        : '⚠ チャネルが判定できないため OTA（日割り）で計算。自社LP の可能性があればユーザーに確認');
    }
  }
  return { target, channel, datesChanged, curStart, curEnd, newStart, newEnd,
    wantNoc, wantCdw, curNoc, curCdw, cur, curDays, newDays, proposal };
}

async function runPreviewPriceChange(args: Record<string, any>): Promise<any> {
  const plan = await buildPricePlan(args);
  if ('error' in plan) return { success: false, error: plan.error };
  const { channel, cur, proposal, curDays, newDays } = plan;
  return {
    success: true,
    channel,
    current: { ...cur, days: curDays },
    proposal: { ...proposal.parts, days: newDays },
    diff: diffParts(cur, proposal.parts),
    basis: proposal.lines,
    ...(proposal.needs_decision ? { needs_decision: proposal.needs_decision } : {}),
    note: '反映するときは apply_price_change（確認カードでユーザーが承認してから保存）。',
  };
}

export async function runApplyPriceChange(
  args: Record<string, any>,
  context: { requestUpdateConfirm?: (before: Record<string, any>, changes: Array<{ key: string; label: string; before: string; after: string }>) => Promise<boolean> },
): Promise<any> {
  const plan = await buildPricePlan(args);
  if ('error' in plan) return { success: false, error: plan.error };
  const { target, cur, proposal } = plan;
  if (proposal.needs_decision) {
    return { success: false, needs_decision: proposal.needs_decision, basis: proposal.lines,
      error: '単価が決まらないため書き込んでいません。' + proposal.needs_decision };
  }
  const yesNo = (b: boolean) => (b ? 'あり' : 'なし');
  const changes: Array<{ key: string; label: string; before: string; after: string }> = [];
  if (plan.datesChanged) {
    if (plan.newStart !== plan.curStart) changes.push({ key: 'pickup_datetime', label: '貸出日時', before: formatDateTime(plan.curStart), after: formatDateTime(plan.newStart) });
    if (plan.newEnd !== plan.curEnd) changes.push({ key: 'return_datetime', label: '返却日時', before: formatDateTime(plan.curEnd), after: formatDateTime(plan.newEnd) });
  }
  if (plan.wantNoc !== plan.curNoc) changes.push({ key: 'noc_waiver', label: 'NOC', before: yesNo(plan.curNoc), after: yesNo(plan.wantNoc) });
  if (plan.wantCdw !== plan.curCdw) changes.push({ key: 'cdw', label: 'CDW', before: yesNo(plan.curCdw), after: yesNo(plan.wantCdw) });
  const money = diffParts(cur, proposal.parts);
  changes.push(...money.map((m) => ({ key: `price_${m.key}`, label: m.label, before: m.before, after: m.after })));
  if (changes.length === 0) return { success: false, error: '変更がありません（日時・補償・金額とも現状と同じ）' };
  changes.push({ key: 'basis', label: '計算根拠', before: '', after: proposal.lines.join(' / ') });

  const beforeSnapshot = {
    reservation_id: target.id, contact_code: target.contact_code, customer_name: target.customer_name,
    car_type: target.car_type, pickup_datetime: formatDateTime(target.start_date),
    return_datetime: formatDateTime(target.end_date), status: formatStatus(target.status_code ?? target.status),
    channel: plan.channel,
  };
  const confirmed = context.requestUpdateConfirm ? await context.requestUpdateConfirm(beforeSnapshot, changes) : true;
  if (!confirmed) {
    return { success: false, cancelled: true, message: 'ユーザーが料金の反映を取り消しました。どこが違うか（日額・日数・割引の扱い等）を確認してください。' };
  }

  // 保存: 既存予約をベースに変更項目だけ上書き（detail 系はルートと detail の両方に書く。runUpdateReservation と同じ理由）
  const merged: any = { ...target };
  merged.detail = { ...(target.detail || {}) };
  const setDetail = (k: string, v: any) => { merged.detail[k] = v; merged[k] = v; };
  if (plan.datesChanged) { merged.start_date = plan.newStart; merged.end_date = plan.newEnd; }
  setDetail('noc', plan.wantNoc);
  setDetail('cdw', plan.wantCdw);
  const p = proposal.parts;
  setDetail('base_price', p.base);
  setDetail('coupon', p.coupon > 0 ? p.coupon : '');
  setDetail('option_price', p.option);
  setDetail('noc_price', p.noc);
  setDetail('cdw_price', p.cdw);
  setDetail('sum_price', p.sum);
  if (plan.datesChanged) setDetail('days', plan.newDays);
  const auditLines = [
    'エージェント経由で料金を再計算して反映',
    ...changes.filter((c) => c.key !== 'basis').map((c) => `${c.label}: ${c.before || '（未設定）'} → ${c.after}`),
    `計算根拠: ${proposal.lines.join(' / ')}`,
    String(args.memo ?? '').trim() ? `メモ: ${String(args.memo).trim()}` : '',
  ];
  merged.memo = appendAgentMemoEntry(target.memo, buildAgentMemoEntry('reservation', auditLines));
  merged.__completeDetail = true;
  await ensureStatusCachePopulated();
  const saved = await saveDispatchReservation(merged);
  if (!saved) return { success: false, error: '保存 API が失敗しました。変更は反映されていません（サイドパネルで確認してください）' };

  // 保存後の実反映を検証（200 ≠ 副作用。記録#19）
  const unverified: string[] = [];
  try {
    const refetched = await fetchDispatchReservations({ id: Number(saved?.id ?? target.id) });
    const after: any = (refetched as any[])?.find((r) => Number(r.id ?? r.reservation_id) === Number(saved?.id ?? target.id));
    if (after) {
      const got = currentParts(after.detail || {});
      (['base', 'option', 'noc', 'cdw', 'sum'] as const).forEach((k) => {
        if (got[k] !== p[k]) unverified.push(`${k}: 期待 ${p[k]} / 実際 ${got[k]}`);
      });
      if (plan.datesChanged && new Date(String(after.start_date)).getTime() !== new Date(plan.newStart).getTime()) unverified.push('貸出日時');
      if (plan.datesChanged && new Date(String(after.end_date)).getTime() !== new Date(plan.newEnd).getTime()) unverified.push('返却日時');
    } else {
      unverified.push('再取得できず');
    }
  } catch { unverified.push('再取得できず'); }

  return {
    success: true,
    contact_code: target.contact_code,
    changes: changes.filter((c) => c.key !== 'basis'),
    basis: proposal.lines,
    sum: { before: cur.sum, after: p.sum, diff: p.sum - cur.sum },
    ...(unverified.length ? { unverified,
      note: `⚠ 保存後の確認で一致しない項目があります: ${unverified.join(' / ')}。サイドパネルで実際の値を確認してください。` } : {}),
    invoice_note: '請求書（Square）は自動では更新されません。発行済みなら 未払い=取消→再作成（void_square_invoice → create_square_invoice）/ 支払い済み=差額 '
      + `¥${Math.max(0, p.sum - cur.sum).toLocaleString()} のみ請求（追加ルール#9）。`,
  };
}

/**
 * 予約期間に空いている号車の一覧（2026-09-08 記録#28 の在庫判断ルール）。
 *
 * 「基本はその予約の車種タイプに属する車両から選ぶ。ただしガチャ枠は複数タイプに
 * またがる」を実装したもの。assign_vehicle に渡せる候補を出すのが役目で、
 * **読み取り専用**（アサイン自体は確認カード付きの assign_vehicle）。
 */
async function runListAvailableVehicles(args: Record<string, any>): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };

  const list = await fetchDispatchReservations({ contactCode });
  const target: any = (list || []).find((r: any) => String(r.contact_code || '') === contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };

  const start = args.pickup_datetime ? ensureDateTimeIso(String(args.pickup_datetime)) : String(target.start_date);
  const end = args.return_datetime ? ensureDateTimeIso(String(args.return_datetime)) : String(target.end_date);
  if (new Date(start).getTime() >= new Date(end).getTime()) {
    return { success: false, error: '返却日時が貸出日時より前（または同時刻）になっています' };
  }

  const { ids: allowedIds, pool, upgrades } = await allowedVehicleTypeIds(target);
  if (allowedIds.size === 0) {
    return { success: false, error: `予約の車種「${target.car_type}」から対象の車両タイプを解決できませんでした` };
  }

  const [vehicles, overlapping] = await Promise.all([
    fetchDispatchVehicles({}),
    fetchDispatchReservations({ startDate: start.slice(0, 10), endDate: end.slice(0, 10) }),
  ]);

  // 期間に重なる有効予約が押さえている車両ID
  const busy = new Set<number>();
  (overlapping || []).forEach((r: any) => {
    if (Number(r.id) === Number(target.id)) return;                     // 自分自身は除く
    if (CONFLICT_EXCLUDED_STATUSES.has(String(r.status_code ?? r.status ?? ''))) return;
    if (new Date(String(r.start_date)).getTime() < new Date(end).getTime()
      && new Date(String(r.end_date)).getTime() > new Date(start).getTime()) {
      const vid = Number(r.vehicle_id);
      if (Number.isFinite(vid) && vid > 0) busy.add(vid);
    }
  });

  const { idToCode, byId } = await getVehicleTypeCodeMaps();
  const typeLabel = (tid: number) =>
    String((byId.get(tid) as any)?.name ?? idToCode.get(tid) ?? tid);

  const available: Record<string, string[]> = {};
  let candidateCount = 0;
  (vehicles as any[]).forEach((v: any) => {
    const tid = Number(v.vehicle_type_id);
    if (!allowedIds.has(tid)) return;
    if (v.out_of_use === true) return;                                   // 使用不可
    if (!isVehicleUsableFor(v, String(start).slice(0, 10), String(end).slice(0, 10))) return;   // 有効 OFF・期間外（予約の期間で判断）
    candidateCount++;
    if (busy.has(Number(v.id))) return;
    const label = typeLabel(tid) + (upgrades.has(tid) ? '（無料アップグレード先）' : '');
    (available[label] = available[label] || []).push(String(v.vehicle_code ?? ''));
  });
  Object.values(available).forEach((arr) => arr.sort());
  const total = Object.values(available).reduce((n, arr) => n + arr.length, 0);

  return {
    success: true,
    contact_code: contactCode,
    period: { pickup: start, return: end },
    basis: pool
      ? `${target.car_type} 枠のため複数タイプから選べる（${pool.codes.join(' / ')}）`
      : `車種「${target.car_type}」に属する車両から選ぶ`
        + (upgrades.size > 0
          ? `。満車時は同系統の上位版（${Array.from(upgrades).map(typeLabel).join(' / ')}）へ無料アップグレード可（予約の車種は変えない・お客様には元の車種で案内）`
          : ''),
    current_vehicle: target.vehicle_code ?? null,
    available_count: total,
    available_by_type: available,
    checked_vehicles: candidateCount,
    note: total === 0
      ? 'この期間に空いている車両はありません（使用不可・非稼働は除外済み）。期間の変更か、担当者への相談が必要です。'
      : '車両コードをそのまま assign_vehicle に渡せます。アサインは確認カードで承認してから実行されます。',
  };
}

/**
 * 車両アサインの変更（2026-09-01 前田さん指示で tier C → B へ昇格）。
 * 安全策（車両アサインを AI に開放するための3枚の防壁）:
 *   1. **同タイプ内のみ** — タイプが変わると車種・料金が変わる（580件事象の射程）ため拒否
 *   2. **変更先の衝突チェックを内蔵** — 重なりがあれば確認カードすら出さない
 *   3. 保存は予約一覧の実績経路と同じ全量コピー {...fresh, vehicle_id} + 監査メモ + 事後検証
 * car_type は変更しない（一覧の executeAssignment と同じ判断）。
 */
async function runAssignVehicle(
  args: Record<string, any>,
  context: { requestUpdateConfirm?: (before: Record<string, any>, changes: Array<{ key: string; label: string; before: string; after: string }>) => Promise<boolean> },
): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  const vehicleCode = String(args.vehicle_code ?? '').trim();
  if (!contactCode || !vehicleCode) {
    return { success: false, error: 'contact_code と vehicle_code（変更先の車両コード）を指定してください' };
  }

  const list = await fetchDispatchReservations({ contactCode });
  const target: any = (list || []).find((r: any) => String(r.contact_code || '') === contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };

  const vehicles = await fetchDispatchVehicles({});
  const vehicle: any = (vehicles as any[]).find(
    (v: any) => String(v.vehicle_code ?? '').toLowerCase() === vehicleCode.toLowerCase(),
  );
  if (!vehicle) {
    // 2026-09-03 記録#28: AI が**車両タイプコード**（T-AV-H 等）を渡してきた。
    // 「見つかりません」だけでは直しようがないので、何を渡すべきかまで返す
    const { codeToId } = await getVehicleTypeCodeMaps();
    if (codeToId.has(vehicleCode)) {
      const sameType = (vehicles as any[])
        .filter((v: any) => Number(v.vehicle_type_id) === Number(codeToId.get(vehicleCode)))
        .map((v: any) => String(v.vehicle_code ?? ''))
        .filter(Boolean).slice(0, 10);
      return {
        success: false,
        error: `${vehicleCode} は**車両タイプ**のコードです。assign_vehicle には号車の車両コード`
          + `（例: ${sameType[0] || 'T-XX-X-1234'}）を渡してください`,
        candidates: sameType,
      };
    }
    return { success: false, error: `車両コード ${vehicleCode} が見つかりませんでした（コードは予約一覧の車両コード列と同じ表記）` };
  }
  if (Number(vehicle.id) === Number(target.vehicle_id)) {
    return { success: true, no_change: true, message: `予約 ${contactCode} は既に ${vehicleCode} がアサインされています（変更なし）` };
  }

  // 防壁1: その予約にアサインしてよい車両タイプか
  // （通常は同タイプのみ。**ガチャ枠は複数タイプにまたがる**のでプールで判定・2026-09-08）
  const { ids: allowedIds, pool, upgrades } = await allowedVehicleTypeIds(target);
  if (allowedIds.size > 0 && !allowedIds.has(Number(vehicle.vehicle_type_id))) {
    return {
      success: false,
      error: pool
        ? `車両 ${vehicleCode} は「${target.car_type}」枠で選べる車両タイプに含まれていません`
          + `（選べるのは ${pool.codes.join(' / ')}）`
        : `車両 ${vehicleCode} は別の車両タイプです（予約は「${target.car_type}」）。`
          + '同系統の上位版への無料アップグレード以外、タイプをまたぐ変更は車種・料金が変わるため AI からは行いません。'
          + '予約一覧の車両コード列から実施してください',
    };
  }
  const isUpgrade = upgrades.has(Number(vehicle.vehicle_type_id));

  // 防壁2: 変更先車両の衝突チェック
  const conflicts = await findVehicleConflicts(
    Number(vehicle.id), String(target.start_date), String(target.end_date), Number(target.id),
  );
  if (conflicts.length > 0) {
    return {
      success: false,
      error: `変更先の ${vehicleCode} は予約期間に他の予約と重なっています。別の車両を選んでください`,
      conflicts,
    };
  }

  const beforeCode = String(target.vehicle_code ?? '（未アサイン）');
  const confirmed = context.requestUpdateConfirm
    ? await context.requestUpdateConfirm(
        {
          contact_code: contactCode, customer_name: target.customer_name,
          car_type: target.car_type,
          pickup_datetime: formatDateTime(target.start_date),
          return_datetime: formatDateTime(target.end_date),
        },
        [{ key: 'vehicle', label: '車両', before: beforeCode,
           after: `${vehicleCode}（重なりなし確認済み）${pool ? `／${target.car_type} 枠` : ''}`
             + (isUpgrade ? `／無料アップグレード（予約の車種「${target.car_type}」は変えない）` : '') }],
      )
    : true;
  if (!confirmed) return { success: false, cancelled: true, message: 'ユーザーが車両変更を取りやめました' };

  // 防壁3: 実績経路の保存（全量コピー + 監査メモ + 事後検証）
  const fresh = (await fetchDispatchReservations({ contactCode }))
    ?.find((r: any) => String(r.contact_code || '') === contactCode);
  if (!fresh) return { success: false, error: '保存前の再取得に失敗しました' };
  const auditEntry = buildAgentMemoEntry('reservation', [
    `車両変更: ${beforeCode} → ${vehicleCode}` + (isUpgrade ? '（無料アップグレード・車種は据置）' : ''),
  ]);
  const merged: any = {
    ...fresh,
    vehicle_id: Number(vehicle.id),
    detail: { ...(fresh.detail || {}) },
  };
  merged.memo = appendAgentMemoEntry(fresh.memo, auditEntry);
  merged.detail.memo = merged.memo;
  merged.__completeDetail = true;
  const saved = await saveDispatchReservation(merged);
  if (!saved) return { success: false, error: '保存 API が失敗しました。変更は反映されていません' };

  // 事後検証（記録#19 と同じ思想: 200 と副作用は別）
  let verified: boolean | null = null;
  try {
    const after = (await fetchDispatchReservations({ contactCode }))
      ?.find((r: any) => String(r.contact_code || '') === contactCode);
    if (after) verified = Number(after.vehicle_id) === Number(vehicle.id);
  } catch { /* 検証不能でも保存は成立 */ }

  return {
    success: true,
    contact_code: contactCode,
    vehicle: { before: beforeCode, after: vehicleCode },
    ...(verified === false
      ? { warning: '⚠ 保存後の確認で車両の反映が確認できませんでした。予約一覧で実際の値を確認してください' }
      : {}),
    message: `予約 ${contactCode} の車両を ${beforeCode} → ${vehicleCode} に変更しました（メモに監査を記録）`
      + (verified === true ? '（反映確認済み）' : ''),
  };
}

// -------- Notion 連携 tools（2026-07-09。読み取り専用、PHP proxy 経由） --------
// トークンは sys/.env にサーバー側でのみ保持。未設定時は PHP が明確なエラーを返し、
// Gemini がその旨をユーザーに伝える（フロントは素通しでよい）。

export async function runGetVehicleNotion(args: Record<string, any>): Promise<any> {
  const query = String(args.query || '').trim();
  if (!query) return { success: false, error: 'query（車両コード等）を指定してください' };
  return postAgentAction('notion_vehicle', { query });
}

export async function runGetAccidentNotion(args: Record<string, any>): Promise<any> {
  const query = String(args.query || '').trim();
  if (!query) return { success: false, error: 'query（車両番号・顧客名等）を指定してください' };
  return postAgentAction('notion_accident', { query });
}

/** 事故連絡簿への追記（新規行の作成のみ）。書き込み前に必ず確認カードで承認を得る */
export async function runCreateAccidentNotion(
  args: Record<string, any>,
  context: {
    requestUpdateConfirm?: (
      before: Record<string, any>,
      changes: Array<{ key: string; label: string; before: string; after: string }>,
    ) => Promise<boolean>;
  },
): Promise<any> {
  const fields = Array.isArray(args.fields)
    ? args.fields
        .map((f: any) => ({ name: String(f?.name || '').trim(), value: String(f?.value ?? '').trim() }))
        .filter((f: any) => f.name && f.value)
    : [];
  if (fields.length === 0) {
    return { success: false, error: 'fields（プロパティ名と値の配列）を指定してください' };
  }
  // 書き込みは必ずユーザー承認を挟む（予約作成と同じ流儀）
  if (context.requestUpdateConfirm) {
    const confirmed = await context.requestUpdateConfirm(
      { _kind: 'notion_create', _target: 'Notion 事故連絡簿（新規行を追加）' },
      fields.map((f: any) => ({ key: f.name, label: f.name, before: '（新規）', after: f.value })),
    );
    if (!confirmed) {
      return { success: false, cancelled: true, message: 'ユーザーが事故連絡簿への追記をキャンセルしました' };
    }
  }
  return postAgentAction('notion_accident_create', { fields });
}

export async function runSearchNotion(args: Record<string, any>): Promise<any> {
  const query = String(args.query || '').trim();
  if (!query) return { success: false, error: 'query を指定してください' };
  return postAgentAction('notion_search', { query, page_size: 5 });
}

export async function runGetNotionPage(args: Record<string, any>): Promise<any> {
  const pageId = String(args.page_id || '').trim();
  if (!pageId) return { success: false, error: 'page_id を指定してください' };
  return postAgentAction('notion_page', { page_id: pageId });
}

// -------- Dispatcher --------

export async function dispatchTool(
  name: string,
  args: Record<string, any>,
  context: {
    requestConfirm: (args: Record<string, any>) => Promise<boolean>;
    requestUpdateConfirm?: (
      before: Record<string, any>,
      changes: Array<{ key: string; label: string; before: string; after: string }>,
    ) => Promise<boolean>;
    requestWizard?: (fields: WizardField[]) => Promise<Record<string, any>>;
    /** record_conversation 用: 画面に出ているやりとりの全文 */
    getTranscript?: () => string;
    sessionId?: string;
    /** propose_plan / advance_plan 用（手順カード。useAgent が持つ） */
    requestPlan?: (args: Record<string, any>) => Promise<any>;
    advancePlan?: (args: Record<string, any>) => any;
    /** このターンのオペレータの発言（請求書の取り消し・作成の指示を承認として扱う。2026-10-31） */
    lastUserText?: string;
  },
): Promise<any> {
  // モデルがツール名の前後に空白を付けることがある（2026-10-13 セッション2278「 calculate_price」→ unknown tool）
  name = String(name ?? '').trim();
  switch (name) {
    case 'propose_plan':
      return context.requestPlan ? context.requestPlan(args) : { success: false, error: 'この画面では手順カードを出せません' };
    case 'advance_plan':
      return context.advancePlan ? context.advancePlan(args) : { success: false, error: 'この画面では手順カードを出せません' };
    case 'search_reservations':
      return runSearchReservations(args);
    case 'search_vehicle_types':
      return runSearchVehicleTypes(args);
    case 'list_all_vehicle_types':
      return runListAllVehicleTypes(args);
    case 'calculate_price':
      return runCalculatePrice(args);
    case 'search_customer':
      return runSearchCustomer(args);
    case 'create_reservation':
      return runCreateReservation(args, context);
    case 'update_reservation':
      return runUpdateReservation(args, context);
    case 'cancel_reservation_full':
      return runCancelReservationFull(args, context);
    case 'get_square_payment_status':
      return runGetSquarePaymentStatus(args);
    case 'void_square_invoice':
      return runVoidSquareInvoice(args, context);
    case 'create_square_invoice':
      return runCreateSquareInvoice(args, context);
    case 'list_mail_templates':
      return runListMailTemplates(args);
    case 'archive_mail':
      return runArchiveMail(args);
    case 'list_reservation_mails':
      return runListReservationMails(args);
    case 'get_mail_content':
      return runGetMailContent(args);
    case 'set_mail_followup':
      return runSetMailFollowup(args);
    case 'check_mail_attachments':
      return runCheckMailAttachments(args);
    case 'check_schedule_conflict':
      return runCheckScheduleConflict(args);
    case 'preview_price_change':
      return runPreviewPriceChange(args);
    case 'apply_price_change':
      return runApplyPriceChange(args, context);
    case 'report_contradiction': {
      const title = String(args.title ?? '').trim();
      if (!title) return { success: false, error: 'title（何と何が食い違っているか）を指定してください' };
      const r = await sendEscalation({
        kind: 'ai_reported', title,
        evidence: (Array.isArray(args.evidence) ? args.evidence : [String(args.evidence ?? '')]).map(String).filter(Boolean),
        contactCode: args.contact_code ? String(args.contact_code) : null,
      }, { sessionId: context.sessionId || '', transcript: context.getTranscript ? context.getTranscript() : '' });
      return r.ok
        ? { success: true, escalation_id: r.id, deduped: r.deduped ?? false, mail_to: r.mailTo,
            message: r.deduped ? '同じ内容は送信済みです' : `スタッフ全員にメールしました（#${r.id}）。チャットでも矛盾の内容を伝えて確認してください。` }
        : { success: false, error: r.error || 'エスカレーションに失敗しました' };
    }
    case 'assign_vehicle':
      return runAssignVehicle(args, context);
    case 'list_available_vehicles':
      return runListAvailableVehicles(args);
    case 'set_reservation_checks':
      return runSetReservationChecks(args, context);
    case 'set_license_checked':   // 旧名（2026-08-27 午前まで）の互換
      return runSetReservationChecks({ contact_code: args.contact_code, license: args.checked !== false }, context);
    case 'open_reservation_session':
      return runOpenReservationSession(args);
    case 'get_reservation_timeline':
      return runGetReservationTimeline(args);
    case 'remember_rule':
      return runRememberRule(args, context);
    case 'record_conversation':
      return runRecordConversation(args, context as any);
    case 'draft_reservation_email':
      return runDraftReservationEmail(args);
    case 'send_reservation_email':
      return runSendReservationEmail(args);
    case 'get_vehicle_notion':
      return notionAllowed() ?? runGetVehicleNotion(args);
    case 'get_accident_notion':
      return notionAllowed() ?? runGetAccidentNotion(args);
    case 'create_accident_notion':
      return notionAllowed() ?? runCreateAccidentNotion(args, context);
    case 'search_notion':
      return notionAllowed() ?? runSearchNotion(args);
    case 'get_notion_page':
      return notionAllowed() ?? runGetNotionPage(args);
    case 'send_operator_request':
      return runSendOperatorRequest(args);
    default:
      throw new Error(`unknown tool: ${name}`);
  }
}

// ============================================================
// リクエストウィンドウ（2026-07-12）
// 出発・返却のステータス変更はエージェントが直接実行せず、
// オペレータのブラウザ左上に実行リクエストを飛ばす（sys/requests/api.php）。
// ============================================================
const REQUESTS_API_URL =
  (process.env.REACT_APP_REQUESTS_API_URL as string) || 'https://booking.trailernchu.co.jp/sys/requests/api.php';

const MAIL_API_URL =
  (process.env.REACT_APP_MAIL_API_URL as string) || 'https://booking.trailernchu.co.jp/sys/mail/inbox_api.php';

/**
 * 教わった業務ルールの保存（2026-08-24）。
 * 実運用で「台風による旅程短縮時の返金計算」を教わったのに、会話が終われば消える状態だった
 * （agent_prompt_rules は 0 件）。LINE WORKS bot の「覚えて:」に相当する機能。
 * 保存したルールは agent/api.php が全会話のプロンプトに自動注入する。
 */
async function runRememberRule(
  args: Record<string, any>,
  context: { requestUpdateConfirm?: (before: Record<string, any>, changes: Array<{ key: string; label: string; before: string; after: string }>) => Promise<boolean> },
): Promise<any> {
  const title = String(args.title ?? '').trim();
  const ruleText = String(args.rule_text ?? '').trim();
  if (!title || !ruleText) {
    return { success: false, error: 'title と rule_text の両方を指定してください' };
  }
  // 開発の要望はルールではない（2026-10-15 記録#42:「API経由で請求書が作成できるように実装する」がルールとして
  // 保存され、毎回の指示に混ざった。AI が「請求書を作成できる」と誤解するおそれもある）
  if (isDevelopmentRequest(`${title} ${ruleText}`)) {
    return {
      success: false, not_a_rule: true,
      error: 'これは業務ルールではなく開発の要望です。remember_rule ではなく record_conversation で記録してください'
        + '（開発側が会話記録から拾います）。スタッフには「開発の要望として記録しました」と伝える',
    };
  }

  // 以後すべての会話に影響するため、必ず本人に最終確認させる
  const confirmed = context.requestUpdateConfirm
    ? await context.requestUpdateConfirm(
        { title },
        [{ key: 'rule', label: '今後のルールとして記憶', before: '(未登録)', after: ruleText }],
      )
    : true;
  if (!confirmed) {
    return { success: false, cancelled: true, message: 'ユーザーがルールの記憶を取りやめました' };
  }

  const res = await postAgentAction('save_prompt_rule', {
    scope: 'agent',
    title,
    rule_text: ruleText,
    is_active: 1,
    sort_order: 0,
  });
  if (!res || res.success === false) {
    return { success: false, error: res?.error || 'ルールの保存に失敗しました' };
  }
  return {
    success: true,
    rule_id: res.id,
    message: `「${title}」を今後のルールとして記憶しました。次回以降の会話に自動で反映されます`
      + '（/ai_agent_log の「追加ルール」タブで編集・削除できます）。',
  };
}

/**
 * メールのアーカイブ（2026-08-29 記録#15）。
 * 「アーカイブできますか」に対しツールが無く、AI が一度「受け付けました」と
 * 答えてから撤回する事象があった。mail-queue の既存 action=archive を呼ぶだけ。
 * 未処理 decision は「手動アーカイブ」として自動クローズされる（サーバ側の既存挙動）。
 */
/**
 * メールの状態を変えたことを画面へ知らせる（2026-09-01）。
 * mail-queue は自分の操作でしか再読込しないため、エージェントがアーカイブしても
 * 一覧に残って見えていた（サーバ側は即時に is_inbox=0 まで済ませている）。
 * 画面が開いていなければ誰も拾わないだけで害はない。
 */
function notifyMailQueue(action: string, mailId: string): void {
  try {
    window.dispatchEvent(new CustomEvent('mail-queue:changed', { detail: { action, mail_id: mailId } }));
  } catch { /* 画面通知の失敗で業務処理は止めない */ }
}

async function runArchiveMail(args: Record<string, any>): Promise<any> {
  const mailId = String(args.mail_id ?? '').trim();
  if (!mailId) return { success: false, error: 'mail_id を指定してください（セッション文脈の mail_id）' };
  const res = await fetch(`${MAIL_API_URL}?action=archive&api_key=${LINE_SYNC_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mail_id: mailId, operator: getOperatorName() || 'エージェント' }),
  });
  const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!res.ok || !json.success) return { success: false, error: json.error || `HTTP ${res.status}` };
  notifyMailQueue('archive', mailId);   // mail-queue を開いていれば即座に一覧へ反映
  return { success: true, message: 'メールをアーカイブしました（Gmail の受信トレイからも外れます。戻す場合は Gmail 側で受信トレイへ）。' };
}

/**
 * 予約に紐づく受信メール一覧（2026-08-29 記録#18）。
 * 予約セッション内で「このメールをアーカイブして」と言われても、セッションが
 * メール起点で開かれていない限りエージェントは mail_id を知る手段が無かった
 * （タイムラインのメール行にも id が無かった）。予約番号 → mail_id の解決手段。
 */
async function runListReservationMails(args: Record<string, any>): Promise<any> {
  const code = String(args.contact_code ?? '').trim();
  if (!code) return { success: false, error: 'contact_code（予約番号）を指定してください' };
  const res = await fetch(
    `${MAIL_API_URL}?action=mails_for_code&code=${encodeURIComponent(code)}&api_key=${LINE_SYNC_KEY}`,
  );
  const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!res.ok || !json.success) return { success: false, error: json.error || `HTTP ${res.status}` };
  // 自社ドメイン発（LP の予約通知など）は**返信先にできない**。
  // 2026-09-03: この判定が無かったため AI が通知メールへ返信を試み、サーバの
  // 422 で1ターン無駄にしていた（セッション602）。ツール層で先に伝える。
  const isOwnDomain = (sender: string) => /@(n-car\.jp|n-car\.info|trailernchu\.co\.jp|pit-rent\.com)/i.test(sender);
  const mails = (json.mails || []).map((m: any) => {
    const sender = String(m.sender ?? '');
    const own = isOwnDomain(sender);
    return {
      mail_id: m.mail_id,
      sender,
      subject: m.subject,
      received_at: m.received_at,
      in_inbox: Number(m.is_inbox ?? 0) === 1,   // false = アーカイブ済み
      is_ota: Number(m.is_ota ?? 0) === 1,
      ai_action: m.ai_action || null,
      outcome: m.outcome || null,
      // メールの型（mailWorkflows）。標準手順の判定材料
      type: classifyMail(String(m.subject ?? ''), sender),
      // repliable=false のメールを reply_to_mail_id に渡してはいけない
      repliable: !own,
      ...(own ? { not_repliable_reason: '自社システムの通知（お客様からのメールではない）' } : {}),
    };
  });
  return {
    success: true,
    count: mails.length,
    mails,
    note: mails.length === 0
      ? `予約 ${code} に紐づく受信メールはありません`
      : 'archive_mail にはこの mail_id を使う。in_inbox=false は既にアーカイブ済み。'
        + ' **reply_to_mail_id に渡してよいのは repliable=true のものだけ**'
        + '（repliable=false は自社システムの通知。返信するとサーバに拒否される）。'
        + ' 返信可能なメールが1件も無ければ reply_to_mail_id を渡さず新規メールで起案する。',
  };
}

/**
 * フォローアップ期限（スヌーズ相当・2026-08-29 §16-6j）。
 * リクエスト予約: 承諾メール送信 → 返信待ち（オファー中）→ 期限までに返信なしなら
 * 「予約に至らなかった」へ変更、という業務の期限管理。Gmail のスヌーズは API 非公開
 * のため自前実装（inbox_worker が期限到来時に返信有無を判定して再浮上させる）。
 */
function defaultFollowupAt(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} 12:00:00`;
}

async function runSetMailFollowup(args: Record<string, any>): Promise<any> {
  const mailId = String(args.mail_id ?? '').trim();
  if (!mailId) return { success: false, error: 'mail_id を指定してください（list_reservation_mails で得られます）' };
  const clearing = args.followup_at === '';   // 空文字は明示的な解除
  const followupAt = clearing ? '' : String(args.followup_at ?? '').trim() || defaultFollowupAt();
  const res = await fetch(`${MAIL_API_URL}?action=set_followup&api_key=${LINE_SYNC_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mail_id: mailId,
      followup_at: followupAt,
      note: String(args.note ?? '').trim(),
      operator: getOperatorName() || 'エージェント',
    }),
  });
  const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!res.ok || !json.success) return { success: false, error: json.error || `HTTP ${res.status}` };
  return clearing
    ? { success: true, message: 'フォローアップを解除しました。' }
    : {
        success: true,
        followup_at: followupAt,
        message: `フォローアップ期限を ${followupAt} に設定しました。`
          + '期限までにお客様の返信が無ければ mail-queue に要対応として再浮上します（返信があれば何もしません）。',
      };
}

/**
 * 添付ファイルの AI 判定（2026-09-01 記録#23: 「いずれはこのセッションでも
 * 画像（免許証）の確認ができるようにしたい」）。
 * mail-queue で実績のある judge_attachment（Gemini vision・画像は保存しないパススルー）を
 * mail_id 起点で呼ぶ。一覧 → 判定可能な添付（画像/PDF）を最大3件まで順に判定。
 */
async function runCheckMailAttachments(args: Record<string, any>): Promise<any> {
  const mailId = String(args.mail_id ?? '').trim();
  if (!mailId) return { success: false, error: 'mail_id を指定してください（list_reservation_mails で得られます）' };
  const instruction = String(args.instruction ?? '').trim();

  const listRes = await fetch(
    `${MAIL_API_URL}?action=attachments&mail_id=${encodeURIComponent(mailId)}&api_key=${LINE_SYNC_KEY}`,
  );
  const listJson = await listRes.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!listRes.ok || !listJson.success) return { success: false, error: listJson.error || `HTTP ${listRes.status}` };
  const all = (listJson.attachments || []) as Array<{ attachment_id: string; filename: string; mime: string }>;
  if (all.length === 0) {
    return { success: true, count: 0, results: [], message: 'このメールに添付ファイルはありません。' };
  }
  const judgeable = all.filter((a) => /^(image\/(jpeg|png|webp|heic|heif)|application\/pdf)$/.test(a.mime));
  if (judgeable.length === 0) {
    return {
      success: true, count: all.length, results: [],
      message: `添付は ${all.length} 件ありますが、AI 判定できる形式（画像/PDF）ではありません: `
        + all.map((a) => `${a.filename}（${a.mime}）`).join(' / '),
    };
  }

  const results: any[] = [];
  for (const a of judgeable.slice(0, 3)) {
    const res = await fetch(`${MAIL_API_URL}?action=judge_attachment&api_key=${LINE_SYNC_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mail_id: mailId,
        attachment_id: a.attachment_id,
        mime: a.mime,
        filename: a.filename,
        instruction,
        operator: getOperatorName() || 'エージェント',
      }),
    });
    const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
    results.push({
      filename: a.filename,
      ...(res.ok && json.success ? { verdict: json.verdict } : { error: json.error || `HTTP ${res.status}` }),
    });
  }
  return {
    success: true,
    count: judgeable.length,
    skipped: judgeable.length > 3 ? judgeable.length - 3 : 0,
    results,
    note: '判定は補助です。免許確認チェック等をオンにする前に、氏名一致・有効期限・懸念点をユーザーに提示して合意を得てください。',
  };
}

/** メール1通の本文取得（既存 action=mail の読み取り。2026-08-29） */
async function runGetMailContent(args: Record<string, any>): Promise<any> {
  const mailId = String(args.mail_id ?? '').trim();
  if (!mailId) return { success: false, error: 'mail_id を指定してください（list_reservation_mails で得られます）' };
  const res = await fetch(
    `${MAIL_API_URL}?action=mail&mail_id=${encodeURIComponent(mailId)}&api_key=${LINE_SYNC_KEY}`,
  );
  const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!res.ok || !json.success) return { success: false, error: json.error || `HTTP ${res.status}` };
  const m = json.mail || {};
  const body = String(m.body ?? '');
  // 本文中の予約番号（2026-10-09 セッション2009: キャンセル依頼に2件書いてあったのに1件だけ処理した）
  const codes = extractReservationCodes(`${m.subject ?? ''}\n${body}`);
  const current = getSessionMeta().kind === 'reservation' ? getSessionMeta().anchor : null;
  const others = codes.filter((c) => c !== current);
  return {
    success: true,
    ...(codes.length > 0 ? { reservation_codes: codes } : {}),
    ...(others.length > 0 ? {
      note: `⚠ このメールには ${others.join(', ')} の予約番号も書かれている`
        + (current ? `（いまのセッションは ${current}）` : '')
        + '。依頼が複数の予約にまたがっていないか確認し、該当する全件を処理する（別予約の作業はその予約のセッションで）',
    } : {}),
    mail: {
      mail_id: m.mail_id,
      sender: m.sender,
      subject: m.subject,
      received_at: m.received_at,
      in_inbox: Number(m.is_inbox ?? 0) === 1,
      body: body.length > 4000 ? `${body.slice(0, 4000)}…（全${body.length}文字）` : body,
    },
  };
}

/**
 * 確認・同意チェック（2026-08-27 記録#9 → 同日汎用化）。
 * サイドパネル CheckFlagsSection の5項目（免許確認・支払い確認・利用規約・免責事項・契約同意）。
 *
 * 保存経路（ref-save-reservation.md 準拠）:
 *   - license は**専用関数 toggleReservationLicense** のみ（CLAUDE.md 必須ルール・580件事象以来）
 *   - それ以外は fresh-fetch → 全量コピー {...fresh, detail:{...fresh.detail, 差分}} で保存
 *   - **監査メモを必ず残す**（agent-rules #8。昨日の license 単体ツールはメモに残らない穴があった）
 */
const RESERVATION_CHECK_FIELDS = [
  { key: 'license', label: '免許確認' },
  { key: 'payment', label: '支払い確認' },
  { key: 'terms_and_conditions', label: '利用規約' },
  { key: 'disclaimer', label: '免責事項' },
  { key: 'agreement', label: '契約同意' },
] as const;

async function runSetReservationChecks(
  args: Record<string, any>,
  context: { requestUpdateConfirm?: (before: Record<string, any>, changes: Array<{ key: string; label: string; before: string; after: string }>) => Promise<boolean> },
): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };

  const list = await fetchDispatchReservations({ contactCode });
  const target: any = (list || []).find((r: any) => String(r.contact_code || '') === contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };
  const detail: any = target.detail || {};

  // 変更対象を差分抽出（渡された項目だけ・同値はスキップ）
  const changes: Array<{ key: string; label: string; before: string; after: string; value: boolean }> = [];
  for (const f of RESERVATION_CHECK_FIELDS) {
    if (typeof args[f.key] !== 'boolean') continue;
    const cur = Number(detail[f.key] ?? target[f.key] ?? 0) === 1;
    if (cur === args[f.key]) continue;
    changes.push({
      key: f.key, label: f.label,
      before: cur ? '✓済み' : '未', after: args[f.key] ? '✓済み' : '未',
      value: args[f.key],
    });
  }
  if (changes.length === 0) {
    return { success: true, message: '指定されたチェックはすべて既にその状態です（変更なし）' };
  }

  const confirmed = context.requestUpdateConfirm
    ? await context.requestUpdateConfirm(
        { contact_code: contactCode, customer_name: target.customer_name },
        changes.map(({ key, label, before, after }) => ({ key, label, before, after })),
      )
    : true;
  if (!confirmed) return { success: false, cancelled: true, message: 'ユーザーが変更を取りやめました' };

  // license は専用関数（他と分離して先に実行）
  const licenseChange = changes.find((c) => c.key === 'license');
  if (licenseChange) {
    const saved = await toggleReservationLicense(target, licenseChange.value ? 1 : 0);
    if (!saved) return { success: false, error: '免許確認の保存に失敗しました' };
  }

  // 残り + 監査メモは fresh-fetch → 全量コピーで1回の保存にまとめる
  const others = changes.filter((c) => c.key !== 'license');
  const auditEntry = buildAgentMemoEntry('reservation',
    changes.map((c) => `${c.label}: ${c.before} → ${c.after}`));
  const fresh = (await fetchDispatchReservations({ contactCode }))
    ?.find((r: any) => String(r.contact_code || '') === contactCode);
  if (!fresh) return { success: false, error: '保存前の再取得に失敗しました（メモ監査が残せません）' };
  const merged: any = { ...fresh, detail: { ...(fresh.detail || {}) } };
  for (const c of others) {
    merged.detail[c.key] = c.value ? 1 : 0;
    merged[c.key] = c.value ? 1 : 0;   // root にも（buildDetailPayload は root 優先）
  }
  merged.memo = appendAgentMemoEntry(fresh.memo, auditEntry);
  merged.detail.memo = merged.memo;
  merged.__completeDetail = true;
  const saved2 = await saveDispatchReservation(merged);
  if (!saved2) {
    return {
      success: false,
      error: licenseChange
        ? '⚠ 免許確認は保存できましたが、他のチェック/メモ監査の保存に失敗しました'
        : 'チェックの保存に失敗しました',
    };
  }
  return {
    success: true,
    message: `予約 ${contactCode} のチェックを更新しました: `
      + changes.map((c) => `${c.label}=${c.after}`).join(' / ')
      + '（メモに監査を記録）',
  };
}

/**
 * 予約セッションへの切替（S3・2026-08-26）。
 * 実際の切替は useAgent（フロント状態）が行うため、ここでは**切替要求イベント**を投げる。
 * AgentFloat がこのイベントを受けて agent.openReservationSession を呼ぶ。
 * mail-queue のカードのボタンも同じイベントを使う（導線が1本にまとまる）。
 */
async function runOpenReservationSession(args: Record<string, any>): Promise<any> {
  const code = String(args.contact_code ?? '').trim();
  if (!code) return { success: false, error: 'contact_code（予約番号）を指定してください' };
  // 既に同じ予約セッションの中にいるなら開き直さない（2026-08-28: 再オープンで
  // タイムライン注入が重複した。「開いて」と言われても事実を返すだけでよい）
  const meta = getPageContext && getSessionMeta();
  if (meta.kind === 'reservation' && meta.anchor === code) {
    return { success: true, already_open: true,
      message: `既に予約セッション ${code} の中にいます（ヘッダの「業務 ▸ 予約セッション」が現在地）。このまま指示を続けてください。` };
  }
  // 往復の防止（2026-10-15 セッション2224: スタッフが別予約で作業して「このセッションに戻ります」と戻ったのに、
  // 指示文に別予約の番号があるたびに AI が切替を繰り返した。スタッフは同じ指示を3回打ち直した）
  const from = meta.kind === 'reservation' ? String(meta.anchor ?? '') : 'work';
  const back = recentSessionOpens.get(`${code}->${from}`);
  if (back && Date.now() - back < 30 * 60 * 1000) {
    return {
      success: false, refused: true,
      error: `スタッフは ${code} のセッションからこのセッションに戻って作業している。もう一度切り替えず、このセッションで続けること`
        + '（このお客様へのメール返信はこのセッションで起案してよい。別予約のデータ変更が必要ならスタッフに確認する）',
    };
  }
  // cancelable にして受け手（AgentFloat / MobilePage）が preventDefault で「受領」を返す。
  // 受け手がいない（古いバンドルの PWA 等）のに「開きました」と言うのは捏造になる（2026-08-30）。
  // さらに受け手が done(ok) で**実際に切り替わったか**を返す。待たずに「切り替えます」と答えていたため、
  // 切替が起きなくても AI が同じ返事を繰り返した（2026-10-15）
  let done: (ok: boolean) => void = () => {};
  const finished = new Promise<boolean>((resolve) => { done = resolve; });
  const unhandled = window.dispatchEvent(
    new CustomEvent('agent:open-reservation-session', { detail: { contact_code: code, done }, cancelable: true }),
  );
  if (unhandled) {
    return {
      success: false,
      error: 'この画面はセッション切替を受け取れませんでした。アプリが古い可能性があります'
        + '（スマホ版はメニューの「アップデート」または再起動で最新化してから再度お試しください）',
    };
  }
  const ok = await Promise.race([finished, new Promise<boolean | null>((r) => setTimeout(() => r(null), 15000))]);
  if (ok === false) {
    return { success: false, error: `予約セッション ${code} を開けませんでした（予約番号の誤りか通信エラー）。スタッフに伝えること` };
  }
  recentSessionOpens.set(`${from}->${code}`, Date.now());
  return {
    success: true,
    ...(ok === null ? { note: '切替の完了を確認できていない（時間切れ）' } : {}),
    message: `予約セッション ${code} へ切り替えました。切替後は「この予約」= ${code} になります。`,
  };
}

/** セッション切替の履歴（from->to → 時刻）。往復の検知に使う */
const recentSessionOpens = new Map<string, number>();

async function runGetReservationTimeline(args: Record<string, any>): Promise<any> {
  const code = String(args.contact_code ?? '').trim();
  if (!code) return { success: false, error: 'contact_code（予約番号）を指定してください' };
  const { fetchReservationTimeline } = await import('./sessionApi');
  const tl = await fetchReservationTimeline(code);
  if (!tl) return { success: false, error: 'タイムラインの取得に失敗しました' };
  return {
    success: true,
    count: tl.count,
    events: tl.events.slice(-40),   // 直近40件（トークン節約。全件数は count で分かる）
  };
}

/**
 * 会話の記録（2026-08-23）。
 * 「この件は記録して」でそこまでのやりとりを丸ごと保存する。
 * 会話本文はフロント（useAgent）が context.getTranscript で渡す
 * — ツール側で会話を組み立てず、画面に出ているものをそのまま残す。
 */
async function runRecordConversation(
  args: Record<string, any>,
  context: { getTranscript?: () => string; sessionId?: string },
): Promise<any> {
  const transcript = context.getTranscript ? context.getTranscript() : '';
  if (!transcript.trim()) {
    return { success: false, error: '記録できる会話がありません' };
  }
  const note = String(args.note ?? '').trim();
  const res = await postAgentAction('log_conversation', {
    session_id: context.sessionId || '',
    operator: getOperatorName() || 'unknown',
    note,
    page: getPageContext().page,
    transcript,
    message_count: transcript.split('\n\n').length,
  });
  if (!res || res.success === false) {
    return { success: false, error: res?.error || '記録の保存に失敗しました' };
  }
  return {
    success: true,
    record_id: res.id,
    message: `このやりとりを記録しました（記録番号 ${res.id}）。`
      + '/ai_agent_log の「会話記録」タブで見返せます。',
  };
}

// ============================================================
// メール（2026-08-16）
// 実体は inbox_api.php の compose（起案）/ send_reply（送信）。
// **宛先はサーバ側が予約データから決める**ため、AI もフロントも宛先を指定できない（誤送信ガード）。
// 送信時は本文を ia_sanitize_body で最終検査し、予約メモに送信履歴が自動追記される。
// ============================================================

/**
 * この会話で作った下書きの id。
 * ⚠ これが無いと、AI が任意の decision_id（メールキューの他人の下書き）を
 *    送信できてしまう。エージェント経由の送信は自分が作った下書きだけに限定する。
 */
const agentDraftIds = new Set<number>();
/** draft_id → 起案時の本文。send で body 未指定のときに使う */
const agentDraftBodies = new Map<number, string>();
/** draft_id → 宛先・件名（下書きカードに表示する。記録#16「送信前に必ず内容確認」） */
const agentDraftMeta = new Map<number, { to: string; subject: string }>();

/**
 * 下書きの破棄（2026-09-01・下書きカードの [破棄] ボタン）。
 * サーバの decision はそのまま残す（未送信のまま = メールキューの下書き扱い）。
 * この会話からは送れなくし、AI には「同じ内容で再提案しない」ことを伝える。
 */
export function discardAgentDraft(draftId: number): void {
  agentDraftIds.delete(draftId);
  agentDraftBodies.delete(draftId);
  agentDraftMeta.delete(draftId);
  rejectedUpdates.set(`send|${draftId}`, Date.now());
}

/** 下書きカードの [編集] 保存: 送信時に使う本文を差し替える */
export function updateAgentDraftBody(draftId: number, body: string): void {
  if (agentDraftIds.has(draftId)) agentDraftBodies.set(draftId, body);
}

/**
 * 定型テンプレート一覧（2026-08-24）。
 * サイドパネルの定型（buildBuiltinMail）+ サーバ保存分（sys/templates）を合わせて返す。
 * 実運用で「テンプレートを使いたい」に応えられず作業が2回止まったため追加。
 */
async function runListMailTemplates(args: Record<string, any> = {}): Promise<any> {
  const kind = String(args.kind ?? '').trim().toLowerCase();
  const { availableBuiltinTemplates } = await loadBuiltinMail();
  const builtin = kind === 'saved' ? [] : availableBuiltinTemplates().map((t) => ({
    key: t.key, name: t.label, kind: 'builtin' as const,
  }));
  let saved: any[] = [];
  if (kind === 'builtin') {
    return { success: true, templates: builtin, hint: 'draft_reservation_email の template にこの key を渡す' };
  }
  try {
    const res = await fetch(`${MAIL_API_URL}?action=templates&api_key=${LINE_SYNC_KEY}`);
    const json = await res.json();
    if (json?.success) {
      saved = (json.templates || []).map((t: any) => ({
        key: t.name, name: t.name, subject: t.subject, kind: 'saved' as const,
      }));
    }
  } catch (e: any) {
    // 保存分が取れなくても定型は返す。ただし黙らない（結果に理由を載せる）
    return {
      success: true,
      templates: builtin,
      warning: `保存テンプレートの取得に失敗しました（${e?.message || e}）。定型テンプレートのみ表示しています`,
    };
  }
  return {
    success: true,
    templates: [...builtin, ...saved],
    hint: 'draft_reservation_email の template にこの key を渡すと、AI で作文せず定型文をそのまま使う',
  };
}

/**
 * 予約経路からお客様向けメールの言語を決める（2026-09-28 記録#35:
 * 英語サイト lp[en] の予約に日本語で起案した。「予約経路を見れば言語がわかる」）。
 * lp[ja] / lp[ja][express] → ja、lp[en] → en、lp[zh-tw] → zh-tw。OTA・不明は null（日本語のまま）。
 */
export function languageFromMedia(media: any): string | null {
  const m = /^lp\[([a-z]{2}(?:-[a-z]{2,4})?)\]/i.exec(String(media ?? '').trim());
  return m ? m[1].toLowerCase() : null;
}
const LANGUAGE_LABEL: Record<string, string> = { ja: '日本語', en: '英語', 'zh-tw': '繁体字中国語', zh: '中国語', ko: '韓国語' };

/** 文中の予約番号（自社 TR-/PI-/PT-（枝番つき）・レンナビ RN・OTA R0）を出現順に重複なしで */
export function extractReservationCodes(text: string): string[] {
  const re = /(?:TR|PI|PT)-[A-Z0-9]{6}-[A-Z0-9]{6}(?:-\d{1,2})?|RN\d{6}-\d{4}-\d{5}|\bR0[A-Z0-9]{6}\b/g;
  return Array.from(new Set(String(text || '').match(re) || []));
}

/** 業務ルールではなく開発の要望（「〜を実装する」「〜できるようにしたい」）か */
export function isDevelopmentRequest(text: string): boolean {
  return /(実装する|実装したい|実装を|開発する|開発したい|機能を(追加|作成|作る)|連携機能|できるようにしたい|できるようにする|将来的に)/.test(String(text || ''));
}

/** 定型メールの言語コード（ja / en / tw / kr）。指定の揺れを吸収する */
export function builtinLang(v: string): 'ja' | 'en' | 'tw' | 'kr' {
  const t = String(v || '').trim().toLowerCase();
  if (t === '' || t === 'ja' || t === '日本語' || t === 'japanese') return 'ja';
  if (/^(tw|zh-tw|zh_tw|zh-hant|zh|繁体字|繁體|繁體中文|中文|chinese)$/.test(t)) return 'tw';
  if (/^(kr|ko|ko-kr|韓国語|한국어|korean)$/.test(t)) return 'kr';
  return 'en';
}

/** テンプレ名の表記ゆれ吸収（「リクエスト OK」→「リクエスト OK（在庫確保）」。2026-09-30 セッション1532） */
export function normalizeTemplateName(v: string): string {
  return String(v || '').replace(/[\s　]/g, '').replace(/[（(][^）)]*[）)]$/, '').toLowerCase();   // 末尾の補足だけ無視（途中の括弧は意味を持つ: 深夜早朝版）
}

// 複数台予約のグループ（fetchReservationGroup・applyGroupToBody）は ./reservationGroup（サイドパネルのメールと共用・2026-11-03）

/** 那覇の深夜早朝帯（手数料が付く時間帯: 20:00〜翌8:59。pricing.md） */
export function isNightHour(iso: string): boolean {
  const m = String(iso || '').match(/[T ](\d{2}):/);
  if (!m) return false;
  const h = Number(m[1]);
  return h < 9 || h >= 20;
}

/**
 * お客様向け本文に社内用語が混ざっていないか（2026-09-24 記録#32:
 * 自由作文で「現在オファー中とさせていただきました」と社内ステータス名を書いた）。
 */
const INTERNAL_TERMS = /オファー中|ステータス|\b(booked|nonreserved)\b|予約に至らなかった|無料アップグレード/;
export function findInternalTerms(body: string): string[] {
  const hits = new Set<string>();
  const re = new RegExp(INTERNAL_TERMS.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(body || ''))) !== null) hits.add(m[0]);
  return Array.from(hits);
}
/** 宮古の文面に那覇の固有値（電話・LINE・サイト・店名）が残っていないか（2026-10-03 宮古の点検） */
function otherStoreWarning(text: string): string {
  const hits = findOtherStoreTokens(text);
  return hits.length
    ? `⚠ 宮古のメールに那覇空港店の情報「${hits.join('」「')}」が入っています。宮古の情報に直してから送ってください。`
    : '';
}

/** 本文が「以下のリンク」等と言っているのに URL が無い（2026-10-14 セッション2314: 自由作文に存在しないリンクの言及） */
export function danglingLinkWarning(body: string): string {
  const b = String(body || '');
  return /(以下|下記|こちら|次)の(リンク|URL|ＵＲＬ)|the (following|link below)|click (here|the link)/i.test(b) && !/https?:\/\//.test(b)
    ? '⚠ 本文に「以下のリンク」等とあるのに URL が入っていません。リンクを入れるか、その一文を削ってください。'
    : '';
}

function internalTermsWarning(body: string): string {
  const hits = findInternalTerms(body);
  return hits.length
    ? `⚠ 本文に社内用語「${hits.join('」「')}」が入っています。お客様向けの言い方に直すか、定型テンプレ（list_mail_templates）を使ってください。`
    : '';
}

async function runDraftReservationEmail(args: Record<string, any>): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  const instruction = String(args.instruction ?? '').trim();
  const template = String(args.template ?? '').trim();
  const language = String(args.language ?? '').trim().toLowerCase();
  const replyToMailId = String(args.reply_to_mail_id ?? '').trim();
  const manualBody = String(args.body ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };
  if (!instruction && !template && !manualBody) {
    return { success: false, error: 'メールの目的（instruction）かテンプレート（template）、または修正後の全文（body）を指定してください' };
  }

  // サイドパネルと同じ定型テンプレは**フロントで決定的に組み立てる**（AI の作文を挟まない）。
  // 言語もここで切り替わる（buildBuiltinMail が ja/en に対応している）。
  const { availableBuiltinTemplates, buildBuiltinMail } = await loadBuiltinMail();
  const BUILTIN_MAIL_TEMPLATES = availableBuiltinTemplates();   // 宮古は深夜早朝OKを除く
  const builtin = template
    ? (BUILTIN_MAIL_TEMPLATES.find((t) => t.key === template || t.label === template)
      ?? BUILTIN_MAIL_TEMPLATES.find((t) => normalizeTemplateName(t.label) === normalizeTemplateName(template)))
    : undefined;
  if (builtin) {
    const list = await fetchDispatchReservations({ contactCode });
    const target: any = (list || []).find((r: any) => String(r.contact_code || '') === contactCode);
    if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };
    // 言語: 指定が無ければ予約経路（lp[en] 等）から決める（記録#35）
    const mediaLang = languageFromMedia(target.contact_media ?? target.detail?.contact_media);
    const wanted = language || mediaLang || 'ja';
    // 定型は ja / en / tw（繁体字）/ kr（韓国語）に対応している。日本語以外を一律 en に寄せていたため、
    // 繁体字を指定しても英語で起案されていた（2026-10-12 セッション2179）
    const lang = builtinLang(wanted);
    const langNote = !language && mediaLang && mediaLang !== 'ja'
      ? `予約経路（${target.contact_media ?? target.detail?.contact_media}）から${LANGUAGE_LABEL[mediaLang] ?? mediaLang}のお客様と判断し、`
        + `${({ en: '英語', tw: '繁体字', kr: '韓国語', ja: '日本語' } as Record<string, string>)[lang]}で起案しました。`
      : '';
    const built0 = buildBuiltinMail(builtin.key, toEditorReservation(target) as any, lang);
    if (!built0) return { success: false, error: `テンプレート ${builtin.key} を展開できませんでした` };
    // 複数台予約は代表（-1）宛てに全台分の内容で送る（セッション1532）
    const group = await fetchReservationGroup(target).catch(() => null);
    const built = group
      ? { ...built0, body: applyGroupToBody(built0.body, group, String(target.car_type ?? '')) }
      : built0;
    const groupNote = group
      ? `複数台予約（全${group.count}台: ${group.codes.join(', ')}）のため、全台分の内容（台数・グループ合計 ¥${group.total.toLocaleString()}）で起案しました。`
        + (/-1$/.test(String(target.contact_code)) ? '' : ' ⚠ 代表は枝番 -1 の予約です。-1 のセッションから送るのが運用です。')
      : '';
    // 深夜早朝帯のリクエストに通常の「リクエスト OK」を選んだら知らせる（2026-09-26 に深夜早朝版を追加）
    // 深夜早朝帯は那覇だけ（宮古は 9:00〜20:00 営業・深夜料金なし）
    const nightHint = getStorePolicy().nightService && builtin.key === 'request_ok' && (isNightHour(target.start_date) || isNightHour(target.end_date))
      ? '⚠ 貸出/返却が深夜早朝帯（20:00〜8:59）です。テンプレ「リクエスト（深夜早朝）OK」（request_ok_night）の方が合う可能性があります。どちらにするかユーザーに確認してください。'
      : '';
    const res0 = await fetch(`${MAIL_API_URL}?action=compose_manual&store=${getStoreKey()}&api_key=${LINE_SYNC_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        reservation_code: contactCode,
        subject: built.subject,
        body: built.body,
        origin: `builtin:${builtin.key}:${lang}`,
        reply_to_mail_id: replyToMailId,
        operator: getOperatorName() || 'エージェント',
      }),
    });
    const j0 = await res0.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
    if (!res0.ok || !j0.success) return { success: false, error: j0.error || `HTTP ${res0.status}` };
    agentDraftIds.add(Number(j0.decision_id));
    agentDraftBodies.set(Number(j0.decision_id), String(j0.body ?? ''));
    agentDraftMeta.set(Number(j0.decision_id), { to: String(j0.send_to ?? ''), subject: String(j0.subject ?? '') });
    return {
      success: true, draft_id: j0.decision_id, send_to: j0.send_to,
      subject: j0.subject, body: j0.body, template: builtin.label, language: lang,
      reply_mode: j0.reply_mode || 'new',
      ...(() => {
        const notes = [nightHint, langNote, groupNote, otherStoreWarning(`${j0.subject ?? ''}\n${j0.body ?? ''}`)].filter(Boolean);
        return notes.length ? { note: notes.join('\n') } : {};
      })(),
      message: `テンプレート「${builtin.label}」${lang === 'en' ? '（英語）' : ''}で起案しました`
        + (j0.reply_mode === 'thread' ? '（受信メールへの返信として送られます）' : '（新規メール）')
        + '。まだ送信していません。',
    };
  }

  // 言語の既定: 予約経路（lp[en] 等）。指定があればそれを優先（記録#35）
  let composeLanguage = language;
  let t0: any = null;
  try {
    const l0 = await fetchDispatchReservations({ contactCode });
    t0 = (l0 || []).find((r: any) => String(r.contact_code || '') === contactCode) ?? null;
  } catch { /* 取得失敗は照合・言語の判定をしない */ }
  if (!composeLanguage) {
    const ml = languageFromMedia(t0?.contact_media ?? t0?.detail?.contact_media);
    if (ml && ml !== 'ja') composeLanguage = ml;
  }
  // 下書きに書く合計は予約の合計（複数台はグループ合計）と同じでなければならない（2026-11-03 N1）
  const expectedTotal = t0 ? {
    sum: Number(t0.detail?.sum_price ?? t0.sum_price) || 0,
    groupTotal: (await fetchReservationGroup(t0).catch(() => null))?.total ?? null,
  } : null;
  const manualTotalProblem = expectedTotal && manualBody ? draftTotalProblem(manualBody, expectedTotal) : null;
  if (manualTotalProblem) {
    logEvent({ action: 'agent.draft_total_mismatch', target: contactCode, result: 'fail', err: manualTotalProblem });
    return { success: false, not_executed: true, error: `下書きを作っていません。${manualTotalProblem}` };
  }
  const res = await fetch(`${MAIL_API_URL}?action=compose&store=${getStoreKey()}&api_key=${LINE_SYNC_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      reservation_code: contactCode,
      // body のみ指定 = 修正後の全文をそのまま起案（サーバが Gemini を通さない決定的経路）
      instruction: instruction || (template ? `テンプレート「${template}」で連絡する` : ''),
      template_name: template,   // サーバ保存テンプレ（指定時は決定的に展開）
      body: !instruction && !template ? manualBody : undefined,
      subject: String(args.subject ?? '').trim() || undefined,
      language: composeLanguage,
      reply_to_mail_id: replyToMailId,
      operator: getOperatorName() || 'エージェント',
    }),
  });
  const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!res.ok || !json.success) {
    // サーバのエラーは保存テンプレしか列挙しない。定型（builtin）の key も添えて迷わせない
    // （2026-10-03 セッション1672: 存在しない「offer_ok」を指定 → 一覧に定型が無く行き詰まった）
    const hint = template
      ? ` 定型テンプレの key: ${BUILTIN_MAIL_TEMPLATES.map((t) => `${t.key}（${t.label}）`).join(' / ')}`
      : '';
    return { success: false, error: (json.error || `HTTP ${res.status}`) + hint };
  }

  agentDraftIds.add(Number(json.decision_id));
  agentDraftBodies.set(Number(json.decision_id), String(json.body ?? ''));
  agentDraftMeta.set(Number(json.decision_id), { to: String(json.send_to ?? ''), subject: String(json.subject ?? '') });
  // 指示文からサーバが作文した本文も、出来上がりで合計を照合する（作られた下書きは「送らない」と知らせる）
  const composedTotalProblem = expectedTotal ? draftTotalProblem(String(json.body ?? ''), expectedTotal) : null;
  if (composedTotalProblem) logEvent({ action: 'agent.draft_total_mismatch', target: contactCode, result: 'fail', err: composedTotalProblem });
  return {
    success: true,
    ...(composedTotalProblem ? { claim_problems: [composedTotalProblem] } : {}),
    draft_id: json.decision_id,
    send_to: json.send_to,
    subject: json.subject,
    body: json.body,
    note: [json.note || '', internalTermsWarning(String(json.body ?? '')), danglingLinkWarning(String(json.body ?? '')),
      otherStoreWarning(`${json.subject ?? ''}\n${json.body ?? ''}`)].filter(Boolean).join('\n'),
    reply_mode: json.reply_mode || 'new',
    message: 'メールを起案しました'
      + (json.reply_mode === 'thread' ? '（受信メールへの返信として送られます）' : '（新規メール）')
      + '。まだ送信していません。本文を確認して、送ってよければ送信を指示してください。',
  };
}

export async function runSendReservationEmail(args: Record<string, any>): Promise<any> {
  const draftId = Number(args.draft_id);
  if (!Number.isFinite(draftId) || draftId <= 0) {
    return { success: false, error: 'draft_id を指定してください（先に draft_reservation_email で起案します）' };
  }
  if (!agentDraftIds.has(draftId)) {
    return {
      success: false,
      error: 'この会話で作成した下書きではないため送信できません。draft_reservation_email で起案し直してください',
    };
  }
  // 本文未指定なら起案時の本文をそのまま送る（send_reply は本文必須）
  const body = (typeof args.body === 'string' && args.body.trim() !== '')
    ? args.body
    : (agentDraftBodies.get(draftId) || '');
  if (body.trim() === '') {
    return { success: false, error: '送信する本文がありません。draft_reservation_email で起案し直してください' };
  }

  const sendRejectedAt = rejectedUpdates.get(`send|${draftId}`);
  if (sendRejectedAt && Date.now() - sendRejectedAt < 5 * 60 * 1000) {
    // ガードは1回だけ効かせる: ループは断ち切るが、ユーザーが明示的に
    // 「やっぱり送って」と言った2回目の指示は通す
    rejectedUpdates.delete(`send|${draftId}`);
    return {
      success: false,
      rejected_guard: true,
      error: 'この下書きの送信は直前にユーザーが破棄しています。本文や宛先に問題がないか確認し、'
        + '必要なら起案し直してから送信を提案してください。',
    };
  }
  // 確認カードは出さない（2026-09-01 前田さん）。起案時にチャットへ出る**下書きカード**が
  // 宛先・件名・本文を改行つきで見せ、その [送信] ボタン自体が人の承認になっている。
  // 二重に同じ内容を見せていた確認カードは廃止。ユーザーが「送信して」と言った場合も、
  // その発話が承認そのもの（本文は下書きカードで既に提示済み）。
  const res = await fetch(`${MAIL_API_URL}?action=send_reply&store=${getStoreKey()}&api_key=${LINE_SYNC_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision_id: draftId, body, operator: getOperatorName() || 'エージェント' }),
  });
  const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!res.ok || !json.success) return { success: false, error: json.error || `HTTP ${res.status}` };

  agentDraftIds.delete(draftId); // 二重送信防止（サーバ側でも outcome で弾く）
  agentDraftBodies.delete(draftId);
  agentDraftMeta.delete(draftId);
  // 送信で decision がクローズされ、スレッド返信では元メールもアーカイブされる。
  // mail_id はここでは分からないので画面には再読込だけ依頼する（2026-09-01）
  notifyMailQueue('send', '');
  return {
    success: true,
    sent_to: json.sent_to,
    subject: json.subject,
    memo_appended: json.memo_appended,
    // memo_appended は true のときだけ「残した」と言う（null=追記経路を通っていない。
    // 2026-08-29: スレッド返信で追記されていないのに「残しました」と表示していた）
    message: `${json.sent_to} へ送信しました`
      + (json.memo_appended === true ? '（予約メモに履歴を残しました）'
         : json.memo_appended === false ? '（⚠ 予約メモへの履歴追記は失敗しました）'
         : '（⚠ 予約メモへの履歴追記は確認できませんでした）'),
  };
}

/**
 * キャンセル一式（ステータス + Square請求書取消 + 監査メモ）。
 * 実処理はサーバ側 inbox_api.php?action=cancel_full の**1回のPOST**
 * （メールキューで実証済みの経路。2026-08-11 のロールバック事故対策 = 書き込み1回の原則）。
 * フロントは予約の取得と確認カードだけを担う。
 */
async function runCancelReservationFull(
  args: Record<string, any>,
  context: { requestUpdateConfirm?: (before: Record<string, any>, changes: Array<{ key: string; label: string; before: string; after: string }>) => Promise<boolean> },
): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  const reason = String(args.reason ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };

  const candidates = await fetchDispatchReservations({ contactCode });
  const target: any = (candidates as any[]).find((r) => String(r.contact_code || '') === contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };

  const statusNow = formatStatus(target.status_code ?? target.status);
  if (String(target.status_code) === 'cancelled' || Number(target.status) === 4) {
    return { success: false, error: `予約 ${contactCode} は既にキャンセル済みです` };
  }

  const invoiceLink = String(target.detail?.invoice_link ?? '');
  const changes: Array<{ key: string; label: string; before: string; after: string }> = [
    { key: 'status', label: 'ステータス', before: statusNow, after: 'キャンセル済み' },
  ];
  if (invoiceLink !== '') {
    changes.push({ key: 'invoice', label: 'Square 請求書', before: '発行済み', after: '取消' });
  }
  changes.push({ key: 'memo', label: '社内メモ（追記）', before: '（既存の履歴は維持）',
    after: reason !== '' ? `キャンセル記録 + ${reason}` : 'キャンセル記録' });

  const confirmed = context.requestUpdateConfirm
    ? await context.requestUpdateConfirm({
        reservation_id: target.id,
        contact_code: target.contact_code,
        customer_name: target.customer_name,
        car_type: target.car_type,
        pickup_datetime: formatDateTime(target.start_date),
        return_datetime: formatDateTime(target.end_date),
        status: statusNow,
      }, changes)
    : true;
  if (!confirmed) {
    return { success: false, cancelled: true, message: 'ユーザーがキャンセル処理を取りやめました' };
  }

  const operator = getOperatorName() || '';

  const res = await fetch(`${MAIL_API_URL}?action=cancel_full&store=${getStoreKey()}&api_key=${LINE_SYNC_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact_code: contactCode, reason, operator: operator || 'エージェント' }),
  });
  const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
  if (!res.ok || !json.success) {
    return { success: false, error: json.error || `HTTP ${res.status}`, invoice: json.invoice ?? null };
  }
  return {
    success: true,
    message: `予約 ${contactCode}（${target.customer_name} 様）をキャンセルしました`
      + (json.invoice ? ` / 請求書: ${json.invoice}` : invoiceLink === '' ? '（請求書なし）' : ''),
    invoice: json.invoice ?? null,
  };
}

// -------- Square 請求書の状況と再発行（2026-10-31 前田さん: TR-2TC95Y-3BBKHB の再発行をエージェントで） --------
// 読み取りは squareStatus.ts（サイドパネルと同じ search_by_contact.php・Bearer は internalApiAuth が付ける）。
// 取り消し・作成はサイドパネル（useSquareInvoice）と同じ square_void_invoice.php / square_create_invoice.php。
// 書き込みはどちらも直前に Square を読み直して判断し、確認カード → 実行 → 予約に保存 → 保存の事後検証。

async function findReservationByCode(contactCode: string): Promise<any | null> {
  const list = await fetchDispatchReservations({ contactCode });
  return (list as any[])?.find((r: any) => String(r.contact_code || '') === contactCode) ?? null;
}

function invoiceLinkOf(r: any): string {
  return String(r?.detail?.invoice_link ?? r?.invoice_link ?? '').trim();
}

const yen = (n: unknown) => `¥${(Number(n) || 0).toLocaleString()}`;

async function runGetSquarePaymentStatus(args: Record<string, any>): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };
  const target = await findReservationByCode(contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };
  const link = invoiceLinkOf(target);
  const summary = await fetchSquareStatus(contactCode, link);
  if (!summary.ok) {
    logEvent({ action: 'agent.square_status', target: contactCode, result: 'fail', err: summary.error });
    return { success: false, error: summary.error };
  }
  const reservationSum = Number(target.detail?.sum_price ?? target.sum_price) || 0;
  return {
    success: true,
    contact_code: contactCode,
    customer_name: target.customer_name,
    reservation_sum: reservationSum,
    ...(summary.invoice && reservationSum > 0 && summary.invoice.amount !== reservationSum
      ? { amount_mismatch: `請求書の金額 ${yen(summary.invoice.amount)} と予約の合計 ${yen(reservationSum)} が違います` } : {}),
    ...(link && !summary.invoice
      ? { note: '予約に結ばれた請求書が決済 DB に見つかりません（Square で削除済み・別店舗など）。サイドパネルの Square 欄で確かめてください' } : {}),
    ...(!link ? { note: '予約に請求書が結ばれていません' } : {}),
    ...summary,
  };
}

type SquareWriteContext = {
  requestUpdateConfirm?: (before: Record<string, any>, changes: Array<{ key: string; label: string; before: string; after: string }>) => Promise<boolean>;
  /** このターンのオペレータの発言（請求書の取り消し・作成の指示を承認として扱うか） */
  lastUserText?: string;
};

/** 誰の指示で実行したか（予約メモとイベントログに残す） */
function squareApproval(kind: 'void' | 'create', ctx: SquareWriteContext) {
  const text = String(ctx.lastUserText ?? '').trim();
  const op = operatorInvoiceInstruction(text);
  const byOperator = kind === 'void' ? op.void : op.create;
  const staff = getCurrentStaffName() || getOperatorName() || 'スタッフ';
  return {
    byOperator,
    staff,
    instruction: byOperator ? text.slice(0, 120) : null,
    memoLine: byOperator ? `指示: ${staff}（チャット「${text.slice(0, 80)}」）` : `承認: ${staff}（確認カード）`,
  };
}

function reservationCardInfo(target: any) {
  return {
    reservation_id: target.id, contact_code: target.contact_code, customer_name: target.customer_name,
    car_type: target.car_type, pickup_datetime: formatDateTime(target.start_date),
    return_datetime: formatDateTime(target.end_date), status: formatStatus(target.status_code ?? target.status),
  };
}

async function runVoidSquareInvoice(args: Record<string, any>, context: SquareWriteContext): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  const reason = String(args.reason ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };
  if (!isSquareEnabled()) return { success: false, error: 'この店舗では Square 請求書が使えません' };
  const target = await findReservationByCode(contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };
  const link = invoiceLinkOf(target);
  if (!link) return { success: false, error: `予約 ${contactCode} には請求書が結ばれていません。片付けは不要です（create_square_invoice へ進む）` };
  const approval = squareApproval('void', context);

  // 実行の直前に Square を読み直す（refresh=1）。前に読んだ状態を信じない
  let st = await fetchSquareStatus(contactCode, link);
  if (!st.ok) return { success: false, error: st.error };
  if (!st.reissue.allowed || st.reissue.void_action === 'none') {
    logEvent({ action: 'agent.square_void', target: contactCode, result: 'fail', err: 'blocked',
      detail: { status: st.invoice?.status ?? null, by: approval.staff, approval: approval.byOperator ? 'operator_instruction' : 'confirm_card' } });
    return { success: false, blocked: true, error: st.reissue.blocked_reason ?? '片付けられない状態です', status: st.invoice?.status ?? null };
  }
  let inv = st.invoice!;
  let willCancel = st.reissue.void_action === 'cancel';

  // オペレータがチャットで取り消しを指示していればそれが承認。指示が無ければ確認カードでスタッフが決める
  if (!approval.byOperator) {
    const confirmed = context.requestUpdateConfirm
      ? await context.requestUpdateConfirm(reservationCardInfo(target), [
          { key: 'invoice', label: 'Square 請求書',
            before: `${inv.status}（${inv.status_label}）${yen(inv.amount)}・支払い済み ${yen(inv.paid_amount)}`,
            after: willCancel ? 'Square で取り消して、予約から外す' : '予約から外す（Square で取り消し済み）' },
          { key: 'memo', label: '社内メモ（追記）', before: '（既存の履歴は維持）', after: `古い請求書の記録（${link}）${reason ? ` + ${reason}` : ''}` },
        ])
      : true;
    if (!confirmed) return { success: false, cancelled: true, message: 'ユーザーが請求書の片付けを取りやめました' };
    // カードを待つ間に払われたかもしれないので、もう一度読み直す
    st = await fetchSquareStatus(contactCode, link);
    if (!st.ok) return { success: false, error: st.error };
    if (!st.reissue.allowed || st.reissue.void_action === 'none') {
      return { success: false, blocked: true, error: `確認の間に状態が変わりました: ${st.reissue.blocked_reason ?? st.invoice?.status}`, status: st.invoice?.status ?? null };
    }
    inv = st.invoice!;
    willCancel = st.reissue.void_action === 'cancel';
  }

  if (willCancel) {
    let data: any = null;
    try {
      const res = await fetch(`${SYS_INVOICE_BASE}/square_void_invoice.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoice_id: link }),
      });
      data = await res.json().catch(() => null);
    } catch (e: any) {
      data = { status: 'error', message: e?.message || String(e) };
    }
    if (data?.status !== 'success') {
      // 手順はここで止める。Square のエラーと次の案内をそのままチャットに出させる（2026-10-31 前田さん:
      // 「失敗するようならオペレータが直接 Square 上で取り消してから、API 経由で再度作成します」）
      const squareError = readableSquareError(data?.message ?? data?.error);
      const dashboard = squareDashboardUrl(link);
      const guidance = 'Square の管理画面でこの請求書を取り消してから、「再度作成します」と指示してください';
      logEvent({ action: 'agent.square_void', target: contactCode, result: 'fail', err: squareError,
        detail: { old_invoice: link, status: inv.status, by: approval.staff, instruction: approval.instruction } });
      return { success: false, stop_plan: true, old_invoice: link, old_status: inv.status, square_error: squareError, guidance,
        ...(dashboard ? { square_dashboard_url: dashboard } : {}),
        error: `Square が古い請求書（${inv.status}・${yen(inv.amount)}）の取り消しを受け付けませんでした: ${squareError}。予約はそのままです。${guidance}`,
        next: 'ここで手順を止める（advance_plan で手順2を waiting_staff にし、square_error と guidance をそのままチャットに書く）。create_square_invoice には進まない' };
    }
  }

  const fresh = await findReservationByCode(contactCode);
  if (!fresh) return { success: false, error: willCancel ? 'Square では取り消しましたが、予約の再取得に失敗しました。サイドパネルで請求書IDを消してください' : '予約の再取得に失敗しました' };
  const entry = buildAgentMemoEntry('invoice', [
    `${willCancel ? 'Square 請求書を取り消し' : 'Square 請求書を予約から外した（Square で取り消し済み）'}: ${link}`
      + `（${inv.status}・${yen(inv.amount)}・支払い済み ${yen(inv.paid_amount)}・決済の試み ${st.payment_attempts.total} 回/失敗 ${st.payment_attempts.failed} 回）`,
    approval.memoLine,
    reason,
  ]);
  const merged: any = { ...fresh, invoice_link: '', detail: { ...(fresh.detail || {}), invoice_link: '' } };
  merged.memo = appendAgentMemoEntry(fresh.memo, entry);
  merged.detail.memo = merged.memo;
  merged.__completeDetail = true;
  const saved = await saveDispatchReservation(merged);
  if (!saved) {
    logEvent({ action: 'agent.square_void', target: contactCode, result: 'fail', err: 'reservation save failed',
      detail: { cancelled_on_square: willCancel, old_invoice: link, by: approval.staff, instruction: approval.instruction } });
    return { success: false, error: `${willCancel ? 'Square では取り消しましたが、' : ''}予約の保存に失敗しました。サイドパネルで請求書IDを消してください` };
  }
  let verified: boolean | null = null;
  try { const after = await findReservationByCode(contactCode); if (after) verified = invoiceLinkOf(after) === ''; } catch { /* 検証不能でも保存は成立 */ }
  logEvent({ action: 'agent.square_void', target: contactCode, result: 'ok', detail: {
    mode: willCancel ? 'cancel' : 'unlink', old_invoice: link, status: inv.status, amount: inv.amount, verified,
    by: approval.staff, approval: approval.byOperator ? 'operator_instruction' : 'confirm_card', instruction: approval.instruction } });
  return {
    success: true, contact_code: contactCode, old_invoice: link, old_status: inv.status, mode: willCancel ? 'cancel' : 'unlink',
    approved_by: approval.byOperator ? 'operator_instruction' : 'confirm_card',
    ...(verified === false ? { warning: '⚠ 保存後の確認で請求書IDが残っています。サイドパネルで確かめてください' } : {}),
    message: (willCancel
      ? `Square で古い請求書（${inv.status}・${yen(inv.amount)}）を取り消し、予約から外しました`
      : `古い請求書は Square で取り消し済み（${inv.status}）だったので、予約から外しました`)
      + '（メモに記録）' + (verified === true ? '（反映確認済み）' : ''),
    next: '新しい請求書は create_square_invoice で作る',
  };
}

async function runCreateSquareInvoice(args: Record<string, any>, context: SquareWriteContext): Promise<any> {
  const contactCode = String(args.contact_code ?? '').trim();
  const reason = String(args.reason ?? '').trim();
  if (!contactCode) return { success: false, error: 'contact_code（予約番号）を指定してください' };
  if (!isSquareEnabled()) return { success: false, error: 'この店舗では Square 請求書が使えません' };
  const target = await findReservationByCode(contactCode);
  if (!target) return { success: false, error: `予約 ${contactCode} が見つかりませんでした` };
  const approval = squareApproval('create', context);

  // 実行の直前に Square を読み直す。まだ払える・払われた請求書が結ばれていたら止める（二重請求を防ぐ）
  const oldLink = invoiceLinkOf(target);
  const st = await fetchSquareStatus(contactCode, oldLink);
  if (!st.ok) return { success: false, error: st.error };
  // 取り消し済み（CANCELED）・終わった状態で未払い（FAILED）なら、取り消しの手順は済んだものとして作る
  // （オペレータが Square の管理画面で取り消してから「再度作成します」と指示する流れ）
  const over = canCreateOver(oldLink, st.invoice);
  if (!over.ok) return { success: false, blocked: true, error: over.reason };
  // 同じ予約番号に払われた請求書があれば作らない（予約から外れていても二重請求になる）
  const paidOther = [st.invoice, ...st.other_invoices].find((x) => x && ['PAID', 'PARTIALLY_PAID', 'UNPAID', 'SCHEDULED', 'PAYMENT_PENDING'].includes(x.status));
  if (paidOther) {
    return { success: false, blocked: true,
      error: `同じ予約番号に ${paidOther.status}（${paidOther.status_label}）の請求書（${paidOther.invoice_id}・${yen(paidOther.amount)}）があります。二重請求になるので作りません` };
  }
  const ref = referenceInvoice(st);

  const editor: any = toEditorReservation(target);
  if (!editor?.start_date || !editor?.end_date) return { success: false, error: '貸出日時か返却日時が入っていないため作れません' };
  const email = String(editor.customer_email ?? '').trim();
  if (!email) return { success: false, error: 'お客様のメールアドレスが無いため Square 請求書を作れません。サイドパネルでメールアドレスを入れてください' };
  const sum = Number(editor.sum_price) || 0;

  // 金額は取り消した請求書と同じにする。違えば作らず、差を示してオペレータに聞く
  if (ref && ref.amount !== sum) {
    logEvent({ action: 'agent.square_create', target: contactCode, result: 'fail', err: 'amount_mismatch',
      detail: { reference: ref.invoice_id, reference_amount: ref.amount, reservation_sum: sum, by: approval.staff } });
    return { success: false, needs_decision: true,
      error: `前の請求書（${ref.invoice_id}・${ref.status}）は ${yen(ref.amount)}、予約の合計は ${yen(sum)} で、${yen(Math.abs(sum - ref.amount))} 違います。作っていません。`
        + 'どちらの金額で作るか（予約の料金を直すか）をオペレータに聞いてください',
      reference_line_items: ref.line_items };
  }
  if (approval.byOperator && !ref) {
    return { success: false, needs_decision: true,
      error: 'くらべる前の請求書（FAILED・取り消し済み）が見つからないため、オペレータの指示だけでは作りません。確認カードで金額を確かめて作るか、オペレータに聞いてください' };
  }

  // 送り方: 引数が無ければ、オペレータの指示のときは前の請求書と同じ（お客様に送っていたなら送る）。確認カードのときは下書き
  const refWasSent = !!ref && ref.delivery_method === 'EMAIL';
  const send = args.send === true || args.send === 'true' ? true
    : args.send === false || args.send === 'false' ? false
    : approval.byOperator ? refWasSent : false;
  const sqNum = getSquareIndex();
  const payload: any = buildSquareInvoicePayload(editor, sqNum);
  if (send) payload.publish = true;

  if (!approval.byOperator) {
    const confirmed = context.requestUpdateConfirm
      ? await context.requestUpdateConfirm(reservationCardInfo(target), [
          { key: 'invoice', label: 'Square 請求書', before: ref ? `前の請求書 ${ref.invoice_id}（${ref.status}・${yen(ref.amount)}）` : 'なし',
            after: `新しい請求書 ${yen(sum)}（${editor.car_type ?? ''}・${formatDateTime(editor.start_date)}〜${formatDateTime(editor.end_date)}）` },
          { key: 'send', label: '送り方', before: '',
            after: send ? `Square からお客様（${email}）へ請求書のメールを送る` : '下書き（お客様には届かない。Square で確かめてから送る）' },
          { key: 'memo', label: '社内メモ（追記）', before: '（既存の履歴は維持）', after: `請求書の発行記録${reason ? ` + ${reason}` : ''}` },
        ])
      : true;
    if (!confirmed) return { success: false, cancelled: true, message: 'ユーザーが請求書の作成を取りやめました' };
  }

  let data: any = null;
  try {
    const res = await fetch(`${SYS_INVOICE_BASE}/square_create_invoice.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    data = await res.json().catch(() => null);
  } catch (e: any) {
    data = { status: 'error', message: e?.message || String(e) };
  }
  if (data?.status !== 'success' || !data?.invoice_id) {
    const msg = typeof data?.message === 'string' ? data.message : JSON.stringify(data?.message ?? data?.error ?? '応答なし');
    logEvent({ action: 'agent.square_create', target: contactCode, result: 'fail', err: msg,
      detail: { reference: ref?.invoice_id ?? null, by: approval.staff, instruction: approval.instruction } });
    return { success: false, error: `請求書を作れませんでした（${msg}）。予約はそのままです` };
  }
  const newLink = String(data.invoice_id);
  const published = data.published === true;

  // 予約に結ぶ（サイドパネルと同じく invoice_link を保存）。失敗したら「作ったが結べていない」をはっきり返す
  const fresh = await findReservationByCode(contactCode);
  const entry = buildAgentMemoEntry('invoice', [
    `Square 請求書を作成: ${newLink}（${yen(sum)}・${published ? 'お客様へ送付' : '下書き'}）`,
    ref ? `前の請求書: ${ref.invoice_id}（${ref.status}・${yen(ref.amount)}）の作り直し` : '',
    approval.memoLine, reason,
  ]);
  let saved = false;
  if (fresh) {
    const merged: any = { ...fresh, invoice_link: newLink, detail: { ...(fresh.detail || {}), invoice_link: newLink } };
    merged.memo = appendAgentMemoEntry(fresh.memo, entry);
    merged.detail.memo = merged.memo;
    merged.__completeDetail = true;
    saved = !!(await saveDispatchReservation(merged));
  }
  if (!saved) {
    logEvent({ action: 'agent.square_create', target: contactCode, result: 'fail', err: 'reservation link save failed',
      detail: { new_invoice: newLink, published, by: approval.staff, instruction: approval.instruction } });
    return { success: false, invoice_link: newLink, published,
      error: `請求書は作りました${published ? '（お客様へ送付済み）' : '（下書き）'}が、予約に結べませんでした。サイドパネルの請求書IDに ${newLink} を入れてください` };
  }
  let verified: boolean | null = null;
  try { const after = await findReservationByCode(contactCode); if (after) verified = invoiceLinkOf(after) === newLink; } catch { /* 検証不能でも保存は成立 */ }

  // 新しい請求書を Square から読み直して、番号・状態・送り方・明細を確かめる
  const now = await fetchSquareStatus(contactCode, newLink);
  const ni = now.ok ? now.invoice : null;
  const lines = ref && ni ? diffLineItems(ref.line_items, ni.line_items) : null;
  const delivery = published ? `Square からお客様のメール（${email}）へ送付` : '下書き（お客様にはまだ届いていない。Square で確かめて送る）';
  logEvent({ action: 'agent.square_create', target: contactCode, result: 'ok', detail: {
    new_invoice: newLink, invoice_number: ni?.invoice_number ?? null, status: ni?.status ?? null, published, sum, verified,
    reference: ref?.invoice_id ?? null, line_items_same: lines ? lines.same : null, db_registered: data.db_registered ?? null,
    by: approval.staff, approval: approval.byOperator ? 'operator_instruction' : 'confirm_card', instruction: approval.instruction } });
  return {
    success: true, contact_code: contactCode, invoice_link: newLink, published, amount: sum,
    approved_by: approval.byOperator ? 'operator_instruction' : 'confirm_card',
    new_invoice: {
      invoice_id: ni?.invoice_id ?? newLink.replace(/^sq:\d+:/, ''),
      invoice_number: ni?.invoice_number ?? null,
      status: ni?.status ?? null, status_label: ni ? ni.status_label : '（Square から読み直せませんでした）',
      amount: ni?.amount ?? sum, delivery,
    },
    ...(lines && !lines.same ? { warning_lines: `⚠ 明細が前の請求書と違います。前だけにある行: ${JSON.stringify(lines.only_old)} / 新しいほうだけにある行: ${JSON.stringify(lines.only_new)}。オペレータに伝えてください` } : {}),
    ...(send && !published ? { warning: 'この店舗の請求書は常に下書きです（宮古）。Square で確かめてから送ってください' } : {}),
    ...(data.db_registered === false ? { warning_db: `決済 DB への登録に失敗しました（${data.db_register_error ?? '不明'}）。サイドパネルの Square 欄で［更新］してください` } : {}),
    ...(verified === false ? { warning_link: '⚠ 保存後の確認で請求書IDが一致しません。サイドパネルで確かめてください' } : {}),
    message: `新しい Square 請求書を作り、予約に結びました。番号 ${ni?.invoice_number ?? '（未取得）'}・状態 ${ni?.status ?? '（未取得）'}・${yen(sum)}・${delivery}`
      + (lines ? (lines.same ? '・明細は前の請求書と同じ' : '・⚠ 明細が前の請求書と違う') : '')
      + (verified === true ? '（予約への反映確認済み）' : ''),
    next: 'チャットに新しい請求書の番号・状態・送り方を書く。お客様への返信は draft_reservation_email で案を出すだけ（送信はオペレータ）',
  };
}

async function runSendOperatorRequest(args: Record<string, any>): Promise<any> {
  const vehicleQuery = String(args.vehicle ?? '').trim();
  const type = String(args.type ?? 'departure') === 'return' ? 'return' : 'departure';
  if (!vehicleQuery) return { success: false, error: 'vehicle（車両番号）を指定してください' };

  // 車両解決（bot と同じ優先順: number 完全一致 → code 部分一致 → 末尾数字）
  let vehicleId: number | null = null;
  let label = vehicleQuery;
  try {
    const vehicles: any[] = await fetchDispatchVehicles();
    const q = vehicleQuery.replace(/[\s　]/g, '');
    const tail = (q.match(/(\d{2,})$/) || [])[1];
    const hit = vehicles.find((v: any) => String(v.vehicle_number || '') === q)
      || vehicles.find((v: any) => String(v.vehicle_code || '').toLowerCase().includes(q.toLowerCase()))
      || (tail && tail !== q ? vehicles.find((v: any) => String(v.vehicle_number || '') === tail) : undefined);
    if (hit) {
      vehicleId = Number(hit.id);
      const code = String(hit.vehicle_code || '');
      const m = code.match(/(\d{3,4})(?:SP)?$/);
      label = m ? m[1] : (String(hit.vehicle_number || '') || code || vehicleQuery);
    }
  } catch { /* 未解決でもリクエスト自体は送る */ }

  const staffName = getCurrentStaffName();
  const res = await fetch(`${REQUESTS_API_URL}?action=create&api_key=${LINE_SYNC_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type,
      vehicle_label: label,
      vehicle_id: vehicleId,
      note: `${type === 'return' ? '返却' : '出発'}リクエスト（エージェント）`,
      source: 'web_agent',
      created_by: staffName,
      store: getStoreKey(),
    }),
  });
  const data = await res.json();
  if (!data.success) return { success: false, error: data.error || 'リクエストの送信に失敗しました' };
  const kind = type === 'return' ? '返却' : '貸出';
  return {
    success: true,
    message: `車両${label}${kind}変更のリクエストが送られました。`
      + (vehicleId === null ? '（車両を特定できなかったため、オペレータ側で候補が出ない場合があります）' : ''),
  };
}

/** 社内 Notion を使うツール（店舗の設定で notion=false なら AI に見せない・実行もしない） */
const NOTION_TOOLS = new Set(['get_vehicle_notion', 'get_accident_notion', 'create_accident_notion', 'search_notion', 'get_notion_page']);

/** Notion を使わない店舗なら拒否の結果を返す（使える店舗なら null） */
function notionAllowed(): { success: false; error: string } | null {
  return getStorePolicy().notion ? null
    : { success: false, error: 'この店舗では社内 Notion（車両情報・事故報告）を使っていません' };
}

/** いまの店舗で AI に渡すツール宣言（2026-10-06: 宮古は Notion を使わない） */
export function getAgentToolDeclarations(): any[] {
  return getStorePolicy().notion
    ? (AGENT_TOOL_DECLARATIONS as any[])
    : (AGENT_TOOL_DECLARATIONS as any[]).filter((d) => !NOTION_TOOLS.has(d.name));
}

export const AGENT_STEP_LABELS: Record<string, { running: string; success: (result: any) => string }> = {
  apply_price_change: {
    running: '💴 料金を再計算中…',
    success: (r) => (r?.success
      ? `✅ 料金を反映しました（合計 ¥${Number(r?.sum?.before ?? 0).toLocaleString()} → ¥${Number(r?.sum?.after ?? 0).toLocaleString()}）`
      : r?.cancelled ? '取り消されました' : `⚠️ ${r?.needs_decision ? '日額の確認が必要です' : (r?.error ?? '反映できませんでした')}`),
  },
  get_square_payment_status: {
    running: '💳 Square の決済状況を確認中…',
    success: (r) => (r?.success
      ? `💳 請求書: ${r?.invoice ? `${r.invoice.status}・¥${Number(r.invoice.amount ?? 0).toLocaleString()}（支払い済み ¥${Number(r.invoice.paid_amount ?? 0).toLocaleString()}）` : 'なし'}・決済の試み ${r?.payment_attempts?.total ?? 0} 回`
      : `⚠️ ${r?.error ?? '読めませんでした'}`),
  },
  void_square_invoice: {
    running: '🧾 古い請求書を片付け中…',
    success: (r) => (r?.success ? `✅ ${r?.message ?? '古い請求書を片付けました'}` : r?.cancelled ? '取り消されました' : `⚠️ ${r?.error ?? '片付けられませんでした'}`),
  },
  create_square_invoice: {
    running: '🧾 Square 請求書を作成中…',
    success: (r) => (r?.success ? `✅ ${r?.message ?? '請求書を作りました'}` : r?.cancelled ? '取り消されました' : `⚠️ ${r?.error ?? '作れませんでした'}`),
  },
  propose_plan: {
    running: '📝 手順の確認待ち…',
    success: (r) => (r?.approved ? '📝 手順が承認されました' : r?.success === false ? `⚠️ ${r?.error ?? '手順を出せませんでした'}` : '📝 手順を直します'),
  },
  advance_plan: {
    running: '📝 手順に印を付けています…',
    success: (r) => (r?.success ? (r?.finished ? '✅ 全部の手順が終わりました' : `📝 手順 ${r?.step} を更新しました`) : `⚠️ ${r?.error ?? ''}`),
  },
  report_contradiction: {
    running: '📣 スタッフへ矛盾を連絡中…',
    success: (r) => (r?.success ? `📣 スタッフ全員にメールしました（#${r?.escalation_id}）` : `⚠️ 連絡に失敗: ${r?.error ?? ''}`),
  },
  search_reservations: {
    running: '🔍 予約を検索中…',
    success: (r) => `✅ 予約 ${r?.count ?? 0} 件見つかりました${r?.truncated ? '（30件まで表示）' : ''}`,
  },
  search_vehicle_types: {
    running: '🚗 車両タイプを検索中…',
    success: (r) => {
      if ((r?.count ?? 0) === 0) {
        return `⚠️ 該当なし（候補 ${r?.alternatives?.length ?? 0} 件を提示）`;
      }
      const withStock = Array.isArray(r?.results)
        ? r.results.filter((x: any) => x.availability?.has_stock).length
        : 0;
      const hasStockInfo = Array.isArray(r?.results) && r.results.some((x: any) => x.availability);
      return hasStockInfo
        ? `✅ 車両タイプ ${r.count} 件（在庫あり ${withStock} 件）`
        : `✅ 車両タイプ ${r.count} 件見つかりました`;
    },
  },
  list_all_vehicle_types: {
    running: '🚙 全車両タイプの料金を取得中…',
    success: (r) => {
      const n = r?.vehicle_types?.length ?? 0;
      const withStock = Array.isArray(r?.vehicle_types)
        ? r.vehicle_types.filter((x: any) => x.has_stock).length
        : 0;
      const campaign = r?.campaign?.name ? ` ／🎉${r.campaign.name}` : '';
      return `✅ 車両タイプ ${n} 件（在庫あり ${withStock} 件）${campaign}`;
    },
  },
  calculate_price: {
    running: '💴 料金を計算中…',
    success: (r) => {
      const qty = Number(r?.quantity || 1);
      const perVehicle = Number(r?.per_vehicle?.grand_total ?? r?.total_price ?? 0).toLocaleString();
      const grandTotal = Number(r?.grand_total ?? r?.total_price ?? 0).toLocaleString();
      const dis = Number(r?.per_vehicle?.campaign_discount ?? r?.discount ?? 0);
      const disText = dis > 0 ? ` （割引 -${dis.toLocaleString()}円）` : '';
      const nights = r?.period?.nights ?? r?.nights ?? r?.days ?? 0;
      if (qty > 1) return `✅ ${nights} 泊 ${qty} 台 合計 ${grandTotal}円${disText}（1 台 ${perVehicle}円）`;
      return `✅ ${nights} 泊 合計 ${grandTotal}円${disText}`;
    },
  },
  search_customer: {
    running: '👤 顧客を検索中…',
    success: (r) => `✅ 顧客 ${r?.count ?? 0} 件見つかりました`,
  },
  create_reservation: {
    running: '📝 予約を作成中…',
    success: (r) => {
      if (r?.cancelled) return '⚠️ 予約作成はキャンセルされました';
      if (r?.success === false) return `⚠️ ${r?.error ?? '予約作成に失敗しました'}`;
      const vehicle = r?.assigned_vehicle?.vehicle_code ? ` / 車両 ${r.assigned_vehicle.vehicle_code}` : '';
      return `✅ 予約を作成しました (ID: ${r?.reservation_id ?? '-'}${vehicle})`;
    },
  },
  update_reservation: {
    running: '✏️ 予約を更新中…',
    success: (r) => {
      if (r?.cancelled) return '⚠️ 更新はキャンセルされました';
      if (r?.success === false) return `⚠️ ${r?.error ?? '予約更新に失敗しました'}`;
      const code = r?.contact_code ? ` (${r.contact_code})` : '';
      const n = Array.isArray(r?.changes) ? r.changes.length : 0;
      return `✅ 予約を更新しました${code} ／ ${n} 項目`;
    },
  },
  get_vehicle_notion: {
    running: '📔 Notion の車両マスタを検索中…',
    success: (r) => (r?.success === false
      ? `⚠️ ${r?.error ?? 'Notion 検索に失敗しました'}`
      : `✅ Notion 車両情報 ${r?.count ?? 0} 件${(r?.count ?? 0) > 1 ? '（候補複数）' : ''}`),
  },
  get_accident_notion: {
    running: '📔 Notion の事故DBを検索中…',
    success: (r) => (r?.success === false
      ? `⚠️ ${r?.error ?? '事故DB検索に失敗しました'}`
      : `✅ 事故情報 ${r?.count ?? 0} 件${(r?.count ?? 0) > 1 ? '（候補複数）' : ''}`),
  },
  create_accident_notion: {
    running: '📝 事故連絡簿へ追記中…',
    success: (r) => {
      if (r?.cancelled) return '⚠️ 追記はキャンセルされました';
      if (r?.success === false) return `⚠️ ${r?.error ?? '事故連絡簿への追記に失敗しました'}`;
      return `✅ 事故連絡簿に追記しました（${(r?.set_properties || []).length} 項目）`;
    },
  },
  search_notion: {
    running: '📔 Notion ドキュメントを検索中…',
    success: (r) => (r?.success === false
      ? `⚠️ ${r?.error ?? 'Notion 検索に失敗しました'}`
      : `✅ Notion ${r?.count ?? 0} 件ヒット`),
  },
  get_notion_page: {
    running: '📔 Notion ページを取得中…',
    success: (r) => (r?.success === false
      ? `⚠️ ${r?.error ?? 'Notion ページ取得に失敗しました'}`
      : '✅ Notion ページを取得しました'),
  },
};

/**
 * 予約セッションを開くときに注入する**予約スナップショット**（2026-09-03）。
 *
 * これが無かったため、セッションに入っているのにエージェントが毎回
 * `search_reservations` を呼び直していた（前田さん指摘）。さらに悪いことに、
 * 予約データを知らないまま `check_schedule_conflict` に**推測した日付**を渡して
 * エラーになる事象も起きていた（記録#26）。開いた時点で事実を渡してしまう。
 *
 * 読み取り専用。search_reservations と同じ整形（車両コード・チャネル・料金）を使う。
 */
/**
 * 実車のタイプが予約の車種と違う（= 無料アップグレード運用中）なら注記を返す（2026-09-19 記録#31）。
 * 「アップグレードしたと言わなくていい。当日20系に変更しているかもしれないので」
 * → メール等でお客様に伝える車種は**予約の car_type のまま**。AI がメモから推測して
 *    「30系へアップグレードしました」と書いた事象を、事実の側から封じる。
 */
async function describeUpgradeInEffect(r: any): Promise<string> {
  // ⚠ 車両の car_type は**表示名**（例: デリカD:5）、予約の car_type は**コード**（例: M-D5-H）。
  //   文字列で比べると同じ車種でも不一致になり、2026-09-27 記録#34 で
  //   デリカ予約×デリカ実車に「無料アップグレード運用中」と誤表示した。→ 車種タイプID で比べる。
  try {
    if (!r?.vehicle_id) return '';
    const vehicles: any[] = await fetchDispatchVehicles({});
    const v = vehicles.find((x: any) => Number(x.id) === Number(r.vehicle_id));
    const actualId = Number(v?.vehicle_type_id);
    if (!v || !Number.isFinite(actualId) || actualId <= 0) return '';
    const { ids, pool, upgrades } = await allowedVehicleTypeIds(r);
    const actualName = String(v.car_type ?? '');
    if (pool) return '';                       // ガチャ枠: 車種非確約。プール内なら通常運用
    if (upgrades.has(actualId)) {
      return `\n  ※ 実車は ${actualName}（無料アップグレード運用中）。お客様への案内・メールでは予約の車種「${r.car_type}」のまま書き、アップグレードには触れない`;
    }
    if (ids.size > 0 && !ids.has(actualId)) {
      return `\n  ⚠ 実車 ${actualName} は予約の車種「${r.car_type}」と別タイプ（無料アップグレード対象外）。意図した振替か担当者に確認`;
    }
    return '';
  } catch { return ''; }
}

export async function buildReservationSnapshot(contactCode: string): Promise<string | null> {
  try {
    const res: any = await runSearchReservations({ contact_code: contactCode });
    const r = res?.results?.[0];
    if (!r) return null;
    const p = r.prices || {};
    const yen = (v: any) => (typeof v === 'number' ? `¥${v.toLocaleString()}` : '—');
    const lines = [
      `予約番号: ${r.contact_code}（予約ID ${r.id}）`,
      `お客様: ${r.customer_name ?? '—'}　TEL ${r.customer_tel ?? '—'}`,
      `期間: ${formatDateTime(r.start_date)} 〜 ${formatDateTime(r.end_date)}`,
      `車両: ${r.car_type ?? '—'}${r.vehicle_code ? `（${r.vehicle_code}）` : '（未アサイン）'}`
        + (await describeUpgradeInEffect(r)),
      `ステータス: ${formatStatus(r.status)}　チャネル: ${r.channel ?? '不明'}`
        + (r.channel_raw ? `（${r.channel_raw}）` : ''),
      ...(() => {
        const ml = languageFromMedia(r.channel_raw);
        return ml && ml !== 'ja'
          ? [`お客様の言語: **${LANGUAGE_LABEL[ml] ?? ml}**（予約経路から判定。メールはこの言語で起案する）`] : [];
      })(),
      ...(r.plan ? [
        `プラン: ${r.plan}`,
        `送迎: ${isWalkInPlan(r.plan) ? '**送迎なし（駅近来店）**（店舗へ直接来店。送迎はしない・セルフプランとは別）' : isRepresentativePickupPlan(r.plan) ? '**代表者送迎**（代表者の方だけ送迎。セルフではない）' : isSelfPlan(r.plan) ? '**セルフプラン**（原則ご自身で来店。希望があれば送迎可・時間確約なし）' : 'あり（原則、送迎あり）'}`,
      ] : []),
      ...(r.hotel_delivery ? [`ホテル配車: **${r.hotel_delivery}**（予約に登録済み。便名欄・メモに書き直さない）`] : []),
      `料金: 基本 ${yen(p.base_price)} / オプション ${yen(p.option_price)} / NOC ${yen(p.noc_price)} / CDW ${yen(p.cdw_price)} / 合計 ${yen(p.sum_price)}`,
    ];
    const group = await fetchReservationGroup(r).catch(() => null);
    if (group) {
      lines.push(`複数台予約: 全${group.count}台（${group.codes.join(', ')}）グループ合計 ¥${group.total.toLocaleString()}。`
        + 'お客様へのメールは代表（枝番 -1）から全台分の内容で送る');
    }
    return lines.join('\n');
  } catch {
    return null;   // 取得失敗でもセッションは開く（タイムラインだけで成立する）
  }
}
